import { Modal, notification } from "antd";
import config from "./config";
import request, { headers, uri } from "./http-request";
import { WarningFilled } from "@ant-design/icons";
import { toFacilityProviderRecord } from "../../features/facilities/facilityUtils";

const lib = {};
const key = "notification";

const getFilenameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) return null;
  // Examples:
  // content-disposition: attachment; filename="file.csv"
  // content-disposition: attachment; filename=file.csv
  const match = /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i.exec(contentDisposition);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const joinUrl = (base, path) => {
  const b = String(base || '').replace(/\/+$/, '');
  const p = String(path || '').replace(/^\/+/, '');
  return `${b}/${p}`;
};

const normalizeExportDownloadUrl = (downloadUrl) => {
  const raw = String(downloadUrl || '').trim();
  if (!raw) return raw;

  // If backend still returns legacy bulkUploads paths/urls, rewrite to the new secured endpoint:
  // /v2/corporations/exports/{filename}
  const rewriteLegacy = (origin, pathname) => {
    const file = String(pathname || '').split('/').filter(Boolean).pop();
    if (!file) return raw;
    return joinUrl(origin, `/v2/corporations/exports/${encodeURIComponent(file)}`);
  };

  if (raw.startsWith('http')) {
    try {
      const u = new URL(raw);
      const path = u.pathname || '';
      if (path.includes('/v2/corporations/exports/')) return raw;
      if (path.includes('/bulkUploads/') || path.includes('/v2/bulkUploads/')) {
        return rewriteLegacy(u.origin, path);
      }
      return raw;
    } catch {
      return raw;
    }
  }

  // Relative path handling
  const normalizedPath = `/${raw.replace(/^\/+/, '')}`;
  if (normalizedPath.includes('/v2/corporations/exports/')) {
    return joinUrl(uri, normalizedPath);
  }
  if (normalizedPath.includes('/bulkUploads/') || normalizedPath.includes('/v2/bulkUploads/')) {
    return rewriteLegacy(uri, normalizedPath);
  }
  return joinUrl(uri, normalizedPath);
};

export const setHeader = () => {
  try {
    const json = localStorage.getItem(config.key.user);
    if (!json || json === null) {
      return {};
    }
    const user = JSON.parse(json);
    return user
      ? {
        headers: {
          Authorization: "Bearer " + user?.jwt,
        },
      }
      : {};
  } catch (e) {
    // If JSON parsing fails (corrupted data), clear it and return empty headers
    localStorage.removeItem(config.key.user);
    localStorage.removeItem(config.key.token);
    return {};
  }
};

/**
 * Download a file from a URL that requires Bearer auth.
 * Works with absolute URLs (e.g. https://v1.amanhmo.com/v2/corporations/exports/...) and relative paths.
 */
lib.downloadFileWithAuth = async (downloadUrl, filename) => {
  if (!downloadUrl) {
    throw new Error('downloadUrl is required');
  }

  const fullUrl = normalizeExportDownloadUrl(downloadUrl);

  const resp = await request.get(fullUrl, { ...setHeader(), responseType: 'blob' });
  const blob = resp?.data;

  const inferredName =
    filename ||
    getFilenameFromContentDisposition(resp?.headers?.['content-disposition']) ||
    fullUrl.split('/').pop() ||
    'export.csv';

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = inferredName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);

  return { success: true, filename: inferredName };
};

lib.sendItemDoc = async (data) => myRequestObj(`saveItemDoc`).post(data);

lib.verifyPaymentFlutter = async (data) => myRequestObj(`verifyPayment`).post(data);

lib.registerDash = async (data) => myRequestObj(`regUserDash`).post(data)

lib.loginSpecial = async (data) => myRequestObj(`login&special`).post(data)

// Legacy login (kept for backward compatibility)
lib.loginLegacy = async (data) => myRequestObj(`loginClaimsAdmin`).post(data)

// V2 RBAC login
lib.login = async (data) => {
  // Application header is already set in http-request.js (Application: 2)
  return myApi('POST', 'v2/auth/login', data);
};

lib.createUser = async (data) => myRequestObj(`aCreateUser`).post(data)

// API call for Admin Users - Aman Medicare
lib.readUser = async (data) => myRequestObj(`aUser`).post(data);

lib.updateProfile = async (data) => myRequestObj(`aChangeStatus`).post(data);

lib.departments = async () => myRequestObj(`departments`).post();

lib.roles = async () => myRequestObj(`roles`).post();

lib.reset = async (data) => myRequestObj(`reset`).post(data);

lib.enrollees = async (data) => myRequestObj(`enrollees`).post(data);

lib.principal = async (data) => myRequestObj(`pricipal`).post(data);

lib.saveNewProfile = async (data) => myRequestObj(`save-profile`).post(data);

// used for verifying clients from claims
lib.getClient = async (data) => myRequestObj(`validateUserClaims`).post(data);

// lib.sendImage = async (data) => myRequestObj(`upload`).post(data);
lib.sendImage = async (data) => myRequestObj(`uploadAlt`).post(data);

// Search for users
lib.search = async (data) => myRequestObj(`search`).post(data);

// Search for users
lib.searchClients = async (data) => myRequestObj(`searchClients`).post(data);

// Search Only Enrolless
lib.searchEnrolee = async (data) => myRequestObj(`searchEnrollees`).post(data);
lib.deleteCareEnrollee = async (insuranceNo, payload = {}) => {
  if (!insuranceNo) throw new Error('insuranceNo is required');
  const encodedInsuranceNo = encodeURIComponent(String(insuranceNo));
  return request.delete(`/v2/care/enrollees/${encodedInsuranceNo}`, {
    ...setHeader(),
    data: payload || {},
  });
};

/**
 * Enrollee archive — GET archive-preview, POST archive / unarchive.
 *
 * These deliberately bypass `myApi`: it turns any non-zero `error` into a
 * notification toast and returns `{error:1, data:[], message}`, which throws
 * `data.code` away. For archive a 409 is a designed branch the confirm dialog
 * must read the code from, not an incidental failure worth toasting.
 * `parseApiCatch` keeps the 409 body and resolves `code` from `data.code` —
 * the position AdminPlans.php:437 and AdminTariffs.php:840 already emit.
 */
const enrolleeArchivePath = (insuranceNo, action) => {
  if (!insuranceNo) throw new Error('insuranceNo is required');
  return `/v2/care/enrollees/${encodeURIComponent(String(insuranceNo))}/${action}`;
};

/** Archive error codes the UI branches on. See api_v1 docs/ENROLLEE_ARCHIVE.md. */
lib.ENROLLEE_ARCHIVE_CODES = {
  BLOCKED: 'ENROLLEE_ARCHIVE_BLOCKED',
  ALREADY_ARCHIVED: 'ENROLLEE_ALREADY_ARCHIVED',
  NO_SLOT: 'ENROLLEE_UNARCHIVE_NO_SLOT',
  UNAVAILABLE: 'ENROLLEE_ARCHIVE_UNAVAILABLE',
};

/** Non-mutating preview of what archiving would do — drives the dialog copy. */
lib.archiveEnrolleePreview = async (insuranceNo) => {
  try {
    const resp = await request.get(
      enrolleeArchivePath(insuranceNo, 'archive-preview'),
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

/**
 * Archive an enrollee. `force: true` cascades a principal onto their
 * dependents and may only be sent after a second, differently-worded
 * confirmation naming the dependent count — never as an automatic retry of a
 * 409 ENROLLEE_ARCHIVE_BLOCKED.
 */
lib.archiveEnrollee = async (insuranceNo, { reason = '', force = false } = {}) => {
  try {
    const resp = await request.post(
      enrolleeArchivePath(insuranceNo, 'archive'),
      { reason, force },
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

/** Unarchive. 409 ENROLLEE_UNARCHIVE_NO_SLOT when a released seat was refilled. */
lib.unarchiveEnrollee = async (insuranceNo, { reason = '' } = {}) => {
  try {
    const resp = await request.post(
      enrolleeArchivePath(insuranceNo, 'unarchive'),
      { reason },
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

lib.checkin = async (data) => myRequestObj("clientCheckin").post(data);

lib.getClient = async (data) => myRequestObj("validateUserClaims").post(data);

// Search for providers
lib.searchProviders = async (data) => myRequestObj(`searchProviders`).post(data);

// Search Organizations
lib.searchOrganizations = async (data) => myRequestObj(`searchOrganization`).post(data);

// Search Costables
lib.searchCostables = async (data) => myRequestObj(`searchCostables`).post(data);

/** GET /v2/search request timeout (ms). Guide: 15–30s for heavy lists. */
const UNIFIED_SEARCH_TIMEOUT_MS = 25000;

/** Maps Search UI `target` (config.searchTarget) to `GET /v2/search?resource=`. */
const UNIFIED_SEARCH_TARGET_TO_RESOURCE = {
  clients: "clients",
  enrollees: "enrollees",
  facilities: "facilities",
  corporations: "organizations",
  onboarding: "onboarding",
  constableItems: "costables",
  pa: "pa",
  claims: "claims",
  checkins: "checkins",
  appointments: "appointments",
};

lib.unifiedSearchTargetToResource = (target) =>
  UNIFIED_SEARCH_TARGET_TO_RESOURCE[target] || null;

/**
 * Client-side rules aligned with GET /v2/search (required term + minimum length when typing).
 * @param {string} resource — canonical `resource` value (e.g. `facilities`, `enrollees`).
 * @returns {{ allowEmpty: boolean, minWhenPresent: number }}
 */
lib.unifiedSearchRulesForResource = (resource) => {
  switch (resource) {
    case "clients":
      return { allowEmpty: false, minWhenPresent: 4 };
    case "facilities":
    case "organizations":
    case "onboarding":
    case "costables":
    case "pa":
    case "claims":
      return { allowEmpty: false, minWhenPresent: 3 };
    case "staff":
    case "tariffs":
      return { allowEmpty: true, minWhenPresent: 3 };
    case "enrollees":
    case "checkins":
    case "appointments":
      return { allowEmpty: true, minWhenPresent: 3 };
    default:
      return { allowEmpty: true, minWhenPresent: 0 };
  }
};

lib.unifiedSearchRulesForTarget = (target) => {
  const r = lib.unifiedSearchTargetToResource(target);
  if (!r) return { allowEmpty: false, minWhenPresent: 1 };
  return lib.unifiedSearchRulesForResource(r);
};

/** Minimum trimmed query length when the user typed something (0 = no minimum). */
lib.unifiedSearchMinQueryLength = (target) =>
  lib.unifiedSearchRulesForTarget(target).minWhenPresent;

lib.unifiedSearchAllowsEmptyQuery = (target) =>
  lib.unifiedSearchRulesForTarget(target).allowEmpty;

/**
 * Merge catalog row from GET /v2/search/resources with built-in rules.
 * @param {string} resource — canonical `resource`
 * @param {object|null|undefined} catalogEntry — row from `data.resources[]`
 */
lib.effectiveUnifiedSearchRules = (resource, catalogEntry) => {
  const base = lib.unifiedSearchRulesForResource(resource);
  if (!catalogEntry || typeof catalogEntry !== "object") return base;

  let allowEmpty = base.allowEmpty;
  if (
    catalogEntry.allows_empty_search !== undefined &&
    catalogEntry.allows_empty_search !== null
  ) {
    allowEmpty = !!(
      catalogEntry.allows_empty_search === true ||
      catalogEntry.allows_empty_search === 1 ||
      catalogEntry.allows_empty_search === "1"
    );
  }
  if (
    catalogEntry.requires_non_empty_search === true ||
    catalogEntry.requires_non_empty_search === 1 ||
    catalogEntry.requires_non_empty_search === "1"
  ) {
    allowEmpty = false;
  }

  let minWhenPresent = base.minWhenPresent;
  if (catalogEntry.min_search_length != null && catalogEntry.min_search_length !== "") {
    const n = Number(catalogEntry.min_search_length);
    if (!Number.isNaN(n)) minWhenPresent = Math.max(0, n);
  }

  return { allowEmpty, minWhenPresent };
};

lib.effectiveUnifiedSearchRulesForTarget = (target, catalogEntry) => {
  const r = lib.unifiedSearchTargetToResource(target);
  if (!r) return { allowEmpty: false, minWhenPresent: 1 };
  return lib.effectiveUnifiedSearchRules(r, catalogEntry);
};

lib.effectiveUnifiedSearchAllowsEmptyQuery = (target, catalogEntry) =>
  lib.effectiveUnifiedSearchRulesForTarget(target, catalogEntry).allowEmpty;

lib.effectiveUnifiedSearchMinQueryLength = (target, catalogEntry) =>
  lib.effectiveUnifiedSearchRulesForTarget(target, catalogEntry).minWhenPresent;

/** Resolve page size using catalog defaults/limits, then clamp to server max per resource. */
lib.unifiedSearchResolvePageSize = (resource, catalogEntry, requestedPageSize) => {
  const serverMax = resource === "clients" ? 50 : 100;
  let n = requestedPageSize;
  if (n == null || Number.isNaN(Number(n)) || Number(n) < 1) {
    const d = catalogEntry?.default_page_size;
    n = d != null && d !== "" ? Number(d) : null;
  }
  if (n == null || Number.isNaN(Number(n)) || Number(n) < 1) {
    n = 20;
  }
  let cap = serverMax;
  const m = catalogEntry?.max_page_size;
  if (m != null && m !== "" && !Number.isNaN(Number(m)) && Number(m) > 0) {
    cap = Math.min(serverMax, Number(m));
  }
  return Math.min(Math.max(1, Number(n) || 20), cap);
};

/** Staff / tariffs require `facility_id` on every request (400 if missing). */
lib.unifiedSearchRequiresFacilityId = (resource) =>
  resource === "staff" || resource === "tariffs";

/**
 * GET /v2/search — unified admin search (Bearer auth).
 * Pass either `target` (search UI key) or `resource` (canonical API resource name).
 */
lib.unifiedSearch = async (args = {}, signal) => {
  const { target, resource: resourceOverride, search = "", page = 1, pageSize = 20, extra = {} } = args;
  const resource =
    resourceOverride ||
    (target ? lib.unifiedSearchTargetToResource(target) : null);
  if (!resource) {
    throw new Error(
      `Invalid search: need a known target or resource (got target=${target}, resource=${resourceOverride})`,
    );
  }
  const term = String(search ?? "").trim();
  const maxPageSize = resource === "clients" ? 50 : 100;
  const limit = Math.min(Math.max(1, Number(pageSize) || 20), maxPageSize);
  const params = {
    resource,
    page: Math.max(1, Number(page) || 1),
    limit,
    pageSize: limit,
    ...extra,
  };
  if (term) {
    params.search = term;
  }
  return request.get("/v2/search/", {
    ...setHeader(),
    params,
    signal,
    timeout: UNIFIED_SEARCH_TIMEOUT_MS,
  });
};

/** Maps canonical API `resource` → Search UI `target` keys used by SearchResultsGrid / config.searchTarget. */
const UNIFIED_SEARCH_RESOURCE_TO_UI_TARGET = {
  clients: "clients",
  enrollees: "enrollees",
  facilities: "facilities",
  /** Catalog alias `providers` → same list as facilities (see §1.6). */
  providers: "facilities",
  organizations: "corporations",
  onboarding: "onboarding",
  deals: "onboarding",
  user_data: "onboarding",
  costables: "constableItems",
  pa: "pa",
  claims: "claims",
  checkins: "checkins",
  encounters: "checkins",
  appointments: "appointments",
};

lib.unifiedSearchCatalogResourceToUiTarget = (resource) =>
  UNIFIED_SEARCH_RESOURCE_TO_UI_TARGET[resource] || null;

/**
 * GET /v2/search/resources — bootstrap catalog (labels, RBAC, validation hints).
 * Optional `facility_id` for staff/tariffs RBAC. See docs/V2_UNIFIED_SEARCH_UI.md (Bootstrap catalog).
 */
lib.unifiedSearchCatalog = async (params = {}, signal) => {
  const q = {};
  const fid = params.facility_id ?? params.facilityId;
  if (fid != null && String(fid).trim() !== "" && !Number.isNaN(Number(fid))) {
    q.facility_id = Number(fid);
  }
  return request.get("/v2/search/resources", {
    ...setHeader(),
    params: q,
    signal,
    timeout: UNIFIED_SEARCH_TIMEOUT_MS,
  });
};

/**
 * GET /v2/search success: `data` is always a top-level array (never `data.records` / `data.items`).
 * @param {unknown} data
 * @returns {object[]}
 */
lib.normalizeUnifiedSearchResults = (data) => {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  return [];
};

/**
 * Flatten resource-specific rows for cards/modals (e.g. enrollees → spread `enrollee.*`).
 * Preserves `policy`, `plan`, `organization`, `status_contract` on enrollee rows.
 * @param {string} resource — canonical API resource
 * @param {object} row
 */
/** DB `status` / `facility_status` fields are integer 0|1, not boolean (§2 legacy mapping). */
lib.unifiedSearchIsActiveStatus = (status) => Number(status) === 1;

lib.normalizeUnifiedSearchRow = (resource, row) => {
  if (!row || typeof row !== "object") return row;
  if (resource === "enrollees" && row.enrollee && typeof row.enrollee === "object") {
    return {
      ...row.enrollee,
      policy: row.policy,
      plan: row.plan,
      organization: row.organization,
      status_contract: row.status_contract,
    };
  }
  if (resource === "facilities") {
    return toFacilityProviderRecord(row);
  }
  /** `checkins` rows stay nested (`consumer`, `filed`, `approved`) per §3.9. */
  return row;
};

/**
 * Parse successful GET /v2/search envelope (§1.1).
 * @param {object} body — axios `res.data`
 */
lib.parseUnifiedSearchSuccessBody = (body) => {
  const resource = body?.resource ?? null;
  const list = lib.normalizeUnifiedSearchList(resource, body?.data);
  const pagination = lib.normalizeUnifiedSearchPagination(body?.pagination);
  const search =
    body?.search != null && typeof body.search === "object" ? body.search : null;
  return {
    resource,
    data: list,
    pagination,
    search,
    statusContractVersion: body?.status_contract_version ?? null,
    scope: body?.scope ?? null,
    raw: body,
  };
};

/**
 * @param {string} resource
 * @param {unknown} data — `body.data` from search response
 * @returns {object[]}
 */
lib.normalizeUnifiedSearchList = (resource, data) =>
  lib.normalizeUnifiedSearchResults(data).map((row) =>
    lib.normalizeUnifiedSearchRow(resource, row),
  );

/**
 * Select options for enrollee typeahead (GET /v2/search?resource=enrollees).
 * Flattens nested `enrollee.*` before building labels.
 * @param {unknown} data — `body.data` from search response
 * @returns {{ label: string, value: string, key: string|number, title: string }[]}
 */
lib.unifiedSearchEnrolleeSelectOptions = (data) =>
  lib.normalizeUnifiedSearchList("enrollees", data)
    .map((user) => {
      const insurance = user?.insurance_no ?? "";
      const middle = user?.middle_name ? `${user.middle_name}, ` : "";
      const surname =
        user?.surname != null ? String(user.surname).trim().toUpperCase() : "";
      const label = `${insurance}: ${user?.first_name ?? ""} ${middle}${surname}`.trim();
      return {
        label,
        value: insurance,
        key: user?.id ?? insurance,
        title: "user",
      };
    })
    .filter((o) => o.value);

/** Facility typeahead options (`resource=facilities` — flat rows, §3.3). */
lib.unifiedSearchFacilitySelectOptions = (data) =>
  lib.normalizeUnifiedSearchList("facilities", data).map((facility) => ({
    label: `${facility.facility_type ? `${String(facility.facility_type).toUpperCase()} - ` : ""}${facility.facility_name} - ${facility?.facility_city || ""} ${facility?.facility_state || ""}`.trim(),
    value: facility?.facility_id,
    key: facility?.facility_id,
    title: "facility",
  })).filter((o) => o.value != null);

/**
 * Normalize pagination (`limit`, `totalPages` vs `pages`, `pageSize` aliases).
 * @param {object|null|undefined} pagination
 */
lib.normalizeUnifiedSearchPagination = (pagination) => {
  if (!pagination || typeof pagination !== "object") return null;
  const page = Number(pagination.page) || 1;
  const limit =
    Number(pagination.limit) ||
    Number(pagination.pageSize) ||
    Number(pagination.page_size) ||
    20;
  const total = Number(pagination.total) || 0;
  let totalPages =
    pagination.totalPages != null
      ? Number(pagination.totalPages)
      : pagination.total_pages != null
        ? Number(pagination.total_pages)
        : pagination.pages != null
          ? Number(pagination.pages)
          : null;
  if ((totalPages == null || Number.isNaN(totalPages)) && limit > 0) {
    totalPages = Math.ceil(total / limit);
  }
  return {
    page,
    limit,
    pageSize: limit,
    total,
    totalPages: totalPages ?? 0,
  };
};

/**
 * Navigate from unified search result to the appropriate admin screen.
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {string} target — UI search target
 * @param {object} record — normalized row
 */
lib.unifiedSearchOpenTarget = (navigate, target, record) => {
  if (!navigate || !record) return;
  const resource = lib.unifiedSearchTargetToResource(target) || target;
  switch (resource) {
    case "pa":
    case "checkins": {
      const code = record.checkin_id ?? record.code;
      if (code) {
        navigate(
          `/preauth-action?checkin=${encodeURIComponent(String(code))}`,
        );
      }
      break;
    }
    case "claims": {
      const encounter =
        record.checkin_code ?? record.checkin_id ?? record.claims_id;
      if (encounter != null && String(encounter).trim() !== "") {
        navigate(
          `/claims/filings/?encounter=${encodeURIComponent(String(encounter))}`,
        );
      }
      break;
    }
    case "onboarding": {
      const social = record.social_no;
      if (social != null && String(social).trim() !== "") {
        navigate(`/corporations?search=${encodeURIComponent(String(social))}`);
      }
      break;
    }
    case "appointments":
      break;
    default:
      break;
  }
};

/** Stable React/list key per resource (see GET /v2/search per-resource contract). */
lib.unifiedSearchRowKey = (resource, record, index = 0) => {
  if (!record || typeof record !== "object") return `row-${index}`;
  switch (resource) {
    case "clients":
      return record.id ?? record.policy_no ?? index;
    case "enrollees":
      return record.enrollee?.id ?? record.id ?? record.insurance_no ?? index;
    case "facilities":
    case "providers":
      return record.facility_id ?? index;
    case "organizations":
      return record.org_id ?? index;
    case "onboarding":
      return record.id ?? record.social_no ?? index;
    case "costables":
      return record.id ?? index;
    case "pa":
    case "checkins":
      return record.checkin_id ?? index;
    case "claims":
      return record.claims_id ?? index;
    case "appointments":
      return record.appointment_id ?? index;
    case "staff":
      return record.facility_users_id ?? index;
    case "tariffs":
      return record.facility_tariff_item_id ?? record.tariff_item_id ?? index;
    default:
      return record.id ?? index;
  }
};

lib.getProviders = async (data) => myRequestObj(`getProviders`).post(data);

// Update facility information
lib.updateProvider = async (data) => myRequestObj(`update_facility`).post(data);

// Grab data for users
lib.grabData = async (data) => myRequestObj(`grabData`).post(data);

// Update for users
lib.updateUserData = async (data) => myRequestObj(`new_update`).post(data);

// Update for users
lib.updateUserDataCopy = async (data) => myRequestObj(`new_update_ad`).post(data);

// Update for users
lib.updateUserFinance = async (data) => myRequestObj(`updatePolicy`).post(data);

// Update for users
lib.unverifiedClients = async (data) => myRequestObj(`unverified`).post(data);
// DEPRECATED: Use counters endpoint (/v2/ui/counters) instead via useCountersEnhanced hook
// This endpoint (unverified_no) has been replaced by the counters endpoint
lib.unverified = async (data) => myRequestObj(`unverified_no`).post(data);

// Update for users
lib.unverifiedEnrollees = async (data) => myRequestObj(`unverified-enrollees`).post(data);

lib.checkin = async (data) => myRequestObj(`clientCheckin`).post(data);

lib.checkout = async (data) => myRequestObj(`checkout`).post(data);

// Admit enrollee: switch encounter to inpatient (sends checkinId, backend sets checkinType = 1)
lib.admitEnrollee = async (checkinId) => myRequestObj(`admitEnrollee`).post({ checkinId });

lib.checkinList = async () => myRequestObj(`aCheckinList`).post();

lib.sendPA = async (data) => myRequestObj(`paRequest`).post(data);
lib.submitPA = async (data = {}, options = {}) => {
  const idempotencyKey = options?.idempotencyKey;
  const config = idempotencyKey
    ? {
      headers: {
        ...(setHeader()?.headers || {}),
        'Idempotency-Key': idempotencyKey,
      },
    }
    : {};
  return request.post('/v2/pa/', data, { ...setHeader(), ...config });
};

lib.submitPAManual = async (data = {}, options = {}) => {
  const idempotencyKey = options?.idempotencyKey;
  const config = idempotencyKey
    ? {
      headers: {
        ...(setHeader()?.headers || {}),
        'Idempotency-Key': idempotencyKey,
      },
    }
    : {};
  return request.post('/v2/pa/manual', data, { ...setHeader(), ...config });
};

/**
 * Build `unit_cost` / `unit_approved_cost` for a single PA manual line (snapshot at filing).
 * When `itemPrice` returned an authoritative row, unit fields mirror that snapshot; otherwise
 * derive from line totals ÷ quantity (manual / negotiation paths).
 *
 * @param {{ quantity?: unknown, cost?: unknown, approved_cost?: unknown }} dataRow
 * @param {Record<string, unknown>|null|undefined} costDataSnapshot — last `itemPrice` row for this line (form state)
 * @returns {{ quantity: number, unit_cost: number, unit_approved_cost: number, cost: number, approved_cost: number }}
 */
lib.computePaManualLineUnitSnapshot = (dataRow = {}, costDataSnapshot) => {
  const qty = Math.max(1, Number(dataRow.quantity) || 1);
  const lineCost = Number(dataRow.cost ?? 0);
  const snap = costDataSnapshot && typeof costDataSnapshot === 'object' ? costDataSnapshot : null;
  const hasAuthoritativePrice = Boolean(
    snap && (snap.cost != null || snap.cost_facility != null || snap.cost_hmo != null),
  );
  // Approved total is operator-editable on the filing form; honor the edited value
  // when present instead of overwriting it with the catalog/HMO price.
  const hasEditedApproved =
    dataRow.approved_cost != null &&
    dataRow.approved_cost !== '' &&
    Number.isFinite(Number(dataRow.approved_cost));
  const editedApproved = hasEditedApproved ? Number(dataRow.approved_cost) : null;
  let unitCost;
  let approvedTotal;
  if (hasAuthoritativePrice) {
    unitCost = Number(snap.cost ?? snap.cost_facility ?? snap.cost_hmo ?? 0);
    approvedTotal = editedApproved != null
      ? editedApproved
      : Number(snap.cost_hmo ?? snap.cost ?? snap.cost_facility ?? 0) * qty;
    return {
      quantity: qty,
      unit_cost: unitCost,
      unit_approved_cost: qty > 0 ? approvedTotal / qty : approvedTotal,
      cost: unitCost * qty,
      approved_cost: approvedTotal,
    };
  }
  unitCost = qty > 0 ? lineCost / qty : lineCost;
  approvedTotal = editedApproved != null ? editedApproved : 0;
  return {
    quantity: qty,
    unit_cost: unitCost,
    unit_approved_cost: qty > 0 ? approvedTotal / qty : approvedTotal,
    cost: lineCost,
    approved_cost: approvedTotal,
  };
};

/**
 * Apply `PRICE_DRIFT` response rows to parallel PA manual payload + display line arrays.
 * @param {Array<{ line_index: number, quantity?: number, current_unit_cost: number, current_unit_approved_cost?: number|null }>} driftedItems
 * @param {object[]} payloadRows — `state._data`
 * @param {object[]} displayRows — `state._items`
 */
lib.applyPaManualPriceDrift = (driftedItems, payloadRows = [], displayRows = []) => {
  const list = Array.isArray(driftedItems) ? driftedItems : [];
  const byIdx = new Map(list.map((d) => [Number(d.line_index), d]));
  const patch = (row, idx) => {
    const drift = byIdx.get(idx);
    if (!drift) return row;
    const q = Math.max(1, Number(drift.quantity ?? row?.quantity) || 1);
    const uc = Number(drift.current_unit_cost);
    const uaRaw = drift.current_unit_approved_cost;
    const ua = uaRaw != null && uaRaw !== '' && Number.isFinite(Number(uaRaw))
      ? Number(uaRaw)
      : uc;
    return {
      ...row,
      quantity: q,
      unit_cost: uc,
      unit_approved_cost: ua,
      cost: uc * q,
      approved_cost: ua * q,
    };
  };
  return {
    data: (payloadRows || []).map(patch),
    items: (displayRows || []).map(patch),
  };
};

lib.saveProvider = async (data) => myRequestObj(`add_facility`).post(data);

lib.getCostableCategories = async () => myRequestObj(`constableCategoriesList`).post();

lib.validatSocial = async (data) => myRequestObj(`validate_social`).post(data);

lib.checkMailAvailability = async (data) => myRequestObj(`checkEmailAvailable`).post(data);

lib.updateUserEmail = async (data) => myRequestObj(`updatePolicyEmail`).post(data);

// Organizatoins Management
// Get All Organizations
lib.orgs = async (data) => myRequestObj(`organizations`).post(data);

// Get All Organizations
lib.updateOrgDate = async (data) => myRequestObj(`updateOrgExpiry`).post(data);

// Get All Organizations
lib.updateOrgDate = async (data) => myRequestObj(`updateOrgExpiry`).post(data);

// Deprecated: prefer lib.patchCorporation (PATCH /v2/corporations/{socialNo}).
lib.updateOrgData = async (data) => myRequestObj(`updateOrgData`).post(data);

/**
 * Update organization profile — PATCH /v2/corporations/{socialNo}.
 * Send only changed whitelist fields (org_name, org_email, org_phone, org_address,
 * org_city, org_state, org_status, org_logo_path, org_declared_headcount,
 * org_enrollment_segment_override). Do not echo list/stats rows.
 * Do not PATCH org_enrollment_segment / org_enrollment_segment_locked (system-managed).
 */
lib.patchCorporation = async (socialNo, payload = {}) => {
  if (!socialNo) throw new Error('socialNo is required');
  try {
    const resp = await request.patch(
      `/v2/corporations/${encodeURIComponent(socialNo)}`,
      payload,
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

/** Single org profile — GET /v2/corporations/{socialNo} (includes enrollment segment enrichment). */
lib.getCorporation = async (socialNo) => {
  if (!socialNo) throw new Error('socialNo is required');
  try {
    const resp = await request.get(
      `/v2/corporations/${encodeURIComponent(socialNo)}`,
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

lib.ORG_PROFILE_PATCH_FIELDS = [
  'org_name',
  'org_email',
  'org_phone',
  'org_address',
  'org_city',
  'org_state',
  'org_status',
  'org_logo_path',
  'org_declared_headcount',
  'org_enrollment_segment_override',
];

/** Diff editable profile fields against an original row (changed keys only). */
lib.diffOrgProfilePatch = (next = {}, original = {}) => {
  const patch = {};
  lib.ORG_PROFILE_PATCH_FIELDS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(next, key)) return;

    if (key === 'org_enrollment_segment_override') {
      const normalizeOverride = (v) => {
        if (v === undefined) return undefined;
        if (v === null || v === '') return null;
        return String(v).toLowerCase();
      };
      const a = normalizeOverride(next[key]);
      const b = normalizeOverride(original?.[key]);
      if (a !== b) patch[key] = a;
      return;
    }

    if (key === 'org_declared_headcount') {
      const normalizeHeadcount = (v) => {
        if (v === null || v === undefined || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      const a = normalizeHeadcount(next[key]);
      const b = normalizeHeadcount(original?.[key]);
      if (a !== b) patch[key] = a;
      return;
    }

    const a = next[key] == null ? '' : String(next[key]);
    const b = original?.[key] == null ? '' : String(original[key]);
    if (a !== b) patch[key] = next[key];
  });
  return patch;
};

// Get All Organizations
lib.createOrg = async (data) => myRequestObj(`createOrg`).post(data);

// Plan Management

/*
 *   Plans Designer
 */
// Get a list of plan types
lib.getPlanTypes = async (data = {}) => myRequestObj(`plan_type`).post(data);

// Get a list of plan types
lib.getPlans = async (data = {}) => myRequestObj(`plans`).post(data);

// Admin plans catalog (v2): GET /v2/admin/plans
// scope: active|all|deleted|inactive|public|corporate|assignable|utility|archived
// optional plan_type_id, audience, purchasable — see docs/ADMIN_PLANS_CATALOG_API.md
lib.getAdminPlansCatalog = async (params = {}) => myApi('GET', 'v2/admin/plans/', params);

/** Tab labels, counts, audience_options, flags — GET /v2/admin/plans/taxonomy */
lib.getAdminPlansTaxonomy = async () => myApi('GET', 'v2/admin/plans/taxonomy');

/** Normalize list response from GET /v2/admin/plans */
lib.parseAdminPlansList = (resp) => {
  const parsed = lib.parseApiEnvelope(resp);
  if (!parsed.ok) {
    return { ok: false, plans: [], message: parsed.message || 'Failed to load plans' };
  }
  const data = parsed.data ?? {};
  const plans = Array.isArray(data.plans)
    ? data.plans
    : (Array.isArray(data) ? data : []);
  return { ok: true, plans, message: parsed.message };
};

// Update plan catalog visibility & pricing (v2): PATCH /v2/admin/plans/:planId
// Body may include plan_audience, plan_purchasable, plan_cost, plan_six_cost,
// plan_corporate_cost, plan_corporate_family_cost, plan_sme_cost, plan_sme_family_cost.
lib.updateAdminPlan = async (planId, payload = {}) => {
  if (planId == null || planId === '') throw new Error('planId is required');
  try {
    const resp = await request.patch(
      `/v2/admin/plans/${encodeURIComponent(planId)}`,
      payload,
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

// Archive a plan (v2): POST /v2/admin/plans/:planId/archive
// Returns 409 PLAN_ARCHIVE_BLOCKED with data.blocking = { org_count, policy_count }
// when the plan is still on active orgs/policies; resend with force: true to proceed.
lib.archiveAdminPlan = async (planId, { reason = '', force = false } = {}) => {
  if (planId == null || planId === '') throw new Error('planId is required');
  try {
    const resp = await request.post(
      `/v2/admin/plans/${encodeURIComponent(planId)}/archive`,
      { reason, force: Boolean(force) },
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

// Un-archive a plan (v2): POST /v2/admin/plans/:planId/unarchive
lib.unarchiveAdminPlan = async (planId) => {
  if (planId == null || planId === '') throw new Error('planId is required');
  try {
    const resp = await request.post(
      `/v2/admin/plans/${encodeURIComponent(planId)}/unarchive`,
      {},
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

/** True when an archive attempt was blocked by active orgs/policies (409). */
lib.isPlanArchiveBlocked = (parsed) => {
  if (!parsed) return false;
  return parsed.httpStatus === 409
    || parsed.code === 'PLAN_ARCHIVE_BLOCKED'
    || parsed?.body?.code === 'PLAN_ARCHIVE_BLOCKED'
    || parsed?.body?.data?.code === 'PLAN_ARCHIVE_BLOCKED';
};

// Admin tiers catalog (v2): GET /v2/admin/tiers?scope=active|inactive|all
lib.getAdminTiers = async (params = {}) => {
  const queryParams = { scope: 'active', ...params };
  return myApi('GET', 'v2/admin/tiers/', queryParams);
};

// Create tier (v2): POST /v2/admin/tiers
lib.createAdminTier = async (payload = {}) => {
  return myApi('POST', 'v2/admin/tiers/', payload);
};

// Update tier (v2): PUT /v2/admin/tiers/:tier_id
lib.updateAdminTier = async (tierId, payload = {}) => {
  if (!tierId) throw new Error('tierId is required');
  const encodedId = encodeURIComponent(tierId);
  return myApi('PUT', `v2/admin/tiers/${encodedId}`, payload);
};

// Deactivate tier (v2 soft-delete): DELETE /v2/admin/tiers/:tier_id
lib.deactivateAdminTier = async (tierId) => {
  if (!tierId) throw new Error('tierId is required');
  const encodedId = encodeURIComponent(tierId);
  return myApi('DELETE', `v2/admin/tiers/${encodedId}`);
};

// Admin facility types catalog (v2): GET /v2/admin/facility-types
lib.getAdminFacilityTypes = async () => {
  return myApi('GET', 'v2/admin/facility-types/');
};

// Create facility type (v2): POST /v2/admin/facility-types
lib.createAdminFacilityType = async (payload = {}) => {
  return myApi('POST', 'v2/admin/facility-types/', payload);
};

// Update facility type (v2): PUT /v2/admin/facility-types/:id
lib.updateAdminFacilityType = async (facilityTypeId, payload = {}) => {
  if (!facilityTypeId) throw new Error('facilityTypeId is required');
  const encodedId = encodeURIComponent(facilityTypeId);
  return myApi('PUT', `v2/admin/facility-types/${encodedId}`, payload);
};

// Delete facility type (v2): DELETE /v2/admin/facility-types/:id
lib.deleteAdminFacilityType = async (facilityTypeId) => {
  if (!facilityTypeId) throw new Error('facilityTypeId is required');
  const encodedId = encodeURIComponent(facilityTypeId);
  return myApi('DELETE', `v2/admin/facility-types/${encodedId}`);
};

// --- Standard item types (v2): category-scoped taxonomy between costable_category
// and individual catalog items, e.g. "Chronic Disease Medication" under Drugs.
// See api_v1 docs/PLAN_LIMIT_ITEM_TRACKING_STRATEGY.md.

// GET /v2/admin/standard-item-types — optional { costable_category_id, include_inactive }
lib.getAdminStandardItemTypes = async (params = {}) => {
  return myApi('GET', 'v2/admin/standard-item-types/', params);
};

// POST /v2/admin/standard-item-types — { costable_category_id, name, description? }
lib.createAdminStandardItemType = async (payload = {}) => {
  return myApi('POST', 'v2/admin/standard-item-types/', payload);
};

// PUT /v2/admin/standard-item-types/:id — any of { name, description, status, costable_category_id }
lib.updateAdminStandardItemType = async (typeId, payload = {}) => {
  if (!typeId) throw new Error('typeId is required');
  const encodedId = encodeURIComponent(typeId);
  return myApi('PUT', `v2/admin/standard-item-types/${encodedId}`, payload);
};

// DELETE /v2/admin/standard-item-types/:id — deactivates, does not hard-delete
lib.deactivateAdminStandardItemType = async (typeId) => {
  if (!typeId) throw new Error('typeId is required');
  const encodedId = encodeURIComponent(typeId);
  return myApi('DELETE', `v2/admin/standard-item-types/${encodedId}`);
};

// --- Item type tagging: confirming a standard_item_types id on tariff items,
// and the review queue that drives it. Facilities may suggest; only a
// reviewer's confirmation here is ever read for enforcement.

// GET /v2/admin/item-type-tagging/untagged — optional { facility_id, category_id, pending_suggestion, limit }
lib.getAdminUntaggedItemTypes = async (params = {}) => {
  return myApi('GET', 'v2/admin/item-type-tagging/untagged', params);
};

// PUT /v2/admin/item-type-tagging/facility-items/:id — { standard_item_type_id } (null clears)
lib.confirmFacilityItemType = async (facilityTariffItemId, payload = {}) => {
  if (!facilityTariffItemId) throw new Error('facilityTariffItemId is required');
  const encodedId = encodeURIComponent(facilityTariffItemId);
  return myApi('PUT', `v2/admin/item-type-tagging/facility-items/${encodedId}`, payload);
};

// PUT /v2/admin/item-type-tagging/standard-items/:id — { standard_item_type_id } (null clears)
lib.setStandardTariffItemType = async (tariffItemId, payload = {}) => {
  if (!tariffItemId) throw new Error('tariffItemId is required');
  const encodedId = encodeURIComponent(tariffItemId);
  return myApi('PUT', `v2/admin/item-type-tagging/standard-items/${encodedId}`, payload);
};

// Create New Plan
lib.createPlan = async (data = {}) => myRequestObj(`createPlan`).post(data);

// Update a plan
lib.updatePlan = async (data = {}) => myRequestObj(`updatePlan`).post(data);

// design a category
lib.designplan = async (data = {}) => myRequestObj(`relatePlanCategories`).post(data);

// design a category
lib.planDetails = async (data = {}) => myRequestObj(`planDetail`).post(data);

// Organization Plans
lib.orgPlans = async (data = {}) => myRequestObj(`orgPlans`).post(data);

// Submit Organization Plans
lib.submitOrgPlans = async (data = {}) => myRequestObj(`submitOrgPlans`).post(data);

// Remove Organization Plans
lib.removeOrgPlans = async (data = {}) => myRequestObj(`removeOrgPlan`).post(data);

/*
 *   Plan Category Designer
 */
// Get list of plan categories
lib.getCategories = async (data = {}) => myRequestObj(`categories`).post(data);

// Create a new category
lib.createCategory = async (data = {}) => myRequestObj(`createCategory`).post(data);

// Update a category
lib.updateCategory = async (data = {}) => myRequestObj(`updateCategory`).post(data);

// design a category
lib.designCategory = async (data = {}) => myRequestObj(`relateCatItems`).post(data);

// detail a category
lib.categoryDetails = async (data = {}) => myRequestObj(`categoryDetails`).post(data);

// Organization Password Management (Admin Only)
// Set organization password using social number
lib.setOrganizationPassword = async (data) => myApi('PUT', `v2/corporations/admin/password`, data);

// Set organization password using base64 hashed social number
lib.setOrganizationPasswordByHash = async (hashedSocialNo, password) => 
  myApi('PUT', `v2/corporations/admin/${hashedSocialNo}/password`, { password });

// Get paginated organizations list (v2 API)
lib.getOrganizationsV2 = async (params = {}) => {
  return myApi('GET', 'v2/corporations', params);
};

/** Resolve one organization from list route hash (base64 org_social_no). */
lib.getOrganizationByHash = async (hash) => {
  if (!hash) return null;
  let socialNo = hash;
  try {
    if (typeof atob === 'function') {
      const decoded = atob(hash);
      if (decoded) socialNo = decoded;
    }
  } catch (_) {
    /* keep hash as search term */
  }
  const resp = await lib.getOrganizationsV2({ search: socialNo, page: 1, limit: 50 });
  const parsed = lib.parseApiEnvelope(resp);
  if (!parsed.ok || !Array.isArray(parsed.data)) return null;
  return parsed.data.find((row) => (
    row.hash === hash
    || String(row.org_social_no) === String(socialNo)
    || (typeof btoa === 'function' && btoa(String(row.org_social_no || '')) === hash)
  )) || parsed.data[0] || null;
};

/** Parse axios catch for v2 envelopes (includes 409 blocking payloads). */
lib.parseApiCatch = (e) => {
  const body = e?.response?.data ?? {};
  const httpStatus = e?.response?.status;
  const data = body?.data ?? null;
  return {
    ok: false,
    error: Number(body?.error ?? 1),
    message: body?.message ?? body?.msg ?? e?.message ?? 'Request failed',
    data,
    meta: body?.meta ?? null,
    httpStatus,
    body,
    code: data?.code ?? null,
    blockingPolicies: Array.isArray(data?.blocking_policies) ? data.blocking_policies : [],
  };
};

const planRequestPath = (requestId, action) => {
  const base = `v2/corporations/admin/plan-requests/${encodeURIComponent(requestId)}`;
  return action ? `${base}/${action}` : base;
};

/** HR plan change / negotiation queue — list open + history. */
lib.getPlanRequests = async (params = {}) => {
  const query = {
    status: 'open',
    page: 1,
    limit: 20,
    ...params,
  };
  return myApi('GET', 'v2/corporations/admin/plan-requests', query);
};

/** Single plan request with offers[] — GET /v2/corporations/admin/plan-requests/{id} */
lib.getPlanRequest = async (requestId) => {
  if (requestId == null) throw new Error('requestId is required');
  return myApi('GET', planRequestPath(requestId));
};

/** Open-queue count for nav badge (add negotiations + remove tickets). */
lib.getPendingPlanRequestsCount = async (orgId = null) => {
  const base = { status: 'open', page: 1, limit: 1 };
  if (orgId != null && orgId !== '') base.org_id = orgId;

  const parseTotal = (resp) => {
    const parsed = lib.parseApiEnvelope(resp);
    if (!parsed.ok) return null;
    return Number(parsed.meta?.total ?? parsed.data?.meta?.total ?? 0) || 0;
  };

  try {
    const [addResp, removeResp] = await Promise.all([
      lib.getPlanRequests({ ...base, action: 'add' }),
      lib.getPlanRequests({ ...base, action: 'remove' }),
    ]);
    const addTotal = parseTotal(addResp);
    const removeTotal = parseTotal(removeResp);
    if (addTotal != null && removeTotal != null) return addTotal + removeTotal;
  } catch (_) {
    /* fall through to mixed open */
  }

  const resp = await lib.getPlanRequests(base);
  const parsed = lib.parseApiEnvelope(resp);
  if (!parsed.ok) return 0;
  return Number(parsed.meta?.total ?? parsed.data?.meta?.total ?? 0) || 0;
};

/** Split open counts for Negotiations / Removals tab badges. */
lib.getOpenPlanRequestCounts = async (orgId = null) => {
  const base = { status: 'open', page: 1, limit: 1 };
  if (orgId != null && orgId !== '') base.org_id = orgId;
  const parseTotal = async (action) => {
    const parsed = lib.parseApiEnvelope(await lib.getPlanRequests({ ...base, action }));
    if (!parsed.ok) return 0;
    return Number(parsed.meta?.total ?? parsed.data?.meta?.total ?? 0) || 0;
  };
  const [add, remove] = await Promise.all([parseTotal('add'), parseTotal('remove')]);
  return { add, remove, total: add + remove };
};

/** HR bulk upload audit — GET /v2/corporations/{socialNo}/deals/upload-logs */
lib.listCorpBulkUploadLogs = async (socialNo, params = {}) => {
  if (!socialNo) throw new Error('socialNo is required');
  return myApi(
    'GET',
    `v2/corporations/${encodeURIComponent(socialNo)}/deals/upload-logs`,
    { page: 1, limit: 10, ...params },
  );
};

/** Staff upload approval queue — list pending/history.
 * Pass status: 'all' (or source_group: 'all') for unfiltered lists.
 * Omitting status defaults to pending on the API — always send explicitly when needed.
 */
lib.getOrganizationStaffUploads = async (params = {}) => {
  const query = {
    page: 1,
    limit: 20,
    ...params,
  };
  // Default to pending only when caller did not set status at all
  if (query.status == null) query.status = 'pending';
  // Drop undefined keys so axios does not serialize them oddly
  Object.keys(query).forEach((key) => {
    if (query[key] === undefined) delete query[key];
  });
  return myApi('GET', 'v2/corporations/admin/staff-uploads', query);
};

/**
 * Aggregate staff-upload counts — GET /v2/corporations/admin/staff-uploads/counts.
 * Returns { awaiting_review_total, pending, partially_reviewed, failed, by_status }.
 * Optional filters: org_id, source_group ('bulk' | 'staff_requests').
 */
lib.getStaffUploadCounts = async (params = {}) => {
  const query = {};
  if (params.org_id != null && params.org_id !== '') query.org_id = params.org_id;
  if (params.source_group != null && params.source_group !== '' && params.source_group !== 'all') {
    query.source_group = params.source_group;
  }
  return myApi('GET', 'v2/corporations/admin/staff-uploads/counts', query);
};

/**
 * Count of staff-upload batches awaiting review (pending + partially_reviewed).
 * Uses the dedicated counts endpoint; falls back to the single-status list meta
 * when the counts endpoint is unavailable (older backend).
 */
lib.getPendingStaffUploadsCount = async (orgId = null, sourceGroup = null) => {
  const parsed = lib.parseApiEnvelope(await lib.getStaffUploadCounts({ org_id: orgId, source_group: sourceGroup }));
  if (parsed.ok) {
    const data = parsed.data ?? {};
    const total = data.awaiting_review_total
      ?? ((Number(data.pending) || 0) + (Number(data.partially_reviewed) || 0));
    return Number(total) || 0;
  }
  // Fallback: legacy single-status list meta.total
  const params = { status: 'pending', page: 1, limit: 1 };
  if (orgId != null && orgId !== '') params.org_id = orgId;
  const legacy = lib.parseApiEnvelope(await lib.getOrganizationStaffUploads(params));
  if (!legacy.ok) return 0;
  return Number(legacy.meta?.total ?? legacy.data?.meta?.total ?? 0) || 0;
};

lib.getOrganizationStaffUpload = async (batchId) => {
  if (!batchId) throw new Error('batchId is required');
  return myApi(
    'GET',
    `v2/corporations/admin/staff-uploads/${encodeURIComponent(batchId)}`,
  );
};

lib.reviewOrganizationStaffUpload = async (batchId, decision, payload = {}) => {
  if (!batchId) throw new Error('batchId is required');
  if (!['approve', 'reject'].includes(decision)) throw new Error('Invalid staff upload decision');
  return myApi(
    'POST',
    `v2/corporations/admin/staff-uploads/${encodeURIComponent(batchId)}/${decision}`,
    payload,
  );
};

lib.reviewOrganizationStaffUploadItem = async (itemId, decision, payload = {}) => {
  if (!itemId) throw new Error('itemId is required');
  const allowed = ['approve', 'reject', 'mark-approve', 'mark-reject'];
  if (!allowed.includes(decision)) throw new Error('Invalid staff upload item decision');
  return myApi(
    'POST',
    `v2/corporations/admin/staff-uploads/items/${encodeURIComponent(itemId)}/${decision}`,
    payload,
  );
};

/* ---------------------------------------------------------------------------
 * Claims adjudication of line items.
 *
 * Two stages on purpose: marking stages a decision and moves no money, applying
 * commits it. claim_flag is what the payable totals read, so a reviewer ticking
 * through lines must not write it directly.
 * ------------------------------------------------------------------------- */

/**
 * The claim's line items from v2, which carry the adjudication fields
 * (claim_mark, claim_decision, claim_decidable). The legacy PAListAdminClaim
 * payload does not, so the review panel loads its own rather than depending on
 * whatever the host screen happened to fetch.
 */
lib.getClaimItemsV2 = async (checkinId, params = {}) => {
  if (!checkinId) throw new Error('checkinId is required');
  return myApi('GET', `v2/claims/${encodeURIComponent(checkinId)}/items`, {
    include_flags: false, page: 1, pageSize: 100, ...params,
  });
};

/** Decision counts for the review header. */
lib.getClaimLineDecisions = async (checkinId) => {
  if (!checkinId) throw new Error('checkinId is required');
  return myApi('GET', `v2/claims/${encodeURIComponent(checkinId)}/decisions`);
};

/**
 * Stage a decision on specific lines.
 * One request for the whole selection -- not one per row.
 */
lib.markClaimLines = async (checkinId, itemIds, decision, comment = '') => {
  if (!checkinId) throw new Error('checkinId is required');
  const ids = (Array.isArray(itemIds) ? itemIds : [itemIds])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
  if (!ids.length) throw new Error('At least one line is required');
  const allowed = ['accept', 'reject', 'clear'];
  if (!allowed.includes(decision)) throw new Error('Invalid claim line decision');
  return myApi('POST', `v2/claims/${encodeURIComponent(checkinId)}/decisions/mark`, {
    item_ids: ids,
    decision,
    ...(comment ? { comment } : {}),
  });
};

/** Accept all / reject all: stages the decision on every decidable line. */
lib.markAllClaimLines = async (checkinId, decision, comment = '') => {
  if (!checkinId) throw new Error('checkinId is required');
  if (!['accept', 'reject'].includes(decision)) throw new Error('Invalid claim line decision');
  return myApi('POST', `v2/claims/${encodeURIComponent(checkinId)}/decisions/mark-all`, {
    decision,
    ...(comment ? { comment } : {}),
  });
};

/** Commit every staged mark on the claim. This is the step that moves money. */
lib.applyClaimLineDecisions = async (checkinId) => {
  if (!checkinId) throw new Error('checkinId is required');
  return myApi('POST', `v2/claims/${encodeURIComponent(checkinId)}/decisions/apply`, {});
};

/** Commit staged mark-approve / mark-reject decisions for a batch. */
lib.applyOrganizationStaffUploadDecisions = async (batchId, payload = {}) => {
  if (!batchId) throw new Error('batchId is required');
  return myApi(
    'POST',
    `v2/corporations/admin/staff-uploads/${encodeURIComponent(batchId)}/apply-decisions`,
    payload,
  );
};

/** Download per-row batch report CSV (admin). */
lib.downloadOrganizationStaffUploadReport = async (batchId, filename) => {
  if (!batchId) throw new Error('batchId is required');
  const path = `v2/corporations/admin/staff-uploads/${encodeURIComponent(batchId)}/report.csv`;
  return lib.downloadFileWithAuth(path, filename || `staff_upload_batch_${batchId}.csv`);
};

// ─── Enrollment conflicts (finance / care-admin) ─────────────────────────────

const enrollmentConflictsBase = 'v2/admin/enrollment-conflicts/';

lib.getEnrollmentConflictsSummary = async (params = {}) => {
  const query = {};
  if (params.social_no) query.social_no = params.social_no;
  if (params.organization_id != null && params.organization_id !== '') {
    query.organization_id = params.organization_id;
  }
  return myApi('GET', `${enrollmentConflictsBase}/summary`, query);
};

lib.getEnrollmentConflicts = async (params = {}) => {
  const query = {
    status: params.status ?? 'open',
    page: params.page ?? 1,
    pageSize: params.pageSize ?? params.limit ?? 20,
  };
  ['social_no', 'organization_id', 'session_id', 'conflict_code', 'severity'].forEach((key) => {
    if (params[key] != null && params[key] !== '') query[key] = params[key];
  });
  return myApi('GET', enrollmentConflictsBase, query);
};

lib.getEnrollmentConflict = async (conflictId) => {
  if (conflictId == null || conflictId === '') throw new Error('conflictId is required');
  return myApi('GET', `${enrollmentConflictsBase}/${encodeURIComponent(conflictId)}`);
};

lib.resolveEnrollmentConflict = async (conflictId, payload = {}) => {
  if (conflictId == null || conflictId === '') throw new Error('conflictId is required');
  return myApi(
    'POST',
    `${enrollmentConflictsBase}/${encodeURIComponent(conflictId)}/resolve`,
    payload,
  );
};

lib.exportEnrollmentConflictsCsv = async (params = {}, filename) => {
  const query = new URLSearchParams();
  ['session_id', 'social_no', 'organization_id', 'status', 'conflict_code', 'severity'].forEach((key) => {
    if (params[key] != null && params[key] !== '') query.set(key, String(params[key]));
  });
  if (!query.has('status')) query.set('status', 'open');
  const qs = query.toString();
  const path = `${enrollmentConflictsBase}/export.csv${qs ? `?${qs}` : ''}`;
  return lib.downloadFileWithAuth(
    path,
    filename || `enrollment_conflicts_${params.session_id || params.social_no || 'open'}.csv`,
  );
};

lib.getEnrollmentConflictSession = async (sessionId) => {
  if (sessionId == null || sessionId === '') throw new Error('sessionId is required');
  return myApi('GET', `${enrollmentConflictsBase}/sessions/${encodeURIComponent(sessionId)}`);
};

lib.reuploadEnrollmentConflictSession = async (sessionId, rows = []) => {
  if (sessionId == null || sessionId === '') throw new Error('sessionId is required');
  return myApi(
    'POST',
    `${enrollmentConflictsBase}/sessions/${encodeURIComponent(sessionId)}/reupload`,
    { rows },
  );
};

lib.applyEnrollmentConflictSession = async (sessionId) => {
  if (sessionId == null || sessionId === '') throw new Error('sessionId is required');
  return myApi(
    'POST',
    `${enrollmentConflictsBase}/sessions/${encodeURIComponent(sessionId)}/apply`,
    {},
  );
};

lib.scanEnrollmentConflicts = async (payload = {}) => {
  return myApi('POST', `${enrollmentConflictsBase}/scan`, payload);
};

/**
 * Bulk-clear open enrollment conflicts for an org.
 * Requires confirm:true. Does not delete live clients.
 */
lib.clearEnrollmentConflicts = async (payload = {}) => {
  return myApi('POST', `${enrollmentConflictsBase}/clear`, payload);
};

/** Open-conflict badge count (global or org-scoped). */
lib.getEnrollmentConflictsOpenCount = async (params = {}) => {
  const parsed = lib.parseApiEnvelope(await lib.getEnrollmentConflictsSummary(params));
  if (!parsed.ok) {
    if (Number(parsed.httpStatus) === 503) {
      return { open: 0, pausedSessions: 0, unavailable: true, message: parsed.message };
    }
    return { open: 0, pausedSessions: 0, unavailable: true, message: parsed.message };
  }
  const data = parsed.data || {};
  return {
    open: Number(data.open_conflicts_total) || 0,
    pausedSessions: Number(data.sessions_needs_resolution) || 0,
    byCode: data.by_code || {},
    bySeverity: data.by_severity || {},
    unavailable: false,
  };
};

// Org enrollment segment definitions (corporate vs SME classification)
const orgEnrollmentSegmentsBase = 'v2/admin/org-enrollment-segments/';

lib.getOrgEnrollmentSegmentsTaxonomy = async () => myApi('GET', `${orgEnrollmentSegmentsBase}/taxonomy`);

lib.getOrgEnrollmentSegments = async () => myApi('GET', orgEnrollmentSegmentsBase);

lib.updateOrgEnrollmentSegment = async (key, payload = {}) => {
  if (!key) throw new Error('segment key is required');
  return myApi('PATCH', `${orgEnrollmentSegmentsBase}/${encodeURIComponent(key)}`, payload);
};

/** Pre-check before approving a plan removal. */
lib.checkOrgPlanRemoval = async (socialNo, planId) => {
  if (!socialNo || planId == null) {
    throw new Error('socialNo and planId are required');
  }
  const path = `v2/corporations/${encodeURIComponent(socialNo)}/policies/${encodeURIComponent(planId)}/removal-check`;
  return myApi('GET', path);
};

/** Normalize one org plan catalog row from v2 or legacy shapes. */
lib.normalizeOrgPlanCatalogRow = (row = {}) => ({
  ...row,
  plan_id: row.plan_id ?? row.id,
  plan_name: row.plan_name ?? row.name ?? '',
  org_plan_cost: row.org_plan_cost ?? row.cost ?? 0,
  org_plan_familty_cost: row.org_plan_family_cost ?? row.org_plan_familty_cost ?? row.family_cost ?? 0,
  plan_description: row.plan_description ?? row.description ?? '',
});

/** Active plan types assigned to an org (Manage plans) — prefer /assigned. */
lib.getOrganizationPlanCatalog = async (socialNo) => {
  if (!socialNo) throw new Error('socialNo is required');
  const base = `v2/corporations/${encodeURIComponent(socialNo)}/policies`;
  // Explicit path avoids colliding with the policy-holder roster (?tab=...).
  const assigned = await myApi('GET', `${base}/assigned`);
  const assignedParsed = lib.parseApiEnvelope(assigned);
  if (assignedParsed.ok || assignedParsed.httpStatus !== 404) {
    return assigned;
  }
  return myApi('GET', `${base}/`);
};

lib.parseOrganizationPlanCatalog = (resp) => {
  const parsed = lib.parseApiEnvelope(resp);
  if (!parsed.ok) {
    return { ok: false, rows: [], message: parsed.message };
  }
  // Guard: policy-holder roster shape (current_tab/tabs) is not Manage plans.
  const envelope = resp?.data && typeof resp.data === 'object' ? resp.data : resp;
  if (envelope && (envelope.current_tab != null || envelope.tabs != null)) {
    return {
      ok: false,
      rows: [],
      message: 'Unexpected policy-holder response for Manage plans. Use GET .../policies/assigned (no tab).',
    };
  }
  const data = parsed.data;
  let rows = [];
  if (Array.isArray(data)) rows = data;
  else if (Array.isArray(data?.plans)) rows = data.plans;
  else if (Array.isArray(data?.rows)) rows = data.rows;
  return {
    ok: true,
    rows: rows.map(lib.normalizeOrgPlanCatalogRow),
    message: parsed.message,
  };
};

/** Admin bypass — add plan type to org immediately (staff JWT). */
lib.addOrganizationPlan = async (socialNo, { plan_id, cost, family_cost } = {}) => {
  if (!socialNo || plan_id == null) {
    throw new Error('socialNo and plan_id are required');
  }
  const body = {
    plan_id,
    id: plan_id,
    org_plan_cost: cost ?? 0,
    org_plan_family_cost: family_cost ?? 0,
    cost: cost ?? 0,
    family_cost: family_cost ?? 0,
  };
  // Use myApi (same path style as GET). Avoid raw axios POST→redirect→GET 405s.
  const path = `v2/corporations/${encodeURIComponent(socialNo)}/policies/`;
  let parsed = lib.parseApiEnvelope(await myApi('POST', path, body));
  if (parsed.ok) return parsed;

  const is405 = parsed.httpStatus === 405 || /method not allowed/i.test(String(parsed.message || ''));
  if (!is405) return parsed;

  // Fallback: bulk `plans[]` shape some builds accept exclusively.
  return lib.parseApiEnvelope(await myApi('POST', path, {
    plans: [{
      id: plan_id,
      plan_id,
      cost: cost ?? 0,
      family_cost: family_cost ?? 0,
      org_plan_cost: cost ?? 0,
      org_plan_family_cost: family_cost ?? 0,
    }],
  }));
};

/** Admin bypass — add several plan types in one request when supported. */
lib.addOrganizationPlans = async (socialNo, plans = []) => {
  if (!socialNo) throw new Error('socialNo is required');
  const rows = (Array.isArray(plans) ? plans : [])
    .filter((p) => p && (p.plan_id != null || p.id != null))
    .map((p) => {
      const planId = p.plan_id ?? p.id;
      const cost = p.cost ?? 0;
      const familyCost = p.family_cost ?? 0;
      return {
        id: planId,
        plan_id: planId,
        cost,
        family_cost: familyCost,
        org_plan_cost: cost,
        org_plan_family_cost: familyCost,
      };
    });
  if (!rows.length) throw new Error('At least one plan is required');
  const path = `v2/corporations/${encodeURIComponent(socialNo)}/policies/`;
  const resp = await myApi('POST', path, { plans: rows });
  return lib.parseApiEnvelope(resp);
};

/** Admin bypass — edit per-org plan cost (L3): PATCH policies/{planId}. Body: { cost, family_cost }. */
lib.updateOrganizationPlanCost = async (socialNo, planId, { cost, family_cost } = {}) => {
  if (!socialNo || planId == null) {
    throw new Error('socialNo and planId are required');
  }
  const body = {};
  if (cost !== undefined && cost !== null) body.cost = cost;
  if (family_cost !== undefined && family_cost !== null) body.family_cost = family_cost;
  try {
    const resp = await request.patch(
      `/v2/corporations/${encodeURIComponent(socialNo)}/policies/${encodeURIComponent(planId)}`,
      body,
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

/** Admin bypass — remove plan type from org; enforces removal guard (409). */
lib.removeOrganizationPlan = async (socialNo, planId) => {
  if (!socialNo || planId == null) {
    throw new Error('socialNo and planId are required');
  }
  try {
    const resp = await request.delete(
      `/v2/corporations/${encodeURIComponent(socialNo)}/policies/${encodeURIComponent(planId)}`,
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

/** Approve plan request; surfaces PLAN_REMOVAL_BLOCKED on 409. */
/** Edit negotiated costs on a pending add request (§5.1.1). */
/**
 * Admin counter / cost update.
 * PATCH is a compat alias that creates an admin offer (status → countered).
 * Prefer createPlanRequestOffer for structured individual/family bodies.
 */
lib.updatePlanRequest = async (requestId, payload = {}) => {
  if (requestId == null) throw new Error('requestId is required');
  const body = { ...payload };
  try {
    const resp = await request.patch(
      `/${planRequestPath(requestId)}`,
      body,
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

/** POST admin offer — POST /v2/corporations/admin/plan-requests/{id}/offers */
lib.createPlanRequestOffer = async (requestId, payload = {}) => {
  if (requestId == null) throw new Error('requestId is required');
  try {
    const resp = await request.post(
      `/${planRequestPath(requestId, 'offers')}`,
      payload,
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

/**
 * Approve plan request.
 * Normal path: only when status=org_accepted (or can_approve).
 * Force: { force: true } from any open add status.
 */
lib.approvePlanRequest = async (
  requestId,
  { note, org_plan_cost, org_plan_family_cost, force } = {},
) => {
  if (requestId == null) throw new Error('requestId is required');
  const body = { note: note ?? '' };
  if (force) body.force = true;
  if (org_plan_cost !== undefined && org_plan_cost !== null) body.org_plan_cost = org_plan_cost;
  if (org_plan_family_cost !== undefined && org_plan_family_cost !== null) {
    body.org_plan_family_cost = org_plan_family_cost;
  }
  try {
    const resp = await request.post(
      `/${planRequestPath(requestId, 'approve')}`,
      body,
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

/** Reject plan request. */
lib.rejectPlanRequest = async (requestId, { note } = {}) => {
  if (requestId == null) throw new Error('requestId is required');
  try {
    const resp = await request.post(
      `/${planRequestPath(requestId, 'reject')}`,
      { note: note ?? '' },
      setHeader(),
    );
    return lib.parseApiEnvelope(resp);
  } catch (e) {
    return lib.parseApiCatch(e);
  }
};

/*
 * Admin Facilities API (v2) - List and detail for facility management
 */
// List facilities (admin) - paginated with search/filters
lib.getFacilitiesV2 = async (params = {}) => {
  const queryParams = {
    page: 1,
    limit: 20,
    ...params,
  };
  return myApi('GET', 'v2/admin/facilities/', queryParams);
};

// Shared query params for facility suggestions list / summary / export (v2 admin)
lib.buildFacilitySuggestionSharedParams = (params = {}) => {
  const out = {};
  const search = (params.search ?? params.q ?? '').trim();
  if (search) out.search = search;
  if (params.from_date) out.from_date = params.from_date;
  if (params.to_date) out.to_date = params.to_date;
  const st = params.state != null ? String(params.state).trim() : '';
  if (st) out.state = st;
  const ho = params.has_org;
  if (ho === 0 || ho === 1 || ho === '0' || ho === '1') out.has_org = String(ho);
  const em = params.enrollee_matched;
  if (em === 0 || em === 1 || em === '0' || em === '1') out.enrollee_matched = String(em);
  const ob = params.onboarded;
  if (ob === 0 || ob === 1 || ob === '0' || ob === '1') out.onboarded = String(ob);
  const os = params.onboarding_status != null ? String(params.onboarding_status).trim() : '';
  if (os && ['pending', 'linked', 'dismissed', 'duplicate'].includes(os)) {
    out.onboarding_status = os;
  }
  return out;
};

// Enrollee-submitted facility suggestions (admin, read-only)
lib.getFacilitySuggestionsV2 = async (params = {}) => {
  const rawLimit = Number(params.limit);
  const limit = Number.isFinite(rawLimit) ? Math.min(Math.max(1, rawLimit), 100) : 20;
  const rawPage = Number(params.page);
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
  const shared = lib.buildFacilitySuggestionSharedParams(params);
  const queryParams = {
    page,
    limit,
    ...shared,
  };
  return myApi('GET', 'v2/admin/facilities/suggestions', queryParams);
};

// Aggregate counts for facility suggestions (same filters as list; no pagination)
lib.getFacilitySuggestionsSummaryV2 = async (params = {}) => {
  const shared = lib.buildFacilitySuggestionSharedParams(params);
  return myApi('GET', 'v2/admin/facilities/suggestions/summary', shared);
};

/** Count of facility suggestions awaiting onboarding review (onboarding_status = pending). */
lib.getPendingFacilitySuggestionsCount = async () => {
  try {
    const parsed = lib.parseApiEnvelope(await lib.getFacilitySuggestionsSummaryV2({}));
    if (!parsed.ok) return 0;
    const data = parsed.data ?? {};
    return Number(data.onboarding_pending ?? data.summary?.onboarding_pending ?? 0) || 0;
  } catch (_) {
    return 0;
  }
};

// CSV export (authenticated blob download; same filters as list, no page/limit)
lib.exportFacilitySuggestionsV2 = async (params = {}) => {
  const shared = lib.buildFacilitySuggestionSharedParams(params);
  const queryParams = { ...shared };
  const maxRows = Number(params.max_rows);
  if (Number.isFinite(maxRows) && maxRows > 0) {
    queryParams.max_rows = Math.min(Math.max(1, maxRows), 10000);
  }
  const path = '/v2/admin/facilities/suggestions/export';
  try {
    const resp = await request.get(path, {
      ...setHeader(),
      params: queryParams,
      responseType: 'blob',
    });
    let blob = resp?.data;
    const ct = String(resp?.headers?.['content-type'] || '').toLowerCase();
    if (ct.includes('application/json') || (blob && blob.type && blob.type.includes('json'))) {
      const text = typeof blob.text === 'function' ? await blob.text() : '';
      let msg = 'Export failed';
      try {
        const j = JSON.parse(text);
        msg = j.message || j.msg || msg;
      } catch (_) {
        if (text) msg = text;
      }
      throw new Error(msg);
    }
    const filename =
      getFilenameFromContentDisposition(resp?.headers?.['content-disposition']) ||
      'facility_suggestions_export.csv';
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return { success: true, filename };
  } catch (e) {
    const data = e?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      let msg = e?.response?.data?.message || 'Export failed';
      try {
        const j = JSON.parse(text);
        msg = j.message || j.msg || msg;
      } catch (_) {
        if (text) msg = text;
      }
      throw new Error(msg);
    }
    throw new Error(e?.response?.data?.message || e?.message || 'Export failed');
  }
};

// Shared query params for enrollee dashboard list / summary / export (v2 admin)
lib.buildEnrolleeDashboardSharedParams = (params = {}) => {
  const out = {};
  const search = (params.search ?? params.q ?? '').trim();
  if (search) out.search = search;

  const stringFields = [
    'enrollee_state',
    'policy_state',
    'lga',
    'policy_city',
    'gender',
    'marital_status',
    'religion',
    'relationship',
    'role',
    'expiry_from',
    'expiry_to',
    'premium_type',
    'dob_from',
    'dob_to',
    'social_no',
  ];

  stringFields.forEach((field) => {
    const value = params[field] != null ? String(params[field]).trim() : '';
    if (value) out[field] = value;
  });

  const numberFields = [
    'enrollee_status',
    'update_required',
    'flagged',
    'policy_status',
    'paymentStatus',
    'plans_id',
    'plan_type_id',
    'has_org',
    'age_min',
    'age_max',
    'org_id',
  ];

  numberFields.forEach((field) => {
    const raw = params[field];
    if (raw === '' || raw == null) return;
    const value = Number(raw);
    if (Number.isFinite(value)) out[field] = value;
  });

  // ?archived=0|1|all, default 0 (archived rows hidden). Always sent
  // explicitly: the backend normalises loosely, but relying on that would make
  // a typo silently mean "hide archived" instead of failing visibly.
  const archivedRaw = params.archived;
  if (archivedRaw === 'all') out.archived = 'all';
  else if (archivedRaw === 1 || archivedRaw === '1') out.archived = '1';
  else out.archived = '0';

  return out;
};

// Enrollee dashboard (admin): summary aggregates for tiles/charts
lib.getAdminEnrolleesSummary = async (params = {}) => {
  const shared = lib.buildEnrolleeDashboardSharedParams(params);
  return myApi('GET', 'v2/admin/enrollees/summary', shared);
};

// Enrollee dashboard (admin): paginated rows for table
lib.getAdminEnrolleesList = async (params = {}) => {
  const shared = lib.buildEnrolleeDashboardSharedParams(params);
  const rawLimit = Number(params.limit ?? params.pageSize);
  const rawPage = Number(params.page);
  const limit = Number.isFinite(rawLimit) ? Math.min(Math.max(1, rawLimit), 200) : 20;
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
  return myApi('GET', 'v2/admin/enrollees/list', { ...shared, page, limit });
};

// Enrollee dashboard (admin): CSV export with active filters
lib.exportAdminEnrollees = async (params = {}) => {
  const shared = lib.buildEnrolleeDashboardSharedParams(params);
  const queryParams = { ...shared };
  const maxRows = Number(params.max_rows);
  if (Number.isFinite(maxRows) && maxRows > 0) {
    queryParams.max_rows = Math.min(Math.max(1, maxRows), 100000);
  }
  const path = '/v2/admin/enrollees/export';
  try {
    const resp = await request.get(path, {
      ...setHeader(),
      params: queryParams,
      responseType: 'blob',
    });
    let blob = resp?.data;
    const ct = String(resp?.headers?.['content-type'] || '').toLowerCase();
    if (ct.includes('application/json') || (blob && blob.type && blob.type.includes('json'))) {
      const text = typeof blob.text === 'function' ? await blob.text() : '';
      let msg = 'Export failed';
      try {
        const j = JSON.parse(text);
        msg = j.message || j.msg || msg;
      } catch (_) {
        if (text) msg = text;
      }
      throw new Error(msg);
    }
    const filename =
      getFilenameFromContentDisposition(resp?.headers?.['content-disposition']) ||
      'enrollees_export.csv';
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return { success: true, filename };
  } catch (e) {
    const data = e?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      let msg = e?.response?.data?.message || 'Export failed';
      try {
        const j = JSON.parse(text);
        msg = j.message || j.msg || msg;
      } catch (_) {
        if (text) msg = text;
      }
      throw new Error(msg);
    }
    throw new Error(e?.response?.data?.message || e?.message || 'Export failed');
  }
};

// Update suggestion onboarding (link facility, clear link, or set status) — requires facilities.manage or admin.*
lib.updateFacilitySuggestionOnboarding = async (suggestionId, payload = {}) => {
  if (suggestionId == null || suggestionId === '') throw new Error('suggestionId is required');
  const encodedId = encodeURIComponent(suggestionId);
  return myApi('PUT', `v2/admin/facilities/suggestions/${encodedId}/onboarding`, payload);
};

// Get single facility by id (admin)
lib.getFacilityById = async (facilityId, params = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('GET', `v2/admin/facilities/${encodedId}`, params);
};

// Facility users (admin) - for FacilityDetail Users tab
lib.getFacilityUsers = async (facilityId, params = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  const queryParams = { page: 1, limit: 20, ...params };
  return myApi('GET', `v2/admin/facilities/${encodedId}/users`, queryParams);
};

/** Admin: create a facility portal user (name / email / phone / password / role). */
lib.createFacilityUser = async (facilityId, payload = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('POST', `v2/admin/facilities/${encodedId}/users`, payload);
};

/**
 * Admin: set a facility portal user's password.
 *
 * This is the only recovery path these users have — they sign in with a login
 * code and may have no email address, so there is no reset link to send them.
 */
lib.setFacilityUserPassword = async (facilityId, userId, password) => {
  if (!facilityId) throw new Error('facilityId is required');
  if (userId == null || userId === '') throw new Error('userId is required');
  const encodedFacilityId = encodeURIComponent(facilityId);
  const encodedUserId = encodeURIComponent(userId);
  const resp = await myApi(
    'POST',
    `v2/admin/facilities/${encodedFacilityId}/users/${encodedUserId}/password`,
    { password },
  );
  return lib.parseApiEnvelope(resp);
};

/** Admin: update facility portal user (name / email / phone / role). */
lib.updateFacilityUser = async (facilityId, userId, payload = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  if (userId == null || userId === '') throw new Error('userId is required');
  const encodedFacilityId = encodeURIComponent(facilityId);
  const encodedUserId = encodeURIComponent(userId);
  return myApi(
    'PATCH',
    `v2/admin/facilities/${encodedFacilityId}/users/${encodedUserId}`,
    payload
  );
};

/** Admin: set facility portal user password. */
lib.changeFacilityUserPassword = async (facilityId, userId, payload = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  if (userId == null || userId === '') throw new Error('userId is required');
  const encodedFacilityId = encodeURIComponent(facilityId);
  const encodedUserId = encodeURIComponent(userId);
  return myApi(
    'PUT',
    `v2/admin/facilities/${encodedFacilityId}/users/${encodedUserId}/password`,
    payload
  );
};

/** Admin: activate/deactivate facility portal user. Body status: 0|1 */
lib.updateFacilityUserStatus = async (facilityId, userId, status) => {
  if (!facilityId) throw new Error('facilityId is required');
  if (userId == null || userId === '') throw new Error('userId is required');
  const encodedFacilityId = encodeURIComponent(facilityId);
  const encodedUserId = encodeURIComponent(userId);
  return myApi(
    'PUT',
    `v2/admin/facilities/${encodedFacilityId}/users/${encodedUserId}/status`,
    { status: Number(status) ? 1 : 0 }
  );
};

// Facility accesses/permissions (admin) - for FacilityDetail Access tab
lib.getFacilityAccesses = async (facilityId, params = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('GET', `v2/admin/facilities/${encodedId}/accesses`, params);
};

lib.updateFacilityAccesses = async (facilityId, payload = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('PUT', `v2/admin/facilities/${encodedId}/accesses`, payload);
};

// Create/update facility (admin) - optional, for Info tab
lib.createFacilityV2 = async (payload = {}) => {
  // Trailing slash matches dual-registered POST '' and POST '/' on api_v1.
  return myApi('POST', 'v2/admin/facilities/', payload);
};

lib.updateFacilityV2 = async (facilityId, payload = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('PUT', `v2/admin/facilities/${encodedId}`, payload);
};

/*
 * Facility branching (admin) — a facility can be a "branch" of a "main" facility.
 * Group membership drives shared encounter codes, cross-branch encounter
 * aggregation, and tariff assignment. Backend: /v2/admin/facilities/{id}/branches
 */

/** Group view for a facility: the main, its branches, and each branch's tariff source. */
lib.getFacilityBranchesV2 = async (facilityId, params = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('GET', `v2/admin/facilities/${encodedId}/branches`, params);
};

/**
 * Attach an EXISTING facility as a branch of `facilityId`.
 * To onboard a brand-new facility straight into a group, pass parent_facility_id
 * to lib.createFacilityV2 instead — that path also provisions portal credentials.
 */
lib.attachFacilityBranchV2 = async (facilityId, payload = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('POST', `v2/admin/facilities/${encodedId}/branches`, payload);
};

/** Return a branch to standalone. Its encounters, users and tariffs stay with it. */
lib.detachFacilityBranchV2 = async (facilityId, branchId) => {
  if (!facilityId || !branchId) throw new Error('facilityId and branchId are required');
  const encodedId = encodeURIComponent(facilityId);
  const encodedBranch = encodeURIComponent(branchId);
  return myApi('DELETE', `v2/admin/facilities/${encodedId}/branches/${encodedBranch}`);
};

/**
 * Put a branch on its main facility's tariff.
 * mode 'linked' (default) points the branch at the main's tariff so it tracks
 * future updates; mode 'copy' snapshots it into a branch-owned tariff that can
 * then be edited independently.
 */
lib.assignBranchTariffV2 = async (facilityId, branchId, payload = {}) => {
  if (!facilityId || !branchId) throw new Error('facilityId and branchId are required');
  const encodedId = encodeURIComponent(facilityId);
  const encodedBranch = encodeURIComponent(branchId);
  return myApi('POST', `v2/admin/facilities/${encodedId}/branches/${encodedBranch}/tariff`, {
    mode: 'linked',
    ...payload,
  });
};

/** Encounter roll-up across a facility group (main + branches). */
lib.getGroupEncounterSummaryV2 = async (params = {}) => {
  return myApi('GET', 'v2/checkins/group-summary', params);
};

/** Bulk create facilities (admin). Max 500 rows. Auth: facilities.manage | admin.* */
lib.bulkCreateFacilitiesV2 = async (facilities = []) => {
  return myApi('POST', 'v2/admin/facilities/bulk', { facilities });
};

/**
 * Multipart facility bulk upload — CSV / TSV / XLSX. Server parses with RFC4180 / PhpSpreadsheet.
 * jobId is generated by the caller and sent along with the file so progress can be polled
 * via getFacilityBulkUploadJobStatusV2 while this (synchronous, potentially slow) request is in flight.
 */
lib.bulkCreateFacilitiesFileV2 = async (file, jobId) => {
  if (!file) throw new Error('file is required');
  const formData = new FormData();
  formData.append('file', file);
  if (jobId) formData.append('job_id', jobId);
  return myApi('POST', 'v2/admin/facilities/bulk', formData);
};

/** Recent facility bulk upload jobs — GET /v2/admin/facilities/bulk/jobs */
lib.listFacilityBulkUploadJobsV2 = async (params = {}) => {
  const query = {};
  if (params.page != null) query.page = params.page;
  if (params.limit != null) query.limit = params.limit;
  return myApi('GET', 'v2/admin/facilities/bulk/jobs', query);
};

/** Poll target for an in-progress bulk upload's row count / percent. */
lib.getFacilityBulkUploadJobStatusV2 = async (jobId) => {
  if (!jobId) throw new Error('jobId is required');
  return myApi('GET', `v2/admin/facilities/bulk/${encodeURIComponent(jobId)}/status`);
};

/** Download per-upload facility bulk report CSV. */
lib.downloadFacilityBulkUploadReportV2 = async (jobId, filename) => {
  if (!jobId) throw new Error('jobId is required');
  const path = `v2/admin/facilities/bulk/${encodeURIComponent(jobId)}/report`;
  return lib.downloadFileWithAuth(
    path,
    filename || `facility_bulk_${jobId}_report.csv`,
  );
};

/**
 * Generate / resend facility portal login email.
 * force: true regenerates password and sends email again.
 */
lib.onboardFacilityV2 = async (facilityId, { force = false } = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('POST', `v2/admin/facilities/${encodedId}/onboard`, { force: Boolean(force) });
};

/*
 * Claims Batching API (v2)
 */
// Get facility claims with hierarchical grouping
// @param groupBy - 'month' (default), 'week', 'year', or 'encounter_date'
// @param filters - Optional year, month, or week filters
lib.getFacilityClaimsBatches = async (params = {}) => {
  // Ensure group_by defaults to 'month' if not specified
  const queryParams = {
    group_by: 'month',
    ...params,
  };
  return myApi('GET', 'v2/claims/facility', queryParams);
};

// Get claims for a specific batch (encounter date)
lib.getBatchClaims = async (encounterDate, params = {}) => {
  return myApi('GET', `v2/claims/batches/${encounterDate}`, params);
};

// Get facilities list (admin) - claims grouped by facility
lib.getClaimsFacilities = async (params = {}) => {
  return myApi('GET', 'v2/claims/admin/by-facility', params);
};

// Get facility batches grouped by month (admin) - Legacy endpoint
lib.getFacilityBatches = async (facilityId, params = {}) => {
  return myApi('GET', `v2/claims/admin/facilities/${facilityId}/batches`, params);
};

/*
 * Admin Claims API (v2) - Simplified 3-level hierarchy
 */
// Get months list (admin)
lib.getAdminClaimsMonths = async (params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 12,
    ...params,
  };
  return myApi('GET', 'v2/admin/claims/months', queryParams);
};

// Get facilities in month (summary only - admin)
lib.getAdminMonthFacilities = async (month, params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 20,
    ...params,
  };
  return myApi('GET', `v2/admin/claims/months/${month}/facilities`, queryParams);
};

// Get status summary for facility in month (admin) - Phase 1: Fast summary
lib.getAdminFacilityStatusSummary = async (month, facilityId) => {
  return myApi('GET', `v2/admin/claims/months/${month}/facilities/${facilityId}/status-summary`);
};

// Get claims for facility in month (admin) - Phase 2: On-demand claims
// @param statusCode - Optional status code filter (0, 1, 2, 3, 4)
lib.getAdminFacilityClaims = async (month, facilityId, params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 20,
    ...params,
  };
  // If statusCode is provided, add it to query params
  if (params.statusCode !== undefined) {
    queryParams.status_code = params.statusCode;
    delete queryParams.statusCode; // Remove camelCase version
  }
  return myApi('GET', `v2/admin/claims/months/${month}/facilities/${facilityId}`, queryParams);
};

/*
 * Facility Encounter Summary analytics (v2)
 */
lib.buildFacilityEncounterSummaryQueryParams = (params = {}) => {
  const out = {};
  const scalarKeys = [
    'period_type',
    'date_from',
    'date_to',
    'month',
    'month_from',
    'month_to',
    'date_type',
    'search',
    'state',
    'source',
    'encounter_status',
    'has_escalation',
    'organization_id',
    'sort_by',
    'sort_order',
    'sort_dir',
    'output_format',
    'page',
    'pageSize',
  ];
  scalarKeys.forEach((key) => {
    if (params[key] != null && params[key] !== '') {
      out[key] = params[key];
    }
  });
  if (Array.isArray(params.facility_id) && params.facility_id.length) {
    params.facility_id.forEach((id, index) => {
      out[`facility_id[${index}]`] = id;
    });
  } else if (params.facility_id != null && params.facility_id !== '') {
    out.facility_id = params.facility_id;
  }
  return out;
};

lib.getFacilityEncounterSummarySchema = async () => {
  return myApi('GET', 'v2/admin/claims/analytics/facilities/schema');
};

lib.getFacilityEncounterSummary = async (params = {}) => {
  const queryParams = lib.buildFacilityEncounterSummaryQueryParams(params);
  return myApi('GET', 'v2/admin/claims/analytics/facilities', queryParams);
};

lib.previewFacilityEncounterSummary = async (params = {}) => {
  try {
    const resp = await request.post(
      '/v2/admin/claims/analytics/facilities/preview',
      { params: { ...params } },
      { ...setHeader() }
    );
    if (parseInt(resp?.data?.error)) {
      throw new Error(resp?.data?.message || 'Preview failed');
    }
    return resp;
  } catch (e) {
    const message =
      e?.response?.data?.message ||
      e?.response?.data?.msg ||
      e?.message ||
      'Preview failed';
    const err = new Error(message);
    err.status = e?.response?.status;
    throw err;
  }
};

lib.downloadFacilityEncounterSummarySync = async (params = {}) => {
  const outputFormat = params.output_format || 'csv';
  const queryParams = lib.buildFacilityEncounterSummaryQueryParams(params);
  try {
    const resp = await request.get(
      '/v2/admin/claims/analytics/facilities/export',
      { ...setHeader(), params: queryParams, responseType: 'blob' }
    );
    const blob = resp?.data;
    if (!blob) throw new Error('No export file available');
    const ext = outputFormat === 'pdf' ? 'pdf' : outputFormat === 'xlsx' ? 'xlsx' : 'csv';
    const filename =
      getFilenameFromContentDisposition(resp?.headers?.['content-disposition']) ||
      `facility_encounter_summary.${ext}`;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return { success: true, filename, serverRendered: true };
  } catch (e) {
    const status = e?.response?.status;
    const message =
      e?.response?.data?.message ||
      e?.response?.data?.msg ||
      e?.message ||
      'Failed to download facility encounter summary';
    const err = new Error(message);
    err.status = status;
    throw err;
  }
};

/*
 *   Payment Claims API Methods (Status-First Navigation)
 */
// Get payment status overview dashboard
lib.getPaymentStatuses = async () => {
  return myApi('GET', 'v2/admin/claims/payment/statuses');
};

// Get months for a payment status segment
lib.getPaymentStatusMonths = async (segment, params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 12,
    ...params,
  };
  return myApi('GET', `v2/admin/claims/payment/statuses/${segment}/months`, queryParams);
};

// Get facilities for a payment status segment and month
lib.getPaymentStatusFacilities = async (segment, month, params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 20,
    ...params,
  };
  return myApi('GET', `v2/admin/claims/payment/statuses/${segment}/months/${month}/facilities`, queryParams);
};

// Get claims for pending statuses (0, 1, 2)
lib.getPaymentStatusClaims = async (segment, month, facilityId, params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 20,
    ...params,
  };
  return myApi('GET', `v2/admin/claims/payment/statuses/${segment}/months/${month}/facilities/${facilityId}/claims`, queryParams);
};

// Get payment batches for paid status (3)
lib.getPaymentBatches = async (month, facilityId, params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 20,
    ...params,
  };
  return myApi('GET', `v2/admin/claims/payment/statuses/3/months/${month}/facilities/${facilityId}/batches`, queryParams);
};

// Get claims in a payment batch
lib.getPaymentBatchClaims = async (month, facilityId, paymentBatch, params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 20,
    ...params,
  };
  return myApi('GET', `v2/admin/claims/payment/statuses/3/months/${month}/facilities/${facilityId}/batches/${paymentBatch}/claims`, queryParams);
};

/*
 * Claims group archive (v2) — archive claim headers for a facility + month
 */
lib.previewClaimsArchive = async (body = {}) => {
  const facilityId = body.facility_id ?? body.facilityId;
  const month = body.month;
  const useGet = facilityId != null && month && (!body.scope || body.scope === 'facility');
  if (useGet) {
    const query = { facility_id: facilityId, month };
    // Segment 0 (Pending CO) is a real queue: test against null, never truthiness,
    // or the preview would count every status while the apply archives only one.
    if (body.segment !== null && body.segment !== undefined && body.segment !== '') {
      query.segment = body.segment;
    }
    if (body.archived) query.archived = 1;
    return myApi('GET', 'v2/admin/claims/archive/preview', query);
  }
  return myApi('POST', 'v2/admin/claims/archive/preview', body);
};

lib.archiveClaims = async (body = {}) => {
  return myApi('POST', 'v2/admin/claims/archive', body);
};

lib.unarchiveClaims = async (body = {}) => {
  return myApi('POST', 'v2/admin/claims/unarchive', body);
};

/*
 * Client requests archive (claim queries + pending verification)
 */
lib.archiveClientRequests = async (body = {}) => {
  return myApi('POST', 'v2/admin/client-requests/archive', body);
};

lib.unarchiveClientRequests = async (body = {}) => {
  return myApi('POST', 'v2/admin/client-requests/unarchive', body);
};

lib.getPendingEnrollees = async (params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 20,
    ...params,
  };
  return myApi('GET', 'v2/care/enrollees/pending', queryParams);
};

/*
 *   Claims Async Export Jobs API (v2)
 */
lib.createClaimsExportJob = async (exportType, params = {}) => {
  if (!exportType) throw new Error('exportType is required');
  try {
    const resp = await request.post(
      '/v2/admin/claims/exports/jobs/',
      {
        export_type: exportType,
        params: { ...params },
      },
      { ...setHeader() }
    );
    if (parseInt(resp?.data?.error)) {
      throw new Error(resp?.data?.message || 'Failed to queue export job');
    }
    return resp;
  } catch (e) {
    const message =
      e?.response?.data?.message ||
      e?.response?.data?.msg ||
      e?.message ||
      'Failed to queue export job';
    throw new Error(message);
  }
};

lib.listClaimsExportJobs = async (params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 20,
    ...params,
  };
  return myApi('GET', 'v2/admin/claims/exports/jobs/', queryParams);
};

lib.getClaimsExportJob = async (jobId) => {
  if (!jobId) throw new Error('jobId is required');
  const encodedId = encodeURIComponent(jobId);
  return myApi('GET', `v2/admin/claims/exports/jobs/${encodedId}`);
};

lib.downloadClaimsExportJob = async (jobId, fallbackFilename = 'claims_export.csv') => {
  const fetched = await lib.fetchClaimsExportJobBlob(jobId, fallbackFilename);
  const blobUrl = window.URL.createObjectURL(fetched.blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fetched.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
  return { success: true, filename: fetched.filename };
};

/**
 * Download a claims Excel sheet from the payment drill-down (Pending CO / HM /
 * CFO). Params: { segment, level: months|facilities|claims, month, facility_id }.
 * Errors come back as a JSON blob; their message is rethrown.
 */
lib.downloadClaimsSheetV2 = async (params = {}, fallbackFilename = 'claims.xlsx') => {
  let resp;
  try {
    resp = await request.get('/v2/admin/claims/payment/sheet', {
      ...setHeader(),
      params,
      responseType: 'blob',
    });
  } catch (e) {
    let message = e?.message || 'Download failed';
    try {
      const text = await e?.response?.data?.text?.();
      message = JSON.parse(text)?.message || message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const blob = resp?.data;
  if (!blob) throw new Error('No file returned');
  const filename =
    getFilenameFromContentDisposition(resp?.headers?.['content-disposition']) || fallbackFilename;
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
  return { success: true, filename };
};

/** Facility account details (finance only). Returns the API body or throws with `.errors`. */
const facilityAccountsCall = async (method, path, data) => {
  try {
    const resp = method === 'get'
      ? await request.get(path, { ...setHeader() })
      : await request[method](path, data, { ...setHeader() });
    const body = resp?.data || {};
    if (Number(body.error)) {
      const err = new Error(body.message || 'Request failed');
      err.errors = body.errors || {};
      throw err;
    }
    return body.data;
  } catch (e) {
    if (e?.errors) throw e;
    const body = e?.response?.data || {};
    const err = new Error(body.message || e?.message || 'Request failed');
    err.errors = body.errors || {};
    err.httpStatus = e?.response?.status;
    throw err;
  }
};

lib.getBanksV2 = async () => (await facilityAccountsCall('get', '/v2/admin/facilities/banks'))?.banks || [];

lib.getFacilityAccountDetailsV2 = async (facilityId) =>
  facilityAccountsCall('get', `/v2/admin/facilities/${encodeURIComponent(facilityId)}/account-details`);

lib.saveFacilityAccountDetailsV2 = async (facilityId, payload) =>
  facilityAccountsCall('put', `/v2/admin/facilities/${encodeURIComponent(facilityId)}/account-details`, payload);

/** Bulk account-details template: every facility, highest claim volume first. */
lib.downloadFacilityAccountTemplateV2 = async () => {
  const resp = await request.get('/v2/admin/facilities/account-import/template', { ...setHeader(), responseType: 'blob' });
  const filename =
    getFilenameFromContentDisposition(resp?.headers?.['content-disposition']) || 'facility-account-details.csv';
  const blobUrl = window.URL.createObjectURL(resp.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
  return filename;
};

/** Check (dryRun) or apply a filled account-details file. Resolves to { counts, rows, … }. */
lib.runFacilityAccountImportV2 = async (file, { dryRun = true, overwrite = false } = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('dry_run', dryRun ? '1' : '0');
  formData.append('overwrite', overwrite ? '1' : '0');
  return facilityAccountsCall('post', '/v2/admin/facilities/account-import/run', formData);
};

lib.fetchClaimsExportJobBlob = async (jobId, fallbackFilename = 'claims_export.csv') => {
  if (!jobId) throw new Error('jobId is required');
  const encodedId = encodeURIComponent(jobId);
  const path = `/v2/admin/claims/exports/jobs/${encodedId}/download`;
  const resp = await request.get(path, { ...setHeader(), responseType: 'blob' });
  const blob = resp?.data;
  if (!blob) throw new Error('No export file available');
  const filename =
    getFilenameFromContentDisposition(resp?.headers?.['content-disposition']) ||
    fallbackFilename;
  const contentType = resp?.headers?.['content-type'] || blob?.type || '';
  return { blob, filename, contentType, headers: resp?.headers };
};

lib.parseClaimsExportJobJson = async (jobId, fallbackFilename = 'payment_advice.json') => {
  const fetched = await lib.fetchClaimsExportJobBlob(jobId, fallbackFilename);
  const text = await fetched.blob.text();
  let payload;
  try {
    payload = JSON.parse(text);
  } catch {
    throw new Error('Export file is not valid JSON');
  }
  const hasSingleOrgRows = payload?.rows?.length || payload?.rows_flat?.length;
  const hasMultiOrgSections = payload?.organization_sections?.length;
  if (!hasSingleOrgRows && !hasMultiOrgSections) {
    throw new Error('Invalid payment advice export payload');
  }
  return { payload, filename: fetched.filename };
};

lib.downloadOrgPaymentAdviceSync = async (params = {}) => {
  const outputFormat = params.output_format || 'xlsx';
  const queryParams = lib.buildOrgPaymentAdviceQueryParams(params);
  try {
    const resp = await request.get(
      '/v2/admin/organizations/reports/payment-advice/data',
      { ...setHeader(), params: queryParams, responseType: 'blob' }
    );
    const blob = resp?.data;
    if (!blob) throw new Error('No export file available');
    const ext = outputFormat === 'pdf' ? 'pdf' : 'xlsx';
    const filename =
      getFilenameFromContentDisposition(resp?.headers?.['content-disposition']) ||
      `payment_advice.${ext}`;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return { success: true, filename, serverRendered: true };
  } catch (e) {
    const status = e?.response?.status;
    const message =
      e?.response?.data?.message ||
      e?.response?.data?.msg ||
      e?.message ||
      'Failed to download payment advice';
    const err = new Error(message);
    err.status = status;
    throw err;
  }
};

/*
 *   Organization Payment Advice (CBA) Report API (v2)
 */
lib.getOrgPaymentAdviceSchema = async () => {
  return myApi('GET', 'v2/admin/organizations/reports/payment-advice/schema');
};

lib.buildOrgPaymentAdviceQueryParams = (params = {}) => {
  const out = {};
  if (params.organization_id != null) out.organization_id = params.organization_id;
  if (Array.isArray(params.organization_ids) && params.organization_ids.length) {
    params.organization_ids.forEach((id, index) => {
      out[`organization_ids[${index}]`] = id;
    });
  }
  if (params.report_variant) out.report_variant = params.report_variant;
  if (params.output_format) out.output_format = params.output_format;
  if (params.claim_segment != null) out.claim_segment = params.claim_segment;
  if (params.grouping) out.grouping = params.grouping;
  if (params.period && typeof params.period === 'object') {
    Object.entries(params.period).forEach(([key, value]) => {
      if (value != null && value !== '') out[`period[${key}]`] = value;
    });
  }
  if (params.filters && typeof params.filters === 'object') {
    Object.entries(params.filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length) out[`filters[${key}]`] = value;
      } else if (value != null && value !== '') {
        out[`filters[${key}]`] = value;
      }
    });
  }
  if (params.report_options && typeof params.report_options === 'object') {
    Object.entries(params.report_options).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item != null && item !== '') out[`report_options[${key}][${index}]`] = item;
        });
      } else if (value != null && value !== '') {
        out[`report_options[${key}]`] = value;
      }
    });
  }
  if (Array.isArray(params.columns) && params.columns.length) {
    params.columns.forEach((col, index) => {
      out[`columns[${index}]`] = col;
    });
  }
  return out;
};

lib.previewOrgPaymentAdvice = async (params = {}) => {
  try {
    const resp = await request.post(
      '/v2/admin/organizations/reports/payment-advice/preview',
      { params: { ...params } },
      { ...setHeader() }
    );
    if (parseInt(resp?.data?.error)) {
      throw new Error(resp?.data?.message || 'Preview failed');
    }
    return resp;
  } catch (e) {
    const message =
      e?.response?.data?.message ||
      e?.response?.data?.msg ||
      e?.message ||
      'Preview failed';
    throw new Error(message);
  }
};

lib.fetchOrgPaymentAdviceData = async (params = {}) => {
  const queryParams = lib.buildOrgPaymentAdviceQueryParams(params);
  try {
    const resp = await request.get(
      '/v2/admin/organizations/reports/payment-advice/data',
      { ...setHeader(), params: queryParams }
    );
    if (parseInt(resp?.data?.error)) {
      throw new Error(resp?.data?.message || 'Failed to load payment advice data');
    }
    return resp;
  } catch (e) {
    const status = e?.response?.status;
    const message =
      e?.response?.data?.message ||
      e?.response?.data?.msg ||
      e?.message ||
      'Failed to load payment advice data';
    const err = new Error(message);
    err.status = status;
    throw err;
  }
};

lib.queueOrgPaymentAdviceExport = async (params = {}) => {
  return lib.createClaimsExportJob('org.payment_advice', params);
};

/*
 *   Facility Listing Export API (v2) — async jobs + sync CSV
 */
lib.buildFacilityExportQueryParams = (params = {}) => {
  const out = {};
  const scope = params.scope === 'all' ? 'all' : 'filtered';
  out.scope = scope;
  const search = (params.search ?? params.q ?? '').trim();
  if (scope === 'filtered' && search) out.search = search;
  const st = params.state != null ? String(params.state).trim() : '';
  if (st) out.state = st;
  if (params.status != null && params.status !== '') out.status = String(params.status);
  const fmt = params.format != null ? String(params.format).trim().toLowerCase() : '';
  if (fmt && ['csv', 'xlsx', 'pdf'].includes(fmt)) out.format = fmt;
  return out;
};

lib.getFacilityExportFields = async () => {
  return myApi('GET', 'v2/admin/facilities/export/fields');
};

lib.createFacilityExportJob = async (params = {}) => {
  const shared = lib.buildFacilityExportQueryParams(params);
  const fields = Array.isArray(params.fields) ? params.fields.filter(Boolean) : [];
  if (!fields.length) throw new Error('fields must be a non-empty array');
  const format = String(params.format || 'csv').trim().toLowerCase();
  const jobFormat = ['csv', 'xlsx', 'pdf'].includes(format) ? format : 'csv';
  try {
    const resp = await request.post(
      '/v2/admin/facilities/exports/jobs/',
      {
        params: {
          ...shared,
          fields,
          format: jobFormat,
        },
      },
      { ...setHeader() }
    );
    if (parseInt(resp?.data?.error)) {
      throw new Error(resp?.data?.message || 'Failed to queue facility export job');
    }
    return resp;
  } catch (e) {
    const message =
      e?.response?.data?.message ||
      e?.response?.data?.msg ||
      e?.message ||
      'Failed to queue facility export job';
    throw new Error(message);
  }
};

lib.listFacilityExportJobs = async (params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 20,
    ...params,
  };
  return myApi('GET', 'v2/admin/facilities/exports/jobs/', queryParams);
};

lib.getFacilityExportJob = async (jobId) => {
  if (!jobId) throw new Error('jobId is required');
  const encodedId = encodeURIComponent(jobId);
  return myApi('GET', `v2/admin/facilities/exports/jobs/${encodedId}`);
};

lib.downloadFacilityExportJob = async (jobId, fallbackFilename = 'facilities_export.csv') => {
  if (!jobId) throw new Error('jobId is required');
  const encodedId = encodeURIComponent(jobId);
  const path = `/v2/admin/facilities/exports/jobs/${encodedId}/download`;
  const resp = await request.get(path, { ...setHeader(), responseType: 'blob' });
  const blob = resp?.data;
  if (!blob) throw new Error('No export file available');

  const filename =
    getFilenameFromContentDisposition(resp?.headers?.['content-disposition']) ||
    fallbackFilename;
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
  return { success: true, filename };
};

/** Fetch sync export CSV blob without triggering browser download. */
lib.fetchFacilityExportCsvBlob = async (params = {}) => {
  const shared = lib.buildFacilityExportQueryParams(params);
  const fields = Array.isArray(params.fields) ? params.fields.filter(Boolean) : [];
  if (!fields.length) throw new Error('fields must be a non-empty array');
  const queryParams = {
    ...shared,
    fields: fields.join(','),
    format: 'csv',
  };
  const path = '/v2/admin/facilities/export';
  const resp = await request.get(path, {
    ...setHeader(),
    params: queryParams,
    responseType: 'blob',
    validateStatus: (status) => status === 200 || status === 413,
  });
  if (resp.status === 413) {
    return { useAsync: true };
  }
  let blob = resp?.data;
  const ct = String(resp?.headers?.['content-type'] || '').toLowerCase();
  if (ct.includes('application/json') || (blob && blob.type && blob.type.includes('json'))) {
    const text = typeof blob.text === 'function' ? await blob.text() : '';
    let msg = 'Export failed';
    try {
      const j = JSON.parse(text);
      msg = j.message || j.msg || msg;
    } catch (_) {
      if (text) msg = text;
    }
    throw new Error(msg);
  }
  return { useAsync: false, blob };
};

/** Sync CSV when row count ≤ server max_rows_sync; returns { useAsync: true } on 413. */
lib.exportFacilitiesSyncV2 = async (params = {}) => {
  const fields = Array.isArray(params.fields) ? params.fields.filter(Boolean) : [];
  if (!fields.length) throw new Error('fields must be a non-empty array');
  try {
    const fetched = await lib.fetchFacilityExportCsvBlob(params);
    if (fetched.useAsync) {
      return { useAsync: true };
    }
    const blob = fetched.blob;
    const filename =
      'facilities_export.csv';
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return { success: true, filename };
  } catch (e) {
    const data = e?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      let msg = e?.response?.data?.message || 'Export failed';
      try {
        const j = JSON.parse(text);
        msg = j.message || j.msg || msg;
      } catch (_) {
        if (text) msg = text;
      }
      throw new Error(msg);
    }
    throw new Error(e?.response?.data?.message || e?.message || 'Export failed');
  }
};

// Pay claims using V2 payment endpoint
// Two modes:
// - Selected claims: pass claimsIds array
// - Pay all for facility: pass facilityId + optional filters (fromDate/toDate or month/year), without claimsIds
// @param options { claimsIds?, facilityId?, filters?, paymentEvidenceFile }
lib.payClaimsV2 = async ({ claimsIds = null, facilityId = null, filters = {}, paymentEvidenceFile, withoutAdvice = false }) => {
  const formData = new FormData();

  if (Array.isArray(claimsIds) && claimsIds.length > 0) {
    formData.append('claims_ids', JSON.stringify(claimsIds));
  } else if (facilityId) {
    formData.append('pay_all', 'true');
    formData.append('facility_id', String(facilityId));

    // Optional filters
    if (filters.fromDate) formData.append('fromDate', filters.fromDate);
    if (filters.toDate) formData.append('toDate', filters.toDate);
    if (filters.month) formData.append('month', String(filters.month));
    if (filters.year) formData.append('year', String(filters.year));
  }

  if (withoutAdvice) {
    // Paid now, payment advice uploaded later (claims show "Paid — Payment Advice Pending").
    formData.append('without_payment_advice', '1');
  } else {
    formData.append('payment_evidence', paymentEvidenceFile);
  }
  
  // Use myApi - axios will automatically set Content-Type with boundary for FormData
  // Don't manually set Content-Type header - let axios handle it
  return myApi('POST', 'v2/admin/claims/payment/pay', formData);
};

/**
 * Organisation Manage Plans → Bands and Pricing.
 * GET: every band / sub-band on the plan with its base price and this
 * organisation's selection. PUT: save the full selection
 * { plan_id, social_no|org_id, bands: [{ band_id, price|null }] } — adds the
 * plan to the organisation first when it is not assigned yet.
 */
lib.getOrgPlanSetup = async ({ planId, socialNo, orgId }) => {
  const params = { plan_id: planId };
  if (orgId) params.org_id = orgId;
  if (socialNo) params.social_no = socialNo;
  return myApi('GET', 'v2/admin/pm/org-plan-setup', params);
};

lib.saveOrgPlanSetup = async ({ planId, socialNo, orgId, bands }) => {
  const body = { plan_id: planId, bands };
  if (orgId) body.org_id = orgId;
  if (socialNo) body.social_no = socialNo;
  return myApi('PUT', 'v2/admin/pm/org-plan-setup', body);
};

/** Upload the payment advice for a batch that was marked Paid without it. */
lib.uploadPaymentAdvice = async (paymentBatch, file) => {
  const formData = new FormData();
  formData.append('payment_batch', String(paymentBatch));
  formData.append('payment_evidence', file);
  return myApi('POST', 'v2/admin/claims/payment/advice', formData);
};

/*
 *   Plan Items Designer
 */
// Get a list of plan types
lib.getItems = async (data = {}) => myRequestObj(`items`).post(data);

// Create a plan item
lib.createItem = async (data = {}) => myRequestObj(`createItem`).post(data);

// update plan item
lib.updateItem = async (data = {}) => myRequestObj(`updateItem`).post(data);

// Plans Summary
lib.plansSummary = async (data = {}) => myRequestObj(`plansSummary`).post(data)

export const myTime = async () => myRequestObj('currentDate').post();

export const myRequest = async (type = "post", uri = "index", data = {}) => {
  let url = `?${uri}`;
  try {
    if (type === "get") {
      const resp = await request.get(url, data, setHeader());
      if (parseInt(resp?.data?.error)) {
        notification.error({
          key,
          message: resp?.data?.message,
          placement: "topLeft",
        });
        return { error: 1, data: [], message: resp?.data?.message };
      }
      return resp;
    } else {
      const resp = await request.post(url, data, setHeader());
      if (parseInt(resp?.data?.error)) {
        notification.error({
          key,
          message: resp?.data?.message,
          placement: "topLeft",
        });
        return { error: 1, data: [], message: resp?.data?.message };
      }
      return resp;
    }
  } catch (e) {
    // Handle 401 unauthorized responses
    if (e?.response?.status === 401) {
      // The interceptor will handle the logout, just return error
      return { status: "error", msg: "Unauthorized access" };
    }

    notification.error({
      key,
      message: e?.response?.data?.msg || e?.message,
      placement: "topLeft",
    });

    return { status: "error", msg: e?.response?.data?.msg || e?.message };
  }
};

export const myRequestObj = (uri = "index") => {
  const url = `?${uri}`;
  return {
    get: async (data = {}) => {
      try {
        const resp = await request.get(url, data, setHeader());
        if (parseInt(resp?.data?.error)) {
          notification.error({
            key,
            message: resp?.data?.message,
            placement: "topLeft",
          });
          return { error: 1, data: [], message: resp?.data?.message };
        }
        return resp;
      } catch (e) {
        // Handle 401 unauthorized responses
        if (e?.response?.status === 401) {
          // The interceptor will handle the logout, just return error
          return { status: "error", msg: "Unauthorized access" };
        }

        notification.error({
          key,
          message: e?.response?.data?.msg || e?.message,
          placement: "topLeft",
        });

        return { status: "error", msg: e?.response?.data?.msg || e?.message };
      }
    },
    post: async (data = {}) => {
      try {
        const resp = await request.post(url, data, setHeader());
        if (parseInt(resp?.data?.error)) {
          notification.error({
            key,
            message: resp?.data?.message,
            placement: "topLeft",
          });
          return { error: 1, data: [], message: resp?.data?.message };
        }
        return resp;
      } catch (e) {
        // Handle 401 unauthorized responses
        if (e?.response?.status === 401) {
          // The interceptor will handle the logout, just return error
          return { status: "error", msg: "Unauthorized access" };
        }

        notification.error({
          key,
          message: e?.response?.data?.msg || e?.message,
          placement: "topLeft",
        });

        return { status: "error", msg: e?.response?.data?.msg || e?.message };
      }
    },
  };
  // return requestObject;
};

// New: REST-style helper that targets /api/{endpoint}
export const myApi = async (method = 'get', endpoint = '', data = {}, configOverride = {}) => {
  const path = `/${endpoint.replace(/^\//, '')}`;
  try {
    const header = setHeader();
    const options = { ...header, ...configOverride };
    if (method.toLowerCase() === 'get') {
      const resp = await request.get(path, { ...options, params: data });
      if (parseInt(resp?.data?.error)) {
        notification.error({ key, message: resp?.data?.message });
        return { error: 1, data: [], message: resp?.data?.message }
      }
      return resp;
    } else if (method.toLowerCase() === 'delete') {
      const resp = await request.delete(path, { ...options, data });
      if (parseInt(resp?.data?.error)) {
        notification.error({ key, message: resp?.data?.message });
        return { error: 1, data: [], message: resp?.data?.message }
      }
      return resp;
    } else {
      const resp = await request[method.toLowerCase()](path, data, options);
      if (parseInt(resp?.data?.error)) {
        notification.error({ key, message: resp?.data?.message });
        return { error: 1, data: [], message: resp?.data?.message }
      }
      return resp;
    }
  } catch (e) {
    const body = e?.response?.data || {};
    const message = body?.message || body?.msg || e?.message;
    console.log(message)
    return {
      status: 'error',
      error: Number(body?.error ?? 1),
      code: body?.code || body?.data?.code,
      data: body?.data ?? null,
      message,
      msg: message,
      httpStatus: e?.response?.status,
    }
  }
}

/**
 * Normalize axios or myApi return value into a single envelope.
 * myApi returns bare `{ error, message }` (no `.data` wrapper) on application errors.
 */
lib.parseApiEnvelope = (resp) => {
  if (resp == null) {
    return { ok: false, error: 1, message: 'No response from server', data: null, body: null };
  }
  if (resp.status === 'error') {
    return {
      ok: false,
      error: Number(resp.error ?? 1),
      message: resp.msg || resp.message || 'Request failed',
      data: resp.data ?? null,
      body: resp,
      code: resp.code || resp.data?.code || null,
      httpStatus: resp.httpStatus ?? null,
    };
  }
  const isAxiosResponse = Boolean(resp.config);
  const body = isAxiosResponse ? (resp.data ?? {}) : (resp ?? {});
  const error = Number(body?.error ?? 0);
  const message = body?.message ?? body?.msg ?? '';
  return {
    ok: error === 0,
    error,
    message,
    data: body?.data ?? null,
    meta: body?.meta ?? null,
    body,
    code: body?.code || body?.data?.code || null,
    httpStatus: resp.httpStatus ?? (isAxiosResponse ? resp.status : null),
  };
};

/** Show success/error toast from parseApiEnvelope; returns true when ok. */
lib.notifyApiEnvelope = (parsed, { successFallback, errorFallback } = {}) => {
  if (parsed?.ok) {
    notification.success({ message: parsed.message || successFallback || 'Saved' });
    return true;
  }
  notification.error({ message: parsed.message || errorFallback || 'Request failed' });
  return false;
};

// V2 PA and Flags API helpers
lib.getPAListV2 = async (params = {}) => {
  // params: { page, pageSize, status, facility_id, include_flags=true, search }
  const query = { include_flags: true, page: 1, pageSize: 20, ...params };
  return myApi('GET', 'v2/pa', query);
};

/** PA turnaround report (admin PA dashboard). params: { month: 'YYYY-MM', staff_user_id, months } */
lib.paTurnaround = async (params = {}) => {
  return myApi('GET', 'v2/admin/pa/turnaround', params);
};

/** Every PA processing batch on one encounter (code goes in the query: it contains slashes). */
lib.paProcessingHistory = async (checkinId) => {
  return myApi('GET', 'v2/admin/pa/processing-history', { checkin_id: checkinId });
};

lib.getPAByIdV2 = async (paId, params = {}) => {
  return myApi('GET', `v2/pa/${paId}`, { include_flags: true, ...params });
};

lib.getPAItemsV2 = async (paId, params = {}) => {
  return myApi('GET', `v2/pa/${paId}/items`, { include_flags: true, page: 1, pageSize: 20, ...params });
};

lib.submitPABatchDecisions = async (checkinId, payload = {}, options = {}) => {
  if (!checkinId) {
    throw new Error('checkinId is required');
  }
  const idempotencyKey = options?.idempotencyKey;
  const config = idempotencyKey
    ? {
      headers: {
        ...(setHeader()?.headers || {}),
        'Idempotency-Key': idempotencyKey,
      },
    }
    : {};
  /** Body-based route: check-in codes often contain `/`, which breaks path segments even when encoded. */
  const body = { ...payload, checkin_id: checkinId };
  return myApi('POST', 'v2/pa/decisions/batch', body, config);
};

// Encounter-level PA collaboration (queries + flags)
lib.getPAEncounterQueries = async (checkinId, params = {}) => {
  if (!checkinId) throw new Error('checkinId is required');
  const encodedId = encodeURIComponent(checkinId);
  return myApi('GET', `v2/pa/${encodedId}/queries`, params);
};

lib.createPAEncounterQuery = async (checkinId, payload = {}, options = {}) => {
  if (!checkinId) throw new Error('checkinId is required');
  const encodedId = encodeURIComponent(checkinId);
  const idempotencyKey = options?.idempotencyKey;
  const config = idempotencyKey
    ? {
      headers: {
        ...(setHeader()?.headers || {}),
        'Idempotency-Key': idempotencyKey,
      },
    }
    : {};
  return myApi('POST', `v2/pa/${encodedId}/queries`, payload, config);
};

lib.markPAEncounterQueriesRead = async (checkinId, payload = { mark_all_read: true }) => {
  if (!checkinId) throw new Error('checkinId is required');
  const encodedId = encodeURIComponent(checkinId);
  return myApi('PATCH', `v2/pa/${encodedId}/queries/read`, payload);
};

lib.getPAEncounterQueryUnreadCount = async (checkinId) => {
  if (!checkinId) throw new Error('checkinId is required');
  const encodedId = encodeURIComponent(checkinId);
  return myApi('GET', `v2/pa/${encodedId}/queries/unread-count`);
};

lib.getPAEncounterFlags = async (checkinId, params = {}) => {
  if (!checkinId) throw new Error('checkinId is required');
  const encodedId = encodeURIComponent(checkinId);
  return myApi('GET', `v2/pa/${encodedId}/flags`, params);
};

lib.createPAEncounterFlag = async (checkinId, payload = {}, options = {}) => {
  if (!checkinId) throw new Error('checkinId is required');
  const encodedId = encodeURIComponent(checkinId);
  const idempotencyKey = options?.idempotencyKey;
  const config = idempotencyKey
    ? {
      headers: {
        ...(setHeader()?.headers || {}),
        'Idempotency-Key': idempotencyKey,
      },
    }
    : {};
  return myApi('POST', `v2/pa/${encodedId}/flags`, payload, config);
};

lib.resolvePAEncounterFlag = async (checkinId, flagId, payload = {}) => {
  if (!checkinId) throw new Error('checkinId is required');
  if (!flagId) throw new Error('flagId is required');
  const encodedId = encodeURIComponent(checkinId);
  return myApi('POST', `v2/pa/${encodedId}/flags/${flagId}/resolve`, payload);
};

lib.dismissPAEncounterFlag = async (checkinId, flagId, payload = {}) => {
  if (!checkinId) throw new Error('checkinId is required');
  if (!flagId) throw new Error('flagId is required');
  const encodedId = encodeURIComponent(checkinId);
  return myApi('POST', `v2/pa/${encodedId}/flags/${flagId}/dismiss`, payload);
};

// PA Analytics (Admin)
// Summary of PA costs/requests over a time range with optional filters.
// params: { date_from, date_to, date_type, org_id, location_id, status,
//   plan_type (single plan id, backward compatible), plan_type_id (plan category),
//   plan_ids (comma-separated or repeated) }
// Response includes total_encounters_in_scope, total_encounters_with_pa, total_requests (alias).
lib.getPAAnalyticsSummary = async (params = {}) => {
  const query = { ...params };
  return myApi('GET', 'v2/admin/pa/analytics/summary', query);
};

// Time-series of PA metrics over a selected interval (day/week/month/year)
// params: same filter set as summary (date_from, date_to, interval, date_type, org_id,
//   location_id, status, plan_type, plan_type_id, plan_ids)
lib.getPAAnalyticsTimeSeries = async (params = {}) => {
  const query = { interval: 'month', ...params };
  return myApi('GET', 'v2/admin/pa/analytics/time-series', query);
};

// Breakdown of PA metrics by a given dimension (organization, plan_type, location)
// params: same filter set as summary (date_from, date_to, group_by, date_type, org_id,
//   location_id, status, plan_type, plan_type_id, plan_ids)
lib.getPAAnalyticsBreakdown = async (params = {}) => {
  const query = { group_by: 'organization', ...params };
  return myApi('GET', 'v2/admin/pa/analytics/breakdown', query);
};

// Paginated encounter list for PA analytics (same filters as summary).
// params: same filter set as summary, plus page / pageSize (or page / per_page — pass through).
// Optional: scope = 'in_scope' | 'with_pa' if backend supports narrowing the row set.
lib.getPAAnalyticsEncounters = async (params = {}) => {
  const query = { page: 1, pageSize: 20, ...params };
  return myApi('GET', 'v2/admin/pa/analytics/encounters', query);
};

// PA line drill-down (same filters as summary).
// params: kind = 'variance' | 'rejected' | 'approved' | 'pending', direction = 'increase' | 'reduction'
//   (variance only), page, pageSize, sort_by, sort_dir, search.
lib.getPAAnalyticsItems = async (params = {}) => {
  const query = { kind: 'variance', page: 1, pageSize: 20, ...params };
  return myApi('GET', 'v2/admin/pa/analytics/items', query);
};

/**
 * Download PA analytics CSV (sync blob) for the active dashboard filters.
 * params: same filter set as summary, plus:
 *   dataset: 'encounters' (default) | 'breakdown' | 'variance' | 'rejected' (PA lines)
 *   group_by: organization | plan_type | location (breakdown only)
 *   max_rows: optional cap (server max 10000)
 */
lib.downloadPAAnalyticsExport = async (params = {}) => {
  const dataset = ['breakdown', 'variance', 'rejected'].includes(params.dataset)
    ? params.dataset
    : 'encounters';
  const queryParams = { ...params, dataset };
  const maxRows = Number(params.max_rows);
  if (Number.isFinite(maxRows) && maxRows > 0) {
    queryParams.max_rows = Math.min(Math.max(1, maxRows), 10000);
  }
  const path = '/v2/admin/pa/analytics/export';
  try {
    const resp = await request.get(path, {
      ...setHeader(),
      params: queryParams,
      responseType: 'blob',
    });
    let blob = resp?.data;
    const ct = String(resp?.headers?.['content-type'] || '').toLowerCase();
    if (ct.includes('application/json') || (blob && blob.type && blob.type.includes('json'))) {
      const text = typeof blob.text === 'function' ? await blob.text() : '';
      let msg = 'Export failed';
      try {
        const j = JSON.parse(text);
        msg = j.message || j.msg || msg;
      } catch (_) {
        if (text) msg = text;
      }
      throw new Error(msg);
    }
    const fallback = `pa_analytics_${dataset}.csv`;
    const filename =
      getFilenameFromContentDisposition(resp?.headers?.['content-disposition']) || fallback;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return { success: true, filename };
  } catch (e) {
    const data = e?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      let msg = 'Export failed';
      try {
        const j = JSON.parse(text);
        msg = j.message || j.msg || msg;
      } catch (_) {
        if (text) msg = text;
      }
      throw new Error(msg);
    }
    throw new Error(e?.response?.data?.message || e?.message || 'Export failed');
  }
};

// Flags (global)
lib.createPAFlag = async (payload) => {
  // { claim_item_id, flag_type, flag_reason, metadata }
  return myApi('POST', 'v2/pa/flags', payload);
};

lib.listPAFlags = async (params = {}) => {
  return myApi('GET', 'v2/pa/flags', params);
};

lib.getPAFlagById = async (flagId) => {
  return myApi('GET', `v2/pa/flags/${flagId}`);
};

lib.updatePAFlag = async (flagId, payload) => {
  return myApi('PUT', `v2/pa/flags/${flagId}`, payload);
};

lib.resolvePAFlag = async (flagId, payload = {}) => {
  return myApi('POST', `v2/pa/flags/${flagId}/resolve`, payload);
};

lib.dismissPAFlag = async (flagId, payload = {}) => {
  return myApi('POST', `v2/pa/flags/${flagId}/dismiss`, payload);
};

lib.deletePAFlag = async (flagId) => {
  return myApi('DELETE', `v2/pa/flags/${flagId}`);
};

// Flags (per PA)
lib.getFlagsForPA = async (paId, params = {}) => {
  return myApi('GET', `v2/pa/${paId}/flags`, { flag_status: 'active', ...params });
};

lib.flagEntirePA = async (paId, payload) => {
  // { flag_type, flag_reason, metadata }
  return myApi('POST', `v2/pa/${paId}/flags`, payload);
};

// Checkout PA validation status for a checkin
// GET /v2/pa/{checkin_id}/checkout-validation
lib.getCheckoutValidation = async (checkinId) => {
  if (!checkinId) {
    throw new Error('checkinId is required');
  }
  const encodedId = encodeURIComponent(checkinId);
  return myApi('GET', `v2/pa/${encodedId}/checkout-validation`);
};

// Universal Flags (V2)
// Create a universal flag for any target
lib.createFlag = async (payload = {}) => {
  // { target_type, target_id, severity, reason_code, message, context }
  return myApi('POST', 'v2/flags', payload);
};

// List flags by target_type and target_ids (comma-separated or array)
lib.listFlags = async (params = {}) => {
  // { target_type, target_ids, status }
  const q = { ...params };
  if (Array.isArray(q.target_ids)) q.target_ids = q.target_ids.join(',');
  return myApi('GET', 'v2/flags', q);
};

// Resolve a universal flag
lib.resolveFlag = async (flagId, payload = {}) => {
  return myApi('PATCH', `v2/flags/${flagId}/resolve`, payload);
};

// Bulk create flags
lib.bulkCreateFlags = async (payload = []) => {
  // [ { target_type, target_id, ... }, ... ]
  return myApi('POST', 'v2/flags/bulk', payload);
};

/** GET /v2/checkins/ — facility-scoped encounter list (admin passes facility_id).
 *  Trailing slash matches the Helix collection GET; /v2/checkins (no slash) 405s
 *  unless the API also registers that alias. */
lib.getCheckinsV2 = async (params = {}) => myApi('GET', 'v2/checkins/', params);

/** GET /v2/admin/checkins — live (deleted=0) or soft-deleted list. Caller sets deleted. */
lib.getAdminCheckins = async (params = {}) =>
  myApi('GET', 'v2/admin/checkins', { deleted: 0, ...params });

// Admin Checkout Validation API helpers
// (lib.getCheckoutValidation is defined above, with the checkin id
//  percent-encoded — check-in codes contain "/". A second, unencoded
//  definition used to live here and silently shadowed it.)

// Admin Soft-Deleted Checkins Management
lib.getSoftDeletedCheckins = async (params = {}) => {
  // Get list of soft-deleted checkins
  // params: { page, pageSize, facility_id, date_from, date_to, delete_reason }
  // Force deleted=1 so live-list params cannot accidentally open cleanup data.
  return myApi('GET', 'v2/admin/checkins', { ...params, deleted: 1 });
};

lib.restoreCheckin = async (checkinId, mode = 'checked_out') => {
  // Restore a soft-deleted checkin. checked_out (default): back on the
  // Encounters page as checked out, awaiting claim submission.
  return myApi('POST', `v2/admin/checkins/${checkinId}/restore`, { mode });
};

lib.getSoftDeleteStats = async (params = {}) => {
  // Get soft delete statistics
  // params: { date_from, date_to, facility_id }
  return myApi('GET', 'v2/admin/checkins/soft-delete-stats', params);
};

// Super-admin check-in cleanup (admin.* only)
lib.previewCheckinCleanup = async (payload = {}) => {
  return myApi('POST', 'v2/admin/checkins/cleanup/preview', payload);
};

lib.archiveCheckinCleanup = async (payload = {}) => {
  return myApi('POST', 'v2/admin/checkins/cleanup/archive', payload);
};

lib.deleteCheckinCleanup = async (payload = {}) => {
  return myApi('POST', 'v2/admin/checkins/cleanup/delete', payload);
};

/** Reopen the check-in (undo soft-delete, status back to open). Body: { code, reason } */
lib.resetCheckinStatusCleanup = async (payload = {}) => {
  return myApi('POST', 'v2/admin/checkins/cleanup/reset-status', payload);
};

/** Reset selected PA/claim line items back to Pending. Body: { code, item_ids, reason } */
lib.resetCheckinItemsStatus = async (payload = {}) => {
  return myApi('POST', 'v2/admin/checkins/cleanup/items/reset-status', payload);
};

/** POST /v2/admin/checkins/{code}/change-type — admin-only type correction */
lib.changeCheckinType = async (checkinCode, payload = {}) => {
  if (!checkinCode) throw new Error('checkinCode is required');
  return myApi('POST', `v2/admin/checkins/${encodeURIComponent(checkinCode)}/change-type`, payload);
};

// Platform operator pre-assigned act-as contexts (platform.* only)
lib.listPlatformOperators = async () => {
  return myApi('GET', 'v2/auth/platform/operators');
};

lib.getPlatformOperatorContexts = async (params = {}) => {
  return myApi('GET', 'v2/auth/platform/contexts', params);
};

lib.setPlatformOperatorContext = async (payload = {}) => {
  return myApi('PUT', 'v2/auth/platform/contexts', payload);
};

lib.clearPlatformOperatorContext = async (payload = {}) => {
  return myApi('DELETE', 'v2/auth/platform/contexts', payload);
};

// policy_no / insurance_no generation settings (platform.* only — not even admin.*)
lib.getIdentityGenerationSettings = async () => {
  return myApi('GET', 'v2/admin/identity-generation-settings');
};

lib.updateIdentityGenerationSettings = async (payload = {}) => {
  return myApi('PUT', 'v2/admin/identity-generation-settings', payload);
};

lib.previewIdentityGenerationSettings = async (payload = {}) => {
  return myApi('POST', 'v2/admin/identity-generation-settings/preview', payload);
};

// Ops email recipient lists (admin.* | admin.system_config).
// Source mode AMAN_OPS_EMAIL_SOURCE: env | db | both (DB override → env → default both).
// Lists: env only / db only / env ∪ DB merge.
lib.getOpsEmailRecipients = async () => {
  return myApi('GET', 'v2/admin/ops-email-recipients');
};

lib.updateOpsEmailRecipients = async (payload = {}) => {
  return myApi('PUT', 'v2/admin/ops-email-recipients', payload);
};

// Active SMS provider override (admin.* | admin.system_config).
// active_provider: 'termii' | 'trustmail' | 'twilio' | 'nexmo' | null (no override — built-in routing).
lib.getSmsProviderSettings = async () => {
  return myApi('GET', 'v2/admin/sms-provider-settings');
};

lib.updateSmsProviderSettings = async (payload = {}) => {
  return myApi('PUT', 'v2/admin/sms-provider-settings', payload);
};

// --- Encounter/claims workflow overhaul (CLAIMS-ROLLOUT-SWITCH) ---------------
// Runtime switches + pilot allowlist (admin.* | admin.system_config).
lib.getClaimsRollout = async () => {
  return myApi('GET', 'v2/admin/claims-rollout');
};

// Send only the keys being changed — the API leaves the rest untouched.
lib.updateClaimsRollout = async (payload = {}) => {
  return myApi('PUT', 'v2/admin/claims-rollout', payload);
};

/*
 * Test subjects (TEST-SUBJECTS): records whose activity is excluded from every
 * operational and cost calculation while they keep working normally.
 */
lib.getTestSubjects = async (params = {}) => myApi('GET', 'v2/admin/test-subjects', params);
lib.markTestSubject = async ({ type, key, reason }) => myApi('POST', 'v2/admin/test-subjects', { type, key, reason });
lib.unmarkTestSubject = async (id, reason) => myApi('POST', `v2/admin/test-subjects/${encodeURIComponent(id)}/unmark`, { reason });
lib.getTestSubjectSettings = async () => myApi('GET', 'v2/admin/test-subjects/settings');
lib.updateTestSubjectSettings = async ({ exclusion_enabled, reason }) =>
  myApi('PUT', 'v2/admin/test-subjects/settings', { exclusion_enabled, reason });
lib.searchTestSubjectCandidates = async (type, q) => myApi('GET', 'v2/admin/test-subjects/search', { type, q });
lib.getTestSubjectImpact = async (type, key) => myApi('GET', 'v2/admin/test-subjects/impact', { type, key });
lib.getTestSubjectSuggestions = async () => myApi('GET', 'v2/admin/test-subjects/suggestions');
lib.dismissTestSubjectSuggestion = async (type, key) => myApi('POST', 'v2/admin/test-subjects/suggestions/dismiss', { type, key });
lib.migrateLegacyTestSubjects = async (apply = false) => myApi('POST', 'v2/admin/test-subjects/migrate-legacy', { apply });
/** Is this encounter a test subject's? Silent: a tag must never break the page it sits on. */
lib.getEncounterTestStatus = async (code) => {
  try {
    const resp = await myApi('GET', 'v2/admin/test-subjects/encounter', { code });
    return resp?.data?.data || { is_test: false, via: [] };
  } catch (_) {
    return { is_test: false, via: [] };
  }
};

// Submit Claim (spec §7), separate from checkout. Same endpoint the facility
// portal's new flow uses; gated per facility on submit_separation_enabled.
lib.submitClaimV2 = async (checkinId, payload = {}) => {
  return myApi('POST', `v2/claims/${encodeURIComponent(checkinId)}/submit`, payload);
};

// Submit on behalf of facilities (spec §7, §8): { checkin_ids } for a selection,
// or { facility_id, from?, to?, type?, previous_months_only? } for Submit All.
lib.submitPendingClaims = async (payload = {}) => {
  return myApi('POST', 'v2/admin/claims/submit-pending', payload);
};

// Pending Submission page (spec §8).
lib.getPendingSubmissions = async (params = {}) => {
  return myApi('GET', 'v2/admin/claims/pending-submissions', params);
};

// Archive a facility user (spec §6). Permanent; soft — history is kept.
lib.archiveFacilityUser = async (facilityId, userId, reason = '') => {
  return myApi('POST', `v2/admin/facilities/${facilityId}/users/${userId}/archive`, reason ? { reason } : {});
};


// Revert a submitted claim back to an open encounter — admin.* only, and only
// while the claim is Pending CO (not sent to HM, not paid). GET says whether it
// can be reverted and, if not, why.
lib.getClaimRevertStatus = async (checkinId) =>
  myApi('GET', `v2/admin/claims/${encodeURIComponent(checkinId)}/revert`, {});

lib.revertClaim = async (checkinId, reason) =>
  myApi('POST', `v2/admin/claims/${encodeURIComponent(checkinId)}/revert`, { reason });

// Facility ratings moderation list (admin.* | facility_ratings.manage_all).
lib.getFacilityRatings = async (params = {}) => {
  return myApi('GET', 'v2/admin/facility-ratings', params);
};

// Admin activity log browser (admin.* / platform.*) — richer filtering than
// the legacy ?logs/?log endpoints (target table/id, date range, free text).
lib.getAdminLogs = async (params = {}) => {
  return myApi('GET', 'v2/admin/logs', params);
};

lib.getAdminLogsFacets = async () => {
  return myApi('GET', 'v2/admin/logs/facets');
};

lib.getAdminLogEntry = async (id) => {
  return myApi('GET', `v2/admin/logs/${encodeURIComponent(id)}`);
};

// Aggregates for badges on list pages
lib.getFlagAggregates = async (params = {}) => {
  // { target_type, target_ids }
  const q = { ...params };
  if (Array.isArray(q.target_ids)) q.target_ids = q.target_ids.join(',');
  return myApi('GET', 'v2/flags/aggregates', q);
};

// Admin Appointments API (V2)
// List appointments with filters
lib.getAdminAppointments = async (params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 20,
    ...params,
  };
  return myApi('GET', 'v2/admin/appointments/', queryParams);
};

// Get appointment details
lib.getAdminAppointmentDetails = async (appointmentId) => {
  if (!appointmentId) {
    throw new Error('appointmentId is required');
  }
  const encodedId = encodeURIComponent(appointmentId);
  return myApi('GET', `v2/admin/appointments/${encodedId}`);
};

// Confirm appointment (CS/Admin)
lib.confirmAdminAppointment = async (appointmentId, payload = {}) => {
  if (!appointmentId) {
    throw new Error('appointmentId is required');
  }
  const encodedId = encodeURIComponent(appointmentId);
  return myApi('POST', `v2/admin/appointments/${encodedId}/confirm`, payload);
};

// Convert appointment to check-in
lib.convertAppointmentToCheckin = async (appointmentId, checkinType) => {
  if (!appointmentId) {
    throw new Error('appointmentId is required');
  }
  if (!checkinType || (checkinType !== 1 && checkinType !== 2)) {
    throw new Error('checkinType must be 1 or 2');
  }
  const encodedId = encodeURIComponent(appointmentId);
  return myApi('POST', `v2/admin/appointments/${encodedId}/checkin`, { checkinType });
};

// Update client email (CS/Admin)
lib.updateClientEmail = async (policyNo, email) => {
  if (!policyNo) throw new Error('policyNo is required');
  if (!email) throw new Error('email is required');
  const encodedPolicyNo = encodeURIComponent(policyNo);
  return myApi('PATCH', `v2/care/clients/${encodedPolicyNo}/email`, { email });
};

lib.updateClientPhone = async (policyNo, phone) => {
  if (!policyNo) throw new Error('policyNo is required');
  if (!phone) throw new Error('phone is required');
  const encodedPolicyNo = encodeURIComponent(policyNo);
  return myApi('PATCH', `v2/care/clients/${encodedPolicyNo}/phone`, { phone });
};

// Tariff categories (for upload form – facility and admin)
lib.getTariffCategories = async () => {
  return myApi('GET', 'v2/tariffs/categories');
};

// --- Facility tariff APIs (state, active, adopt standard, set active) ---
// GET /v2/facilities/{facility_id}/tariffs/state — empty state / dashboard
lib.getFacilityTariffState = async (facilityId) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('GET', `v2/facilities/${encodedId}/tariffs/state`);
};

// GET /v2/facilities/{facility_id}/tariffs/active — current active tariff (source: standard | specific)
lib.getFacilityActiveTariff = async (facilityId) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('GET', `v2/facilities/${encodedId}/tariffs/active`);
};

// PUT /v2/facilities/{facility_id}/tariffs/active — switch active tariff
// body: { source: "standard" } or { source: "specific", facility_tariff_id: number }, optional: effective_date, created_by
lib.setFacilityActiveTariff = async (facilityId, body) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('PUT', `v2/facilities/${encodedId}/tariffs/active`, body);
};

// POST /v2/facilities/{facility_id}/tariffs/adopt-standard — adopt HMO standard tariff
// body: use_live_standard (default true), effective_date, tariff_name, expiry_date
lib.adoptFacilityStandardTariff = async (facilityId, body = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('POST', `v2/facilities/${encodedId}/tariffs/adopt-standard`, body);
};

// POST /v2/facilities/{facility_id}/tariffs/{tariff_id}/items — add single item to facility tariff (draft only).
// body: item_name (required), category_id, item_brand, item_type, item_dosage, item_strength, item_presentation,
//       cost|facility_cost|unit_cost, hmo_cost, standardized_item_id. Returns data.facility_tariff_item_id.
lib.addFacilityTariffItem = async (facilityId, tariffId, body = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  if (!tariffId) throw new Error('tariffId is required');
  const encFac = encodeURIComponent(facilityId);
  const encTar = encodeURIComponent(tariffId);
  return myApi('POST', `v2/facilities/${encFac}/tariffs/${encTar}/items`, body);
};

// GET /v2/tariffs/standard — Current standard tariff (any authenticated user). Params: category_id (optional), q (optional search). Response: data.tariff, data.items (max 200, not paginated).
lib.getStandardTariff = async (params = {}) => {
  return myApi('GET', 'v2/tariffs/standard', params);
};

// GET /v2/admin/tariffs/standard — Admin view: current standard tariff (full row + scope) and items_count. No items array.
lib.getAdminStandardTariff = async () => {
  return myApi('GET', 'v2/admin/tariffs/standard');
};

// GET /v2/admin/tariffs/standards — All active standard tariffs (generic + scoped). Response: data.tariffs, data.total.
// Optional params: scope_state, scope_zone_id, scope_facility_type, scope_plan_id, scope_location_id, effective_date (Y-m-d), category_id, q|search.
// Each tariff may include category_id (int|null), category_name (string|null) when all items are in one category.
lib.getAdminStandardTariffs = async (params = {}) => {
  return myApi('GET', 'v2/admin/tariffs/standards', params);
};

// GET /v2/admin/tariffs/standard/versions — List all standard tariff versions (for adopt dropdown, history). Optional: active_only=1.
// Response: data.tariffs (array), data.total. Permission: tariffs.read_all or admin.*
lib.getAdminStandardTariffVersions = async (params = {}) => {
  return myApi('GET', 'v2/admin/tariffs/standard/versions', params);
};

// GET /v2/admin/tariffs/standard/scope-options — Options for scope dropdowns (states from zones, zones, facility_types, plans, locations).
// Response: { error: 0, data: { states: [{ value, label }], zones: [{ id, value, label }], facility_types: [...], plans: [...], locations: [...] } }
lib.getStandardTariffScopeOptions = async () => {
  return myApi('GET', 'v2/admin/tariffs/standard/scope-options');
};

// Upload or create HMO standard tariff (admin). Multipart: file, tariff_name, effective_date, expiry_date, optional upload_category_id, optional scope_*.
lib.uploadStandardTariff = async (formData) => {
  if (!formData || !(formData instanceof FormData)) throw new Error('formData is required');
  return request.post('v2/admin/tariffs/standard/upload', formData, { ...setHeader() });
};

// Update standard tariff metadata or scope (admin). Permission: tariffs.standard.manage
lib.updateStandardTariff = async (tariffId, body = {}) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('PUT', `v2/admin/tariffs/standard/${encodedId}`, body);
};

// POST /v2/admin/tariffs/standard/{tariff_id}/items — add single item to standard (HMO) tariff (admin).
// body: item_name (required), category_id, item_brand, item_type, item_dosage, item_strength, item_presentation,
//       cost|unit_cost, facility_cost, hmo_cost. Returns data.tariff_item_id.
lib.addStandardTariffItem = async (tariffId, body = {}) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('POST', `v2/admin/tariffs/standard/${encodedId}/items`, body);
};

// PUT /v2/admin/tariffs/standard/{tariff_id}/items/{item_id} — update price/metadata on a standard tariff item (global).
lib.updateStandardTariffItem = async (tariffId, itemId, body = {}) => {
  if (!tariffId) throw new Error('tariffId is required');
  if (!itemId) throw new Error('itemId is required');
  const encTariff = encodeURIComponent(tariffId);
  const encItem = encodeURIComponent(itemId);
  return myApi('PUT', `v2/admin/tariffs/standard/${encTariff}/items/${encItem}`, body);
};

// POST /v2/admin/tariffs/standard/{tariff_id}/items/{item_id}/archive — soft-archive standard item globally.
lib.archiveStandardTariffItem = async (tariffId, itemId) => {
  if (!tariffId) throw new Error('tariffId is required');
  if (!itemId) throw new Error('itemId is required');
  const encTariff = encodeURIComponent(tariffId);
  const encItem = encodeURIComponent(itemId);
  return myApi('POST', `v2/admin/tariffs/standard/${encTariff}/items/${encItem}/archive`);
};

// POST /v2/admin/tariffs/standard/{tariff_id}/items/{item_id}/unarchive — restore archived standard item.
lib.unarchiveStandardTariffItem = async (tariffId, itemId) => {
  if (!tariffId) throw new Error('tariffId is required');
  if (!itemId) throw new Error('itemId is required');
  const encTariff = encodeURIComponent(tariffId);
  const encItem = encodeURIComponent(itemId);
  return myApi('POST', `v2/admin/tariffs/standard/${encTariff}/items/${encItem}/unarchive`);
};

// POST /v2/admin/tariffs/{tariff_id}/items/{item_id}/archive — facility-local soft-archive (any status).
// Does not change the HMO standard tariff, including for standard-sourced / linked lines.
lib.archiveAdminFacilityTariffItem = async (tariffId, itemId) => {
  if (!tariffId) throw new Error('tariffId is required');
  if (!itemId) throw new Error('itemId is required');
  const encTariff = encodeURIComponent(tariffId);
  const encItem = encodeURIComponent(itemId);
  return myApi('POST', `v2/admin/tariffs/${encTariff}/items/${encItem}/archive`);
};

// Archive/unarchive a WHOLE facility tariff (not a single item) — backend:
// TariffService::archiveTariff. Blocks with 409 + code TARIFF_ARCHIVE_BLOCKED when
// the tariff is the currently-resolved active tariff for a facility or branch;
// re-send with force: true to archive anyway.
lib.archiveAdminTariff = async (tariffId, { reason = '', force = false } = {}) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encTariff = encodeURIComponent(tariffId);
  return myApi('POST', `v2/admin/tariffs/${encTariff}/archive`, { reason, force: Boolean(force) });
};

lib.unarchiveAdminTariff = async (tariffId) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encTariff = encodeURIComponent(tariffId);
  return myApi('POST', `v2/admin/tariffs/${encTariff}/unarchive`);
};

/** True when a tariff archive attempt was blocked because it's still in active use. */
lib.isTariffArchiveBlocked = (resp) =>
  resp?.data?.data?.code === 'TARIFF_ARCHIVE_BLOCKED' || resp?.status === 409;

// POST /v2/facilities/{facility_id}/items/price – pricing lookup (validate during review, compare tariff vs legacy)
lib.getFacilityItemPrice = async (facilityId, body = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('POST', `v2/facilities/${encodedId}/items/price`, body);
};

// Tariff template download (CSV/TSV) – shared by facility and admin
// format: 'csv' | 'tsv'; type: 'minimal' | 'advanced' (optional)
lib.downloadTariffTemplate = async (format = 'csv', type = 'minimal') => {
  const path = 'v2/tariffs/template';
  const params = { format };
  if (type) params.type = type;
  const resp = await request.get(path, { ...setHeader(), params, responseType: 'blob' });
  const blob = resp?.data;
  if (!blob) throw new Error('No template data');
  const ext = format === 'tsv' ? 'tsv' : 'csv';
  const name = type === 'advanced' ? `tariff_advanced_template.${ext}` : `tariff_minimal_template.${ext}`;
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
  return { success: true };
};

// Admin upload: upload tariff on behalf of a facility (multipart/form-data).
// Uses request.post so caller gets full response (including validation errors in data.errors).
lib.uploadTariffForFacility = async (facilityId, formData) => {
  if (!facilityId) throw new Error('facilityId is required');
  if (!formData || !(formData instanceof FormData)) throw new Error('formData is required');
  const encodedId = encodeURIComponent(facilityId);
  const path = `v2/admin/facilities/${encodedId}/tariffs`;
  return request.post(path, formData, { ...setHeader() });
};

// Admin Tariffs V2 API helpers
// List tariffs (admin queue)
lib.getAdminTariffs = async (params = {}) => {
  // Backend supports limit/offset; keep defaults small and override via params
  const queryParams = {
    limit: 50,
    offset: 0,
    ...params,
  };
  return myApi('GET', 'v2/admin/tariffs/', queryParams);
};

// List tariffs for a specific facility (facility-first alias). Single source of truth when admin is in facility context.
// Contract: same as GET /v2/admin/tariffs?facility_id={id}. Response: data.tariffs, data.total, data.limit, data.offset.
// Query: status (optional), limit (default 20, max 100), offset (default 0).
lib.getAdminFacilityTariffs = async (facilityId, params = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  const queryParams = { limit: 20, offset: 0, ...params };
  if (queryParams.limit > 100) queryParams.limit = 100;
  return myApi('GET', `v2/admin/facilities/${encodedId}/tariffs`, queryParams);
};

// GET /v2/admin/facilities/{id}/tariffs/active — admin get facility active tariff(s).
// params: per_category=1 for full matrix (data.active_by_category); category_id for single category.
lib.getAdminFacilityActiveTariff = async (facilityId, params = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('GET', `v2/admin/facilities/${encodedId}/tariffs/active`, params);
};

// GET /v2/admin/facilities/{facility_id}/tariff-items — tariff-aware item search for a facility (admin PA/pricing tools).
// Query: category_id (optional), q|search (optional), limit (optional).
// Response (per guide): legacy item list shape { id, item_name, item_code, item_brand, item_type, item_strength, item_presentation },
// backed by the facility's active tariff (with fallback to costable items when no tariff match).
lib.getAdminFacilityTariffItems = async (facilityId, params = {}) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  const queryParams = {
    limit: 50,
    ...params,
  };
  return myApi('GET', `v2/admin/facilities/${encodedId}/tariff-items`, queryParams);
};

// PUT /v2/admin/facilities/{id}/tariffs/active — admin set facility active tariff.
// body: { source: "standard" } or { source: "specific", facility_tariff_id }, optional category_id (omit = default).
lib.setAdminFacilityActiveTariff = async (facilityId, body) => {
  if (!facilityId) throw new Error('facilityId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('PUT', `v2/admin/facilities/${encodedId}/tariffs/active`, body);
};

// DELETE /v2/admin/facilities/{id}/tariffs/active?category_id=N — remove the tariff assignment
// for one request category (it stops being offered for encounters because of it). Linked branches follow.
lib.removeAdminFacilityCategoryTariff = async (facilityId, categoryId) => {
  if (!facilityId) throw new Error('facilityId is required');
  if (categoryId == null) throw new Error('categoryId is required');
  const encodedId = encodeURIComponent(facilityId);
  return myApi('DELETE', `v2/admin/facilities/${encodedId}/tariffs/active?category_id=${encodeURIComponent(categoryId)}`);
};

// GET /v2/admin/tariffs/categories — categories for admin (matrix, upload forms).
lib.getAdminTariffCategories = async () => {
  return myApi('GET', 'v2/admin/tariffs/categories');
};

// Get tariff detail by ID (admin)
lib.getAdminTariffDetail = async (tariffId, params = {}) => {
  if (!tariffId) {
    throw new Error('tariffId is required');
  }
  const encodedId = encodeURIComponent(tariffId);
  return myApi('GET', `v2/admin/tariffs/${encodedId}`, params);
};

// List items for a specific tariff (admin items endpoint, paginated)
lib.getAdminTariffItems = async (tariffId, params = {}) => {
  if (!tariffId) {
    throw new Error('tariffId is required');
  }
  const encodedId = encodeURIComponent(tariffId);
  const queryParams = {
    page: 1,
    page_size: 50,
    ...params,
  };
  return myApi('GET', `v2/admin/tariffs/${encodedId}/items`, queryParams);
};

// POST /v2/admin/tariffs/{tariff_id}/items — add single item to facility tariff (admin, any status).
// tariff_id = facility_tariff_id. Body: item_name (required), category_id, item_brand, item_type, item_dosage,
// item_strength, item_presentation, cost|facility_cost|unit_cost, hmo_cost, standardized_item_id.
// Returns data.facility_tariff_item_id.
lib.addAdminTariffItem = async (tariffId, body = {}) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('POST', `v2/admin/tariffs/${encodedId}/items`, body);
};

// Tariff-level workflow actions
lib.setTariffUnderReview = async (tariffId) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('POST', `v2/admin/tariffs/${encodedId}/under-review`);
};

// Get which price fields can be adopted when approving the whole tariff
lib.getTariffApprovalOptions = async (tariffId) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('GET', `v2/admin/tariffs/${encodedId}/approval-options`);
};

lib.approveTariff = async (tariffId, body = {}) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('POST', `v2/admin/tariffs/${encodedId}/approve`, body);
};

lib.rejectTariff = async (tariffId, reason) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  const body = { reason };
  return myApi('POST', `v2/admin/tariffs/${encodedId}/reject`, body);
};

lib.requestTariffRevision = async (tariffId, payload = {}) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('POST', `v2/admin/tariffs/${encodedId}/request-revision`, payload);
};

lib.counterProposeTariff = async (tariffId, payload = {}) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('POST', `v2/admin/tariffs/${encodedId}/counter-propose`, payload);
};

// Counter-propose by percentage (bulk); section 8.6
lib.counterProposeTariffPercentage = async (tariffId, payload = {}) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('POST', `v2/admin/tariffs/${encodedId}/counter-propose/percentage`, payload);
};

lib.autoMapTariffItems = async (tariffId) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('POST', `v2/admin/tariffs/${encodedId}/auto-map`);
};

// Tariff items actions
lib.bulkApproveTariffItems = async (tariffId, itemIds = []) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  const body = { item_ids: itemIds };
  return myApi('POST', `v2/admin/tariffs/${encodedId}/items/bulk-approve`, body);
};

lib.approveTariffItem = async (itemId) => {
  if (!itemId) throw new Error('itemId is required');
  const encodedId = encodeURIComponent(itemId);
  return myApi('POST', `v2/admin/tariffs/items/${encodedId}/approve`);
};

lib.proposeTariffItemCost = async (itemId, payload) => {
  if (!itemId) throw new Error('itemId is required');
  const encodedId = encodeURIComponent(itemId);
  return myApi('POST', `v2/admin/tariffs/items/${encodedId}/propose`, payload);
};

// Tariff messages & history
lib.getTariffMessages = async (tariffId, params = {}) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  const queryParams = { limit: 50, ...params };
  return myApi('GET', `v2/admin/tariffs/${encodedId}/messages`, queryParams);
};

lib.sendTariffMessage = async (tariffId, payload) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('POST', `v2/admin/tariffs/${encodedId}/messages`, payload);
};

// Unread count for tariff messages
lib.getTariffMessagesUnreadCount = async (tariffId) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  return myApi('GET', `v2/admin/tariffs/${encodedId}/messages/unread-count`);
};

// Mark a single tariff message as read
lib.markTariffMessageRead = async (messageId) => {
  if (!messageId) throw new Error('messageId is required');
  const encodedId = encodeURIComponent(messageId);
  return myApi('POST', `v2/admin/tariffs/messages/${encodedId}/read`);
};

lib.getTariffHistory = async (tariffId, params = {}) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  const queryParams = { limit: 50, offset: 0, ...params };
  return myApi('GET', `v2/admin/tariffs/${encodedId}/history`, queryParams);
};

// Download tariff as CSV (blob)
lib.downloadTariffCsv = async (tariffId, facilityId) => {
  if (!tariffId) throw new Error('tariffId is required');
  const encodedId = encodeURIComponent(tariffId);
  const path = `v2/admin/tariffs/${encodedId}/download`;
  const resp = await request.get(`/${path}`, { ...setHeader(), responseType: 'blob' });
  const blob = resp?.data;
  const filename = `facility-${facilityId != null ? facilityId : 'unknown'}-tariff-${tariffId}.csv`;
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
  return { success: true, filename };
};

// Universal Flag Types (and PA alias)
lib.getFlagTypes = async () => {
  return myApi('GET', 'v2/flags/types');
};

lib.getPAFlagTypes = async () => {
  return myApi('GET', 'v2/pa/flags/types');
};

// ---------------------------------------------------------------------------
// SaaSPro PA agent (admin integration health + per-encounter advisory overlay)
// See docs: SaaSPro PA agent — integration guide for UI engineers.
// All endpoints require Bearer auth; admin endpoints require RBAC `admin.*`.
// ---------------------------------------------------------------------------

/**
 * @typedef {'approve' | 'partial_approve' | 'query' | 'reject'} SaaSProRecommendation
 * @typedef {'advisory' | 'applied' | 'superseded' | 'rejected_by_human'} SaaSProDecisionStatus
 * @typedef {'pending' | 'sent' | 'failed' | 'shadow'} SaaSProOutboxStatus
 *
 * @typedef {Object} SaaSProAgentDecision
 * @property {number} [id]
 * @property {string|null} [event_id]
 * @property {string} correlation_id
 * @property {string} checkin_id
 * @property {number|null} claim_item_id
 * @property {number|null} submission_revision
 * @property {SaaSProRecommendation|string} recommendation
 * @property {number|null} recommended_approved_cost
 * @property {number|null} confidence
 * @property {string|null} rationale
 * @property {string[]|null} policy_citations
 * @property {SaaSProRecommendation|string|null} [overall_recommendation]
 * @property {string|null} [overall_rationale]
 * @property {SaaSProDecisionStatus} status
 * @property {string} decided_at
 * @property {string} received_at
 *
 * @typedef {Object} SaaSProOutboxRow
 * @property {number} id
 * @property {string} event_id
 * @property {string} event_type
 * @property {string} checkin_id
 * @property {number} submission_revision
 * @property {SaaSProOutboxStatus} status
 * @property {number} attempts
 * @property {number|null} http_status
 * @property {string|null} last_error
 * @property {string|null} sent_at
 * @property {string} created_at
 * @property {string} updated_at
 *
 * @typedef {SaaSProOutboxRow & {
 *   payload: Record<string, unknown> | null,
 *   payload_size_bytes: number,
 *   signature_preview: string | null,
 * }} SaaSProOutboxDetail
 */

/** Canonical PA Agent admin path for a partner. */
lib.paAgentModulePath = (partnerId, resource = '') => {
  if (partnerId == null || partnerId === '') throw new Error('partnerId is required');
  const base = `v2/admin/integrations/partners/${encodeURIComponent(partnerId)}/modules/pa-agent`;
  if (!resource) return base;
  return `${base}/${String(resource).replace(/^\//, '')}`;
};

/** Admin: unified outbound + inbound PA Agent activity search. */
lib.searchPaAgentActivity = async (partnerId, params = {}) => {
  const query = { page: 1, pageSize: 20, ...params };
  if (query.status === 'all') delete query.status;
  if (query.stream === 'all') delete query.stream;
  if (query.recommendation === 'all') delete query.recommendation;
  if (query.event_type === 'all') delete query.event_type;
  return myApi('GET', lib.paAgentModulePath(partnerId, 'activity'), query);
};

/** Admin: paginated outbox queue list (partner-scoped). */
lib.getPaAgentOutbox = async (partnerId, params = {}) => {
  const query = { page: 1, pageSize: 20, ...params };
  if (query.status === 'all') delete query.status;
  if (query.event_type === 'all') delete query.event_type;
  return myApi('GET', lib.paAgentModulePath(partnerId, 'outbox'), query);
};

/** Admin: outbox row detail. */
lib.getPaAgentOutboxDetail = async (partnerId, eventId) => {
  if (!eventId) throw new Error('eventId is required');
  return myApi('GET', lib.paAgentModulePath(partnerId, `outbox/${encodeURIComponent(eventId)}`), {});
};

/** Admin: force outbox retry. */
lib.redispatchPaAgentEvent = async (partnerId, eventId) => {
  if (!eventId) throw new Error('eventId is required');
  return myApi('POST', lib.paAgentModulePath(partnerId, `outbox/${encodeURIComponent(eventId)}/redispatch`), {});
};

/** Admin: dry-run payload (no enqueue). */
lib.previewPaAgentPayload = async (partnerId, checkinId) => {
  if (!checkinId) throw new Error('checkinId is required');
  return myApi('GET', lib.paAgentModulePath(partnerId, 'preview'), { checkin_id: checkinId });
};

/** Admin: paginated inbound decisions (partner module). */
lib.getPaAgentModuleDecisions = async (partnerId, params = {}) => {
  const query = { page: 1, pageSize: 20, ...params };
  if (query.status === 'all') delete query.status;
  if (query.recommendation === 'all') delete query.recommendation;
  return myApi('GET', lib.paAgentModulePath(partnerId, 'decisions'), query);
};

/** Admin: decision detail. */
lib.getPaAgentDecisionDetail = async (partnerId, id) => {
  if (id == null) throw new Error('decision id is required');
  return myApi('GET', lib.paAgentModulePath(partnerId, `decisions/${encodeURIComponent(id)}`), {});
};

/** Admin (dev/staging): simulate inbound decision. */
lib.simulatePaAgentDecision = async (partnerId, payload = {}) => {
  return myApi('POST', lib.paAgentModulePath(partnerId, 'decisions/simulate'), payload);
};

/** @deprecated Use getPaAgentOutbox(partnerId, params) */
lib.getSaaSProOutbox = (partnerId, params) => lib.getPaAgentOutbox(partnerId, params);
/** @deprecated Use getPaAgentOutboxDetail(partnerId, eventId) */
lib.getSaaSProOutboxDetail = (partnerId, eventId) => lib.getPaAgentOutboxDetail(partnerId, eventId);
/** @deprecated Use redispatchPaAgentEvent(partnerId, eventId) */
lib.redispatchSaaSProEvent = (partnerId, eventId) => lib.redispatchPaAgentEvent(partnerId, eventId);
/** @deprecated Use previewPaAgentPayload(partnerId, checkinId) */
lib.previewSaaSProPayload = (partnerId, checkinId) => lib.previewPaAgentPayload(partnerId, checkinId);
/** @deprecated Use getPaAgentModuleDecisions(partnerId, params) */
lib.getSaaSProDecisions = (partnerId, params) => lib.getPaAgentModuleDecisions(partnerId, params);
/** @deprecated Use getPaAgentDecisionDetail(partnerId, id) */
lib.getSaaSProDecisionDetail = (partnerId, id) => lib.getPaAgentDecisionDetail(partnerId, id);
/** @deprecated Use simulatePaAgentDecision(partnerId, payload) */
lib.simulateSaaSProDecision = (partnerId, payload) => lib.simulatePaAgentDecision(partnerId, payload);

/** Facility + admin: per-encounter PA advisories (`GET /v2/pa/agent/decisions`). */
lib.getEncounterPaAgentDecisions = async (checkinId, params = {}) => {
  if (!checkinId) throw new Error('checkinId is required');
  return myApi('GET', 'v2/pa/agent/decisions', {
    checkin_id: checkinId,
    ...params,
  });
};

/** @deprecated Use getEncounterPaAgentDecisions */
lib.getPaAgentDecisions = (checkinId, params) => lib.getEncounterPaAgentDecisions(checkinId, params);

/** @typedef {'active'|'disabled'} IntegrationPartnerStatus */

/** Admin: list integration partners (`data.partners[]`). */
lib.listIntegrationPartners = async (params = {}) =>
  myApi('GET', 'v2/admin/integrations/partners/', params);

/** Admin: partner detail by slug (legacy redirect). */
lib.getIntegrationPartnerBySlug = async (slug) => {
  if (!slug) throw new Error('slug is required');
  return myApi('GET', `v2/admin/integrations/partners/by-slug/${encodeURIComponent(slug)}`, {});
};

/** Admin: create partner `{ slug, name, capabilities? }`. */
lib.createIntegrationPartner = async (payload) =>
  myApi('POST', 'v2/admin/integrations/partners/', payload);

/** Admin: partner detail with users, masked keys, admin_modules, portal. */
lib.getIntegrationPartner = async (partnerId) => {
  if (partnerId == null || partnerId === '') throw new Error('partnerId is required');
  return myApi('GET', `v2/admin/integrations/partners/${encodeURIComponent(partnerId)}`, {});
};

/** Admin: update partner `{ status?: IntegrationPartnerStatus, name?: string }`. */
lib.patchIntegrationPartner = async (partnerId, payload) => {
  if (partnerId == null || partnerId === '') throw new Error('partnerId is required');
  return myApi('PATCH', `v2/admin/integrations/partners/${encodeURIComponent(partnerId)}`, payload);
};

/** Admin: invite portal user `{ email, name? }`. */
lib.inviteIntegrationPartnerUser = async (partnerId, payload) => {
  if (partnerId == null || partnerId === '') throw new Error('partnerId is required');
  return myApi('POST', `v2/admin/integrations/partners/${encodeURIComponent(partnerId)}/users`, payload);
};

/** Admin: update partner user `{ status?, role?, name? }`. Role: 'admin'|'member' (owner protected). */
lib.patchIntegrationPartnerUser = async (partnerId, userId, payload) => {
  if (partnerId == null || partnerId === '') throw new Error('partnerId is required');
  if (userId == null || userId === '') throw new Error('userId is required');
  return myApi(
    'PATCH',
    `v2/admin/integrations/partners/${encodeURIComponent(partnerId)}/users/${encodeURIComponent(userId)}`,
    payload,
  );
};

/** Admin: soft-delete partner user (owner protected — transfer ownership first). */
lib.deleteIntegrationPartnerUser = async (partnerId, userId) => {
  if (partnerId == null || partnerId === '') throw new Error('partnerId is required');
  if (userId == null || userId === '') throw new Error('userId is required');
  return myApi(
    'DELETE',
    `v2/admin/integrations/partners/${encodeURIComponent(partnerId)}/users/${encodeURIComponent(userId)}`,
  );
};

/** Admin: re-issue invite for an invited (never-logged-in) partner user. */
lib.resendIntegrationPartnerInvite = async (partnerId, userId) => {
  if (partnerId == null || partnerId === '') throw new Error('partnerId is required');
  if (userId == null || userId === '') throw new Error('userId is required');
  return myApi(
    'POST',
    `v2/admin/integrations/partners/${encodeURIComponent(partnerId)}/users/${encodeURIComponent(userId)}/resend-invite`,
    {},
  );
};

/** Admin: make `{userId}` the partner owner. Previous owner becomes admin. */
lib.transferIntegrationPartnerOwnership = async (partnerId, userId) => {
  if (partnerId == null || partnerId === '') throw new Error('partnerId is required');
  if (userId == null || userId === '') throw new Error('userId is required');
  return myApi(
    'POST',
    `v2/admin/integrations/partners/${encodeURIComponent(partnerId)}/users/${encodeURIComponent(userId)}/transfer-ownership`,
    {},
  );
};

/** Admin: paginated audit log (`page`, `page_size`). */
lib.getIntegrationPartnerAudit = async (partnerId, params = {}) => {
  if (partnerId == null || partnerId === '') throw new Error('partnerId is required');
  const query = { page: 1, page_size: 20, ...params };
  return myApi('GET', `v2/admin/integrations/partners/${encodeURIComponent(partnerId)}/audit`, query);
};

/** Admin: integration capability catalog (create-partner picker). */
lib.listIntegrationCapabilities = async () =>
  myApi('GET', 'v2/admin/integrations/capabilities', {});

/** Admin: outbound PA Agent webhook config (write-only secret). */
lib.patchPaAgentOutboundConnector = async (partnerId, payload) => {
  if (partnerId == null || partnerId === '') throw new Error('partnerId is required');
  return myApi(
    'PATCH',
    `v2/admin/integrations/partners/${encodeURIComponent(partnerId)}/connector/pa.agent/outbound`,
    payload,
  );
};

export async function uploader(url, formData) {
  let user = null;
  try {
    const json = localStorage.getItem(config.key.user);
    if (json && json !== null) {
      user = JSON.parse(json);
    }
  } catch (e) {
    // If JSON parsing fails, clear corrupted data
    localStorage.removeItem(config.key.user);
    localStorage.removeItem(config.key.token);
  }
  
  // let herder = headers;
  let header
  if (user) {
    header = { ...headers, Authorization: "Bearer " + user?.jwt, }
  } else {
    header = headers;
  }
  try {
    const loc = `${uri}?${url}`;
    const response = await fetch(loc, {
      method: "POST",
      body: formData,
      headers: header
    });
    return await response.json();
  } catch (e) {
    console.error("Error:", e);
    return { status: "error", msg: e?.response?.data?.msg || e?.message };
  }
}

export async function* fetchPaginatedData(
  method = "post",
  uri = "index",
  payload = {}
) {
  let page = 1;
  let totalPages = 1;

  do {
    const response = await myRequest(method, uri, { ...payload, page });
    const responseData = await response?.data;
    //   Quit operation on error
    if (responseData?.error) {
      break;
    }

    const { pages, data } = responseData?.data;
    ++page;
    totalPages = pages;
    yield data;
  } while (page <= totalPages);
}

export const calculateAge = (birthDateTime) => {
  const today = new Date();
  const dob = new Date(birthDateTime.slice(0, 10));
  let age = today.getFullYear() - dob.getFullYear();

  const todayMonthDay = (today.getMonth() << 5) + today.getDate();
  const birthMonthDay = (dob.getMonth() << 5) + dob.getDate();

  if (todayMonthDay < birthMonthDay) {
    age--;
  }

  return age;
};

export const calculateAgeAlt = (birthDate) => {
  const today = new Date();
  const dob = new Date(birthDate);
  let age = today.getFullYear() - dob.getFullYear();

  const todayMonthDay = (today.getMonth() << 5) + today.getDate();
  const birthMonthDay = (dob.getMonth() << 5) + dob.getDate();

  if (todayMonthDay < birthMonthDay) {
    age--;
  }

  return age;
};

export const underscoreToCapitalizedWords = (variable) => {
  return variable
    .split(`_`)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const camelToCapitalizedWords = (variable) => {
  return variable
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const processObjectKeysForCSVExport = (jsonobj) => {
  if (Array.isArray(jsonobj)) {
    return jsonobj.map((value) =>
      Object.entries(value).map((__) => ({
        [underscoreToCapitalizedWords(__[0])]: __[1],
      }))
    );
  }
  return Object.entries(jsonobj).map((__) => ({
    [underscoreToCapitalizedWords(__[0])]: __[1],
  }));
};

export const processObjectKeysForCSVExportOptimized = (
  jsonobj,
  excludedKeys = []
) =>
  Array.isArray(jsonobj)
    ? jsonobj.map((obj) =>
      Object.entries(obj)
        .filter(([key]) => !excludedKeys.includes(key))
        .map(([key, value]) => ({
          [underscoreToCapitalizedWords(key)]: value,
        }))
    )
    : Object.entries(jsonobj)
      .filter(([key]) => !excludedKeys.includes(key))
      .map(([key, value]) => ({
        [underscoreToCapitalizedWords(key)]: value,
      }));

export const downloadJSONAsCSV = (data, filename) => {
  const csvData = [];

  // Extract header row from data
  const header = Object.keys(data);
  csvData.push(header.join(","));

  // Extract data rows from data
  // data.forEach(item => {
  const row = Object.values(data).map((val) => val);
  csvData.push(row.join("\n"));
  // });

  // Create CSV file and download
  const csvContent = csvData.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Release the URL object to prevent empty file downloads
  URL.revokeObjectURL(url);
};

export const downloadCSVFromJSON = (
  jsonData,
  filename = "data.csv",
  excludedKeys = [],
  callback = null,
  delimeter = "\t",
  headerHandler = "underscore"
) => {
  const csvRows = [];
  const headers = Object.keys(jsonData[0]).filter(
    (key) => !excludedKeys.includes(key)
  );
  for (const dataObj of jsonData) {
    const rowValues = [];
    for (const header of headers) {
      rowValues.push(dataObj[header]);
    }
    csvRows.push(rowValues.join(delimeter));
  }

  let csvString = "";
  switch (headerHandler) {
    case "underscore":
      csvString = [
        headers.map((h) => underscoreToCapitalizedWords(h)).join("\t"),
        ...csvRows,
      ].join("\n");
      break;
    case "camel":
      csvString = [
        headers.map((h) => camelToCapitalizedWords(h)).join("\t"),
        ...csvRows,
      ].join("\n");
      break;
    default:
      csvString = [headers.join("\t"), ...csvRows].join("\n");
      break;
  }

  // Create a download link for the CSV file
  const downloadLink = document.createElement("a");
  const blob = new Blob([csvString], { type: "text/csv" });
  downloadLink.href = URL.createObjectURL(blob);
  downloadLink.download = filename;

  // Trigger the download link to download the CSV file
  const triggerDownload = () => {
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  if (callback) {
    downloadLink.addEventListener("click", callback);
  }

  triggerDownload();
};

export function removeDuplicates(array, property) {
  if (!array) return;
  return array.filter(
    (obj, index, self) =>
      !self.slice(index + 1).find((item) => item[property] === obj[property])
  );
}

export function removeDuplicatesCompareAll(array, property) {
  return array.filter((obj, index) =>
    !array.slice(0, index).some((item) => item[property] === obj[property])
  );
}

export function isEqual(obj1, obj2) {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
}

export function updateChangedObjects(oldObjects, newObjects) {
  const oldObjectMap = {};
  const updatedObjects = [];

  // Create a map of old objects indexed by id
  for (const oldObj of oldObjects) {
    oldObjectMap[oldObj.id] = oldObj;
  }

  // Iterate through new objects and compare with corresponding old objects
  for (const newObj of newObjects) {
    const oldObj = oldObjectMap[newObj.id];

    if (!oldObj || !isEqual(oldObj, newObj)) {
      updatedObjects.push(newObj);
    } else {
      updatedObjects.push(oldObj); // Push unchanged objects
    }
  }

  return updatedObjects;
}

export function findDifferentKeysTwoObj(obj1, obj2, keysToOmit = [], controlKey = '') {
  if (!(obj1 && obj2)) return [];

  const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
  const differentKeys = [];

  for (const key of allKeys) {
    if (keysToOmit.includes(key)) {
      continue; // Skip keys that are marked for omission
    }

    const value1 = obj1[key];
    const value2 = obj2[key];

    if (value1 !== value2) {
      differentKeys.push(key);
    }
  }

  return differentKeys;
}

export function findDifferentKeysThreeObj(obj1, obj2, obj3, keysToOmit = []) {
  if (!(obj1 && obj2 && obj3)) return [];
  const allKeys = new Set([
    ...Object.keys(obj1),
    ...Object.keys(obj2),
    ...Object.keys(obj3),
  ]);
  const differentKeys = [];

  for (const key of allKeys) {
    if (keysToOmit.includes(key)) {
      continue; // Skip keys that are marked for omission
    }
    const value1 = obj1[key];
    const value2 = obj2[key];
    const value3 = obj3[key];

    if (value1 !== value2 || value1 !== value3 || value2 !== value3) {
      differentKeys.push(key);
    }
  }

  return differentKeys;
}

export function omitKeys(obj, keysToOmit) {
  if (!obj) return {};
  const newObj = { ...obj };

  for (const key of keysToOmit) {
    delete newObj[key];
  }

  return newObj;
}

export function writeArrayToCSV(data, filename = "data.csv", delimiter = ",") {
  if (!Array.isArray(data) || data.length === 0) {
    console.error("Invalid data provided.");
    return;
  }

  let csvContent = data;
  if (Array.isArray(data[1])) {
    csvContent = data.map((row) => row.join(delimiter)).join("\n");
  }

  const blob = new Blob([csvContent], { type: "text/csv" });
  const link = document.createElement("a");

  link.href = URL.createObjectURL(blob);
  link.download = filename;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export const confirmModal = async (content = <></>, title = 'Warning', type = 'warning', icon = <WarningFilled color="gold" />) => new Promise(resolved => Modal.confirm({
  title, content,
  icon: icon,
  destroyOnClose: true,
  cancelText: 'No',
  okText: 'Yes',
  okType: 'danger',
  onCancel: () => resolved(false),
  onOk: () => resolved(true),
  type
}))

export function sleep(delayInMilliseconds) {
  return new Promise(resolve => setTimeout(resolve, delayInMilliseconds));
}

// export const myTime = async () => {
//   return fetch(worldTimeApi).then(response => response.json())
// }

// ============================================================================
// Notification API Functions (V2)
// ============================================================================

/**
 * Register Expo push token for notifications
 * POST /v2/notifications/push-tokens
 */
lib.registerPushToken = async (expoPushToken, platform = 'web', deviceId = null) => {
  if (!expoPushToken) {
    throw new Error('expoPushToken is required');
  }
  if (!['ios', 'android', 'web'].includes(platform)) {
    throw new Error('platform must be ios, android, or web');
  }

  const body = {
    expo_push_token: expoPushToken,
    platform,
    device_id: deviceId,
  };

  return myApi('POST', 'v2/notifications/push-tokens', body);
};

/**
 * Unregister Expo push token
 * DELETE /v2/notifications/push-tokens
 */
lib.unregisterPushToken = async (expoPushToken) => {
  if (!expoPushToken) {
    throw new Error('expoPushToken is required');
  }

  const body = {
    expo_push_token: expoPushToken,
  };

  return myApi('DELETE', 'v2/notifications/push-tokens', body);
};

/**
 * Get user notification preferences
 * GET /v2/notifications/preferences
 */
lib.getNotificationPreferences = async () => {
  return myApi('GET', 'v2/notifications/preferences');
};

/**
 * Update user notification preferences
 * PUT /v2/notifications/preferences
 */
lib.updateNotificationPreferences = async (preferences) => {
  if (!preferences || typeof preferences !== 'object') {
    throw new Error('preferences object is required');
  }

  return myApi('PUT', 'v2/notifications/preferences', preferences);
};

/**
 * Get UI counters (badges) for current user
 * GET /v2/ui/counters
 * Returns role-specific counters (notifications, PA, claims, appointments, enrollees)
 */
lib.getUICounters = async () => {
  try {
    return myApi('GET', 'v2/ui/counters');
  } catch (error) {
    console.error('Error fetching UI counters:', error);
    // Return empty counters object on error
    return {
      error: 1,
      message: error?.message || 'Failed to fetch counters',
      data: {},
    };
  }
};

/**
 * Get notifications (v2) - paginated list
 * GET /v2/notifications-v2
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Items per page (default: 50)
 * @param {number} params.read - Filter by read status (0=unread, 1=read, omitted=all)
 * @param {string} params.category - Filter by category (appointments, claims, pa, system, etc.)
 */
lib.getNotificationsV2 = async (params = {}) => {
  const queryParams = {
    page: 1,
    pageSize: 50,
    ...params,
  };

  try {
    return myApi('GET', 'v2/notifications-v2/', queryParams);
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return {
      error: 1,
      message: error?.message || 'Failed to fetch notifications',
      data: [],
      pagination: null,
    };
  }
};

/**
 * Get unread notification count with breakdown
 * GET /v2/notifications-v2/unread-count
 * Returns total unread count and optional breakdown by category
 */
lib.getUnreadNotificationCount = async () => {
  try {
    return myApi('GET', 'v2/notifications-v2/unread-count');
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return {
      error: 1,
      message: error?.message || 'Failed to fetch unread count',
      unread_count: 0,
    };
  }
};

/**
 * Get admin notifications history (legacy - kept for backward compatibility)
 * GET /v2/admin/notifications
 * @deprecated Use getNotificationsV2() instead
 * Note: This endpoint may not be implemented yet, fallback to mock if needed
 */
lib.getAdminNotifications = async (params = {}) => {
  // Try new v2 endpoint first
  try {
    return await lib.getNotificationsV2(params);
  } catch (error) {
    // Fallback to old endpoint if v2 doesn't work
    const queryParams = {
      page: 1,
      pageSize: 50,
      ...params,
    };

    try {
      return myApi('GET', 'v2/admin/notifications/', queryParams);
    } catch (fallbackError) {
      // If endpoint doesn't exist, return mock data for development
      console.warn('Admin notifications endpoint not implemented yet, using mock data');
      return {
        error: 0,
        message: 'Mock data - endpoint not implemented',
        data: [],
        unread_count: 0,
      };
    }
  }
};

/**
 * Mark notification as read (v2)
 * PUT /v2/notifications-v2/{id}/read
 * @param {string|number} notificationId - Notification ID
 */
lib.markNotificationAsRead = async (notificationId) => {
  if (!notificationId) {
    throw new Error('notificationId is required');
  }

  const encodedId = encodeURIComponent(notificationId);

  try {
    return myApi('PUT', `v2/notifications-v2/${encodedId}/read`);
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return {
      error: 1,
      message: error?.message || 'Failed to mark notification as read',
    };
  }
};

/**
 * Mark all notifications as read (v2)
 * PUT /v2/notifications-v2/read-all
 * @param {Object} options - Optional parameters
 * @param {string} options.category - Optional category filter (mark all in category as read)
 */
lib.markAllNotificationsAsRead = async (options = {}) => {
  try {
    const body = {};
    if (options.category) {
      body.category = options.category;
    }
    return myApi('PUT', 'v2/notifications-v2/read-all', body);
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return {
      error: 1,
      message: error?.message || 'Failed to mark all notifications as read',
    };
  }
};

/**
 * Delete notification (v2)
 * DELETE /v2/notifications-v2/{id}
 * @param {string|number} notificationId - Notification ID
 */
lib.deleteNotification = async (notificationId) => {
  if (!notificationId) {
    throw new Error('notificationId is required');
  }

  const encodedId = encodeURIComponent(notificationId);

  try {
    return myApi('DELETE', `v2/notifications-v2/${encodedId}`);
  } catch (error) {
    console.error('Error deleting notification:', error);
    return {
      error: 1,
      message: error?.message || 'Failed to delete notification',
    };
  }
};

/**
 * Send a system-wide broadcast notification to client app users
 * POST /v2/admin/notifications/broadcast
 */
// Targeting options shared by announcements and push: landing pages,
// organisations, plans, access types (principal / spouse / child …).
lib.getBroadcastOptions = async () => myApi('GET', 'v2/admin/notifications/broadcast/options');

lib.sendBroadcast = async (payload) => {
  if (!payload?.type || !payload?.title || !payload?.body) {
    throw new Error('type, title, and body are required');
  }
  return myApi('POST', 'v2/admin/notifications/broadcast', payload);
};

/**
 * Get broadcast notification history
 * GET /v2/admin/notifications/broadcasts
 */
lib.getBroadcastHistory = async (page = 1, pageSize = 20) => {
  return myApi('GET', 'v2/admin/notifications/broadcasts', { page, pageSize });
};

/**
 * Cancel a scheduled broadcast
 * DELETE /v2/admin/notifications/broadcasts/{id}
 */
lib.cancelScheduledBroadcast = async (id) => {
  if (!id) {
    throw new Error('broadcast id is required');
  }
  return myApi('DELETE', `v2/admin/notifications/broadcasts/${encodeURIComponent(id)}`);
};

/**
 * Per-device ticket audit for a broadcast (what was accepted/delivered/failed, and why)
 * GET /v2/admin/notifications/broadcasts/{id}/tickets
 */
lib.getBroadcastTickets = async (id, { filter, page = 1, pageSize = 50 } = {}) =>
  myApi('GET', `v2/admin/notifications/broadcasts/${encodeURIComponent(id)}/tickets`, {
    filter,
    page,
    page_size: pageSize,
    pageSize,
  });

/**
 * Retry a completed broadcast's failed (retryable) devices
 * POST /v2/admin/notifications/broadcasts/{id}/retry-failed
 */
lib.retryBroadcastFailed = async (id) =>
  myApi('POST', `v2/admin/notifications/broadcasts/${encodeURIComponent(id)}/retry-failed`, {});

/**
 * Staff password reset (email-primary admin accounts)
 * POST /v2/auth/staff/password-reset-request
 * POST /v2/auth/staff/password-reset
 */
lib.requestStaffPasswordReset = async (email) => {
  return myApi('POST', 'v2/auth/staff/password-reset-request', { email: String(email || '').trim() });
};

lib.completeStaffPasswordReset = async ({ email, code, password, confirm_password }) => {
  return myApi('POST', 'v2/auth/staff/password-reset', {
    email: String(email || '').trim(),
    code: String(code || '').trim(),
    password,
    confirm_password: confirm_password ?? password,
  });
};

/**
 * Email campaigns (multi-audience email broadcast)
 */
lib.getEmailCampaignAudiences = async () => myApi('GET', 'v2/admin/email-campaigns/audiences');

lib.previewEmailCampaign = async (payload) => myApi('POST', 'v2/admin/email-campaigns/preview', payload);

/** Full-fidelity render: the exact branded HTML BrandEmailLayout::wrap() would send. */
lib.previewEmailCampaignHtml = async (payload) => myApi('POST', 'v2/admin/email-campaigns/preview-html', payload);

lib.createEmailCampaign = async (payload) => myApi('POST', 'v2/admin/email-campaigns', payload);

lib.getEmailCampaignHistory = async (page = 1, pageSize = 20) =>
  myApi('GET', 'v2/admin/email-campaigns', { page, page_size: pageSize, pageSize });

lib.getEmailCampaign = async (id) => myApi('GET', `v2/admin/email-campaigns/${encodeURIComponent(id)}`);

lib.getEmailCampaignRecipients = async (id, { status, page = 1, pageSize = 50 } = {}) =>
  myApi('GET', `v2/admin/email-campaigns/${encodeURIComponent(id)}/recipients`, {
    status,
    page,
    page_size: pageSize,
    pageSize,
  });

lib.previewEmailCampaignById = async (id) =>
  myApi('POST', `v2/admin/email-campaigns/${encodeURIComponent(id)}/preview`);

lib.sendEmailCampaign = async (id) =>
  myApi('POST', `v2/admin/email-campaigns/${encodeURIComponent(id)}/send`);

lib.cancelEmailCampaign = async (id) =>
  myApi('POST', `v2/admin/email-campaigns/${encodeURIComponent(id)}/cancel`);

lib.retryFailedEmailCampaign = async (id, recipientIds) =>
  myApi(
    'POST',
    `v2/admin/email-campaigns/${encodeURIComponent(id)}/retry-failed`,
    recipientIds && recipientIds.length ? { recipient_ids: recipientIds } : {},
  );

lib.listEmailCampaignTemplates = async () => myApi('GET', 'v2/admin/email-campaigns/templates');

lib.createEmailCampaignTemplate = async (payload) =>
  myApi('POST', 'v2/admin/email-campaigns/templates', payload);

lib.updateEmailCampaignTemplate = async (id, payload) =>
  myApi('PUT', `v2/admin/email-campaigns/templates/${encodeURIComponent(id)}`, payload);

lib.deleteEmailCampaignTemplate = async (id) =>
  myApi('DELETE', `v2/admin/email-campaigns/templates/${encodeURIComponent(id)}`);

/**
 * Get pending claims (claims pending > specified hours)
 * GET /v2/admin/claims/pending
 * Note: This endpoint may not be implemented yet
 */
lib.getPendingClaims = async (params = {}) => {
  const queryParams = {
    hours: 24,
    ...params,
  };

  try {
    return myApi('GET', 'v2/admin/claims/pending', queryParams);
  } catch (error) {
    // If endpoint doesn't exist, return mock data for development
    console.warn('Pending claims endpoint not implemented yet, using mock data');
    return {
      error: 0,
      message: 'Mock data - endpoint not implemented',
      data: [],
    };
  }
};

/**
 * Get live statistics for dashboard
 * GET /v2/admin/stats
 * Note: This endpoint may not be implemented yet
 */
lib.getAdminStats = async () => {
  try {
    return myApi('GET', 'v2/admin/stats');
  } catch (error) {
    // If endpoint doesn't exist, return mock data for development
    console.warn('Admin stats endpoint not implemented yet, using mock data');
    return {
      error: 0,
      message: 'Mock data - endpoint not implemented',
      data: {
        appointments_today: 0,
        appointments_pending: 0,
        pa_requests_today: 0,
        claims_pending: 0,
      },
    };
  }
};

// ===== Premium Consumption & Limit Enforcement (v2) =====

// GET /v2/care/enrollees/{insurance_no}/consumption
lib.getEnrolleeConsumption = async (insuranceNo, params = {}) => {
  if (!insuranceNo) throw new Error('insuranceNo is required');
  const encoded = encodeURIComponent(insuranceNo);
  return myApi('GET', `v2/care/enrollees/${encoded}/consumption`, params);
};

// GET /v2/care/enrollees/{insurance_no}/consumption-report — Client Consumption Report data
// (PA-approved lines for the page's period). summary: 1 returns only counts.
lib.getClientConsumptionReport = async (insuranceNo, params = {}) => {
  if (!insuranceNo) throw new Error('insuranceNo is required');
  const encoded = encodeURIComponent(insuranceNo);
  return myApi('GET', `v2/care/enrollees/${encoded}/consumption-report`, params);
};

// GET /v2/care/enrollees/{insurance_no}/consumed-items
lib.getEnrolleeConsumedItems = async (insuranceNo, params = {}) => {
  if (!insuranceNo) throw new Error('insuranceNo is required');
  const encoded = encodeURIComponent(insuranceNo);
  return myApi('GET', `v2/care/enrollees/${encoded}/consumed-items`, params);
};

// GET /v2/care/enrollees/{insurance_no}/cycles
lib.getEnrolleeCycles = async (insuranceNo) => {
  if (!insuranceNo) throw new Error('insuranceNo is required');
  const encoded = encodeURIComponent(insuranceNo);
  return myApi('GET', `v2/care/enrollees/${encoded}/cycles`);
};

// GET /v2/admin/tenors
lib.getAdminTenors = async () => myApi('GET', 'v2/admin/tenors');

// POST /v2/admin/tenors
lib.createAdminTenor = async (payload = {}) => myApi('POST', 'v2/admin/tenors', payload);

// PUT /v2/admin/tenors/{id}
lib.updateAdminTenor = async (id, payload = {}) => {
  if (!id) throw new Error('id is required');
  return myApi('PUT', `v2/admin/tenors/${encodeURIComponent(id)}`, payload);
};

// DELETE /v2/admin/tenors/{id}
lib.deleteAdminTenor = async (id) => {
  if (!id) throw new Error('id is required');
  return myApi('DELETE', `v2/admin/tenors/${encodeURIComponent(id)}`);
};

// GET /v2/admin/limit-metric-types
lib.getLimitMetricTypes = async () => myApi('GET', 'v2/admin/limit-metric-types/');

// Legacy plan structure (same as plan detail screen) — used to pick category / item IDs for v2 limit targets
lib.fetchPlanDetailCategories = async (planId) => {
  if (!planId) throw new Error('planId is required');
  return myRequest('post', 'planDetailCategories', { id: planId });
};

lib.fetchPlanCategoryItems = async (planId, plansPlansCategoryId) => {
  if (!planId || !plansPlansCategoryId) throw new Error('planId and plansPlansCategoryId are required');
  return myRequest('post', 'planDetailItems', { id: planId, categoryId: plansPlansCategoryId });
};

// GET /v2/admin/plans/{plan_id}/limits
lib.getPlanLimits = async (planId) => {
  if (!planId) throw new Error('planId is required');
  return myApi('GET', `v2/admin/plans/${encodeURIComponent(planId)}/limits/`);
};

// POST /v2/admin/plans/{plan_id}/limits
lib.createPlanLimit = async (planId, payload = {}) => {
  if (!planId) throw new Error('planId is required');
  return myApi('POST', `v2/admin/plans/${encodeURIComponent(planId)}/limits/`, payload);
};

// PUT /v2/admin/plans/{plan_id}/limits/{limit_id}
lib.updatePlanLimit = async (planId, limitId, payload = {}) => {
  if (!planId || !limitId) throw new Error('planId and limitId are required');
  return myApi('PUT', `v2/admin/plans/${encodeURIComponent(planId)}/limits/${encodeURIComponent(limitId)}`, payload);
};

// DELETE /v2/admin/plans/{plan_id}/limits/{limit_id}
lib.deletePlanLimit = async (planId, limitId) => {
  if (!planId || !limitId) throw new Error('planId and limitId are required');
  return myApi('DELETE', `v2/admin/plans/${encodeURIComponent(planId)}/limits/${encodeURIComponent(limitId)}`);
};

// GET /v2/admin/plans/{plan_id}/limits/enforcement
lib.getPlanEnforcement = async (planId) => {
  if (!planId) throw new Error('planId is required');
  return myApi('GET', `v2/admin/plans/${encodeURIComponent(planId)}/limits/enforcement`);
};

// PUT /v2/admin/plans/{plan_id}/limits/enforcement
lib.updatePlanEnforcement = async (planId, payload = {}) => {
  if (!planId) throw new Error('planId is required');
  return myApi('PUT', `v2/admin/plans/${encodeURIComponent(planId)}/limits/enforcement`, payload);
};

// --- Admin checkin types (encounter types) — /v2/admin/checkin-types ---
lib.listAdminCheckinTypes = async (params = {}) =>
  myApi('GET', 'v2/admin/checkin-types/', params);

lib.getAdminCheckinType = async (id) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('GET', `v2/admin/checkin-types/${encodeURIComponent(id)}`);
};

lib.createAdminCheckinType = async (body = {}) =>
  myApi('POST', 'v2/admin/checkin-types/', body);

lib.updateAdminCheckinType = async (id, body = {}) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('PUT', `v2/admin/checkin-types/${encodeURIComponent(id)}`, body);
};

lib.previewAdminCheckinType = async (id, facilityId) => {
  if (id == null || id === '') throw new Error('id is required');
  if (facilityId == null || facilityId === '') throw new Error('facilityId is required');
  return myApi(
    'GET',
    `v2/admin/checkin-types/${encodeURIComponent(id)}/preview`,
    { facility_id: facilityId },
  );
};

lib.replaceAdminCheckinTypeFacilityTypes = async (id, body = {}) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('PUT', `v2/admin/checkin-types/${encodeURIComponent(id)}/facility-types`, body);
};

lib.replaceAdminCheckinTypeFacilities = async (id, body = {}) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('PUT', `v2/admin/checkin-types/${encodeURIComponent(id)}/facilities`, body);
};

lib.replaceAdminCheckinTypeCategories = async (id, body = {}) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('PUT', `v2/admin/checkin-types/${encodeURIComponent(id)}/categories`, body);
};

lib.replaceAdminCheckinTypeTariffs = async (id, body = {}) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('PUT', `v2/admin/checkin-types/${encodeURIComponent(id)}/tariffs`, body);
};

lib.replaceAdminCheckinTypeItems = async (id, body = {}) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('PUT', `v2/admin/checkin-types/${encodeURIComponent(id)}/items`, body);
};

// GET /v2/admin/checkin-types/lookups/facility-types — dental, hospital, pharmacy, etc.
lib.searchAdminCheckinTypeFacilityTypes = async (params = {}) =>
  myApi('GET', 'v2/admin/checkin-types/lookups/facility-types', params);

// GET /v2/admin/checkin-types/lookups/plans-for-pool — plans that include this limit pool (+ limits).
lib.searchAdminCheckinTypePlansForPool = async (plansCategoriesId) => {
  if (plansCategoriesId == null || plansCategoriesId === '') {
    throw new Error('plansCategoriesId is required');
  }
  return myApi('GET', 'v2/admin/checkin-types/lookups/plans-for-pool', {
    plans_categories_id: plansCategoriesId,
  });
};

// GET /v2/admin/checkin-types/lookups/categories — categories on linked tariff(s).
lib.searchAdminCheckinTypeCategories = async (params = {}) =>
  myApi('GET', 'v2/admin/checkin-types/lookups/categories', params);

// GET /v2/admin/checkin-types/lookups/items — tariff line typeahead for item_rules (min 2 chars).
lib.searchAdminCheckinTypeItems = async (params = {}) =>
  myApi('GET', 'v2/admin/checkin-types/lookups/items', { limit: 20, ...params });

// Facility/authenticated checkin types — /v2/checkin-types
lib.getCheckinTypes = async (params = {}) => myApi('GET', 'v2/checkin-types', params);

lib.getCheckinTypeCategories = async (id, params = {}) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('GET', `v2/checkin-types/${encodeURIComponent(id)}/categories`, params);
};

lib.getCheckinTypeCategoryItems = async (id, categoryId, params = {}) => {
  if (id == null || id === '') throw new Error('id is required');
  if (categoryId == null || categoryId === '') throw new Error('categoryId is required');
  return myApi(
    'GET',
    `v2/checkin-types/${encodeURIComponent(id)}/categories/${encodeURIComponent(categoryId)}/items`,
    params,
  );
};

// GET /v2/pa/{checkin_id}/evidence-policy — workflow rules + existing header/line files.
lib.getPaEvidencePolicy = async (checkinId, params = {}) => {
  if (checkinId == null || checkinId === '') throw new Error('checkinId is required');
  return myApi(
    'GET',
    `v2/pa/${encodeURIComponent(checkinId)}/evidence-policy`,
    params,
  );
};

// GET /v2/checkin-types/{id}/workflow-policy — PA evidence and workflow rules for an encounter type.
lib.getCheckinTypeWorkflowPolicy = async (id, params = {}) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi(
    'GET',
    `v2/checkin-types/${encodeURIComponent(id)}/workflow-policy`,
    params,
  );
};

// POST /v2/pa/{checkin_id}/attachments — upload PA header evidence file.
lib.uploadPaHeaderEvidence = async (checkinId, file) => {
  if (checkinId == null || checkinId === '') throw new Error('checkinId is required');
  if (!file) throw new Error('file is required');
  const formData = new FormData();
  formData.append('file', file);
  return myApi(
    'POST',
    `v2/pa/${encodeURIComponent(checkinId)}/attachments`,
    formData,
  );
};

// POST /v2/pa/{checkin_id}/items/{claim_item_id}/attachments — upload PA line evidence file.
lib.uploadPaItemEvidence = async (checkinId, claimItemId, file) => {
  if (checkinId == null || checkinId === '') throw new Error('checkinId is required');
  if (claimItemId == null || claimItemId === '') throw new Error('claimItemId is required');
  if (!file) throw new Error('file is required');
  const formData = new FormData();
  formData.append('file', file);
  return myApi(
    'POST',
    `v2/pa/${encodeURIComponent(checkinId)}/items/${encodeURIComponent(claimItemId)}/attachments`,
    formData,
  );
};

// POST /v2/pa/{checkin_id}/finalize — validate evidence, apply auto-approve / auto-close.
lib.finalizePaWithEvidence = async (checkinId, payload = {}) => {
  if (checkinId == null || checkinId === '') throw new Error('checkinId is required');
  return myApi(
    'POST',
    `v2/pa/${encodeURIComponent(checkinId)}/finalize`,
    payload,
  );
};

// --- Admin care programs — /v2/admin/care-programs ---
lib.listAdminCarePrograms = async (params = {}) =>
  myApi('GET', 'v2/admin/care-programs/', params);

lib.getAdminCareProgram = async (id) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('GET', `v2/admin/care-programs/${encodeURIComponent(id)}`);
};

lib.getAdminCareProgramSchema = async () =>
  myApi('GET', 'v2/admin/care-programs/schema');

lib.createAdminCareProgram = async (body = {}) =>
  myApi('POST', 'v2/admin/care-programs/', body);

lib.updateAdminCareProgram = async (id, body = {}) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('PUT', `v2/admin/care-programs/${encodeURIComponent(id)}`, body);
};

lib.publishAdminCareProgram = async (id) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('POST', `v2/admin/care-programs/${encodeURIComponent(id)}/publish`);
};

lib.archiveAdminCareProgram = async (id) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('POST', `v2/admin/care-programs/${encodeURIComponent(id)}/archive`);
};

lib.unarchiveAdminCareProgram = async (id) => {
  if (id == null || id === '') throw new Error('id is required');
  return myApi('POST', `v2/admin/care-programs/${encodeURIComponent(id)}/unarchive`);
};

// GET /v2/admin/programs/{code}/utilization — cross-org ops report
lib.getAdminCareProgramUtilization = async (programCode, params = {}) => {
  if (!programCode) throw new Error('programCode is required');
  return myApi(
    'GET',
    `v2/admin/programs/${encodeURIComponent(programCode)}/utilization`,
    params,
  );
};

// Org enablement — /v2/admin/organizations/{orgId}/programs
lib.listOrgCarePrograms = async (orgId) => {
  if (orgId == null || orgId === '') throw new Error('orgId is required');
  return myApi('GET', `v2/admin/organizations/${encodeURIComponent(orgId)}/programs`);
};

lib.updateOrgCareProgram = async (orgId, programCode, body = {}) => {
  if (orgId == null || orgId === '') throw new Error('orgId is required');
  if (!programCode) throw new Error('programCode is required');
  return myApi(
    'PUT',
    `v2/admin/organizations/${encodeURIComponent(orgId)}/programs/${encodeURIComponent(programCode)}`,
    body,
  );
};

// HR / corporate portal — /v2/programs (helpers for future HR UI)
lib.listEnabledCarePrograms = async () => myApi('GET', 'v2/programs');

lib.getCareProgramManifest = async (programCode) => {
  if (!programCode) throw new Error('programCode is required');
  return myApi('GET', `v2/programs/${encodeURIComponent(programCode)}/manifest`);
};

lib.getCareProgramUtilizationSummary = async (programCode, params = {}) => {
  if (!programCode) throw new Error('programCode is required');
  return myApi(
    'GET',
    `v2/programs/${encodeURIComponent(programCode)}/utilization/summary`,
    params,
  );
};

lib.getCareProgramUtilization = async (programCode, params = {}) => {
  if (!programCode) throw new Error('programCode is required');
  return myApi(
    'GET',
    `v2/programs/${encodeURIComponent(programCode)}/utilization`,
    params,
  );
};

lib.exportCareProgramCsv = async (programCode, params = {}) => {
  if (!programCode) throw new Error('programCode is required');
  const query = new URLSearchParams(params).toString();
  const path = `v2/programs/${encodeURIComponent(programCode)}/export.csv${query ? `?${query}` : ''}`;
  return myApi('GET', path, {}, { responseType: 'blob' });
};

/** POST /v2/admin/encounter-codes — facility-only generate (no checkinType) */
lib.generateAdminEncounterCode = async (payload = {}) => {
  return request.post('/v2/admin/encounter-codes/', payload, setHeader());
};

/** GET /v2/admin/enrollees/{id}/encounter-codes */
lib.listEnrolleeEncounterCodes = async (enrolleeId, params = {}) => {
  if (!enrolleeId) throw new Error('enrolleeId is required');
  return request.get(
    `/v2/admin/enrollees/${encodeURIComponent(enrolleeId)}/encounter-codes`,
    { ...setHeader(), params },
  );
};

/** POST /v2/admin/encounter-codes/cancel — body { code } */
lib.cancelAdminEncounterCode = async (code) => {
  return request.post(
    '/v2/admin/encounter-codes/cancel',
    { code: String(code || '').trim() },
    setHeader(),
  );
};

/**
 * GET /v2/admin/encounter-codes/facilities — facility picker for generate.
 * Scoped to the enrollee's plan/tier only, never their location.
 * Higher-tier facilities are included and annotated is_higher_tier (admin soft-allow).
 */
lib.getEncounterCodeFacilities = async (params = {}) => {
  return request.get('/v2/admin/encounter-codes/facilities', { ...setHeader(), params });
};

/**
 * GET /v2/admin/encounter-codes/facility-plan-access
 * — enrollee vs facility plan/tier for File PA higher-tier warning.
 */
lib.getFacilityPlanAccess = async (params = {}) => {
  return request.get('/v2/admin/encounter-codes/facility-plan-access', {
    ...setHeader(),
    params,
  });
};

/**
 * GET /v2/admin/onboarding/otp?phone= — pending one-time codes for a phone
 * number (registration, enrollee account, password reset). Kept for the
 * search-result "Get code" popover; the OTP lookup page uses lookupOtpCodes.
 */
lib.getOnboardingOtp = async (phone) => {
  return request.get('/v2/admin/onboarding/otp', {
    ...setHeader(),
    params: { phone: String(phone || '').trim() },
  });
};

/**
 * GET /v2/admin/onboarding/otp — the OTP lookup page. With `q` (phone,
 * insurance no, policy no, email or name) it searches; without it, it lists
 * every pending code issued in the last `hours`. Codes of every kind come
 * back in data.codes; staff reset codes only for admin.* holders.
 */
lib.lookupOtpCodes = async ({ q = '', hours = 24, includeExpired = null } = {}) => {
  const params = {};
  const term = String(q || '').trim();
  if (term) params.q = term;
  else params.hours = hours;
  if (includeExpired !== null && includeExpired !== undefined) params.include_expired = includeExpired ? 1 : 0;
  return request.get('/v2/admin/onboarding/otp', { ...setHeader(), params });
};

// ---------------------------------------------------------------------------
// Plan Management (new model, dark launch — gated by plan_designer.manage).
// These MUST stay silent on failure: for users without the permission (or
// plans whose mode is off) the panel simply does not exist, so no myApi error
// toasts. Callers treat `null` as "render nothing".
// ---------------------------------------------------------------------------
lib.pmPaPanel = async (checkinCode) => {
  if (!checkinCode) return null;
  try {
    const resp = await request.get('/v2/admin/pm/pa-panel', {
      ...setHeader(),
      params: { checkin_code: String(checkinCode) },
    });
    if (parseInt(resp?.data?.error)) return null;
    return resp?.data?.data || null;
  } catch (e) {
    return null;
  }
};

lib.pmPaMap = async (payload = {}) => {
  try {
    const resp = await request.post('/v2/admin/pm/pa/map', payload, setHeader());
    if (parseInt(resp?.data?.error)) {
      return { error: 1, message: resp?.data?.message || 'Mapping failed' };
    }
    return { error: 0, data: resp?.data?.data, message: resp?.data?.message };
  } catch (e) {
    return { error: 1, message: e?.response?.data?.message || 'Mapping failed' };
  }
};

lib.pmMappingLines = async (checkinCode) => {
  if (!checkinCode) return null;
  try {
    const resp = await request.get('/v2/admin/pm/mapping/lines', {
      ...setHeader(),
      params: { checkin_code: String(checkinCode) },
    });
    if (parseInt(resp?.data?.error)) return null;
    return resp?.data?.data || null;
  } catch (e) {
    return null;
  }
};

// Consumption in context for filing / PA / claims screens: envelope for the
// check-in type, per-line mapping status, Benefit Item positions; unfiled lines
// (item_name + category_id + amount) count as proposed. Silent on failure.
lib.pmConsumptionContext = async (body = {}) => {
  try {
    const resp = await request.post('/v2/admin/pm/consumption/context', body, setHeader());
    if (parseInt(resp?.data?.error)) return null;
    return resp?.data?.data || null;
  } catch (e) {
    return null;
  }
};

lib.pmMap = async (payload = {}) => {
  try {
    const resp = await request.post('/v2/admin/pm/mapping/map', payload, setHeader());
    if (parseInt(resp?.data?.error)) {
      return { error: 1, message: resp?.data?.message || 'Mapping failed' };
    }
    return { error: 0, data: resp?.data?.data, message: resp?.data?.message };
  } catch (e) {
    return { error: 1, message: e?.response?.data?.message || 'Mapping failed' };
  }
};

// ---------------------------------------------------------------------------
// Announcements (member app home-screen banner) — admin management.
// ---------------------------------------------------------------------------
const annCall = async (method, path, body, params) => {
  try {
    const cfg = { ...setHeader(), ...(params ? { params } : {}) };
    const url = `/v2/admin/announcements${path}`;
    const resp = method === 'get' ? await request.get(url, cfg)
      : method === 'delete' ? await request.delete(url, cfg)
      : await request[method](url, body || {}, cfg);
    if (parseInt(resp?.data?.error)) return { error: 1, message: resp?.data?.message };
    return { error: 0, data: resp?.data?.data, message: resp?.data?.message };
  } catch (e) {
    return { error: 1, message: e?.response?.data?.message || e?.message || 'Request failed' };
  }
};
// Facility notices (Facility Portal top-of-page notices) — admin management.
const fnCall = async (method, path, body) => {
  try {
    const cfg = setHeader();
    const url = `/v2/admin/facility-notices${path}`;
    const resp = method === 'get' ? await request.get(url, cfg) : await request[method](url, body || {}, cfg);
    if (parseInt(resp?.data?.error)) return { error: 1, message: resp?.data?.message };
    return { error: 0, data: resp?.data?.data, message: resp?.data?.message };
  } catch (e) {
    return { error: 1, message: e?.response?.data?.message || e?.message || 'Request failed' };
  }
};
lib.facilityNotices = {
  list: () => fnCall('get', ''),
  options: () => fnCall('get', '/options'),
  recipients: (targets) => fnCall('post', '/recipients', { targets }),
  create: (body) => fnCall('post', '', body),
  update: (id, body) => fnCall('put', `/${id}`, body),
  publish: (id) => fnCall('post', `/${id}/publish`),
  deactivate: (id) => fnCall('post', `/${id}/deactivate`),
};

// Facility Portal help (How To guides + tutorial video links) — admin management.
const helpCall = async (method, path, body) => {
  try {
    const cfg = setHeader();
    const url = `/v2/admin/help${path}`;
    const resp = method === 'get' ? await request.get(url, cfg)
      : method === 'delete' ? await request.delete(url, cfg)
      : await request[method](url, body || {}, cfg);
    if (parseInt(resp?.data?.error)) return { error: 1, message: resp?.data?.message };
    return { error: 0, data: resp?.data?.data, message: resp?.data?.message };
  } catch (e) {
    return { error: 1, message: e?.response?.data?.message || e?.message || 'Request failed' };
  }
};
lib.helpContent = {
  guides: () => helpCall('get', '/guides'),
  createGuide: (body) => helpCall('post', '/guides', body),
  updateGuide: (id, body) => helpCall('put', `/guides/${id}`, body),
  deleteGuide: (id) => helpCall('delete', `/guides/${id}`),
  videos: () => helpCall('get', '/videos'),
  createVideo: (body) => helpCall('post', '/videos', body),
  updateVideo: (id, body) => helpCall('put', `/videos/${id}`, body),
  deleteVideo: (id) => helpCall('delete', `/videos/${id}`),
};

lib.announcements = {
  list: () => annCall('get', ''),
  options: () => annCall('get', '/options'),
  create: (body) => annCall('post', '', body),
  update: (id, body) => annCall('put', `/${id}`, body),
  archive: (id) => annCall('delete', `/${id}`),
  preview: (body) => annCall('post', '/preview', body),
};

// ---------------------------------------------------------------------------
// Single pricing service: sub-bands offered for a plan (optionally scoped to an
// organization) and a server-computed quote. Silent on failure.
// ---------------------------------------------------------------------------
lib.pmOfferedBands = async ({ planId, socialNo, orgId } = {}) => {
  if (!planId) return null;
  try {
    const resp = await request.get('/v2/admin/pm/offered-bands', {
      ...setHeader(),
      params: { plan_id: planId, ...(orgId ? { org_id: orgId } : {}), ...(socialNo ? { social_no: socialNo } : {}) },
    });
    if (parseInt(resp?.data?.error)) return null;
    return resp?.data?.data || null;
  } catch (e) {
    return null;
  }
};
lib.pmQuote = async (body = {}) => {
  try {
    const resp = await request.post('/v2/admin/pm/quote', body, setHeader());
    if (parseInt(resp?.data?.error)) return { error: 1, message: resp?.data?.message || 'Could not price this selection' };
    return { error: 0, data: resp?.data?.data };
  } catch (e) {
    return { error: 1, message: e?.response?.data?.message || 'Could not price this selection' };
  }
};

// ---------------------------------------------------------------------------
// Form settings: reference lists + form fields (admin).
// ---------------------------------------------------------------------------
const fcCall = async (method, path, body) => {
  try {
    const url = `/v2/admin/form-config${path}`;
    const resp = method === 'get' ? await request.get(url, setHeader()) : await request[method](url, body || {}, setHeader());
    if (parseInt(resp?.data?.error)) return { error: 1, message: resp?.data?.message };
    return { error: 0, data: resp?.data?.data, message: resp?.data?.message };
  } catch (e) {
    return { error: 1, message: e?.response?.data?.message || e?.message || 'Request failed' };
  }
};
lib.formConfig = {
  lists: () => fcCall('get', '/lists'),
  createList: (body) => fcCall('post', '/lists', body),
  updateList: (key, body) => fcCall('put', `/lists/${encodeURIComponent(key)}`, body),
  addOption: (key, body) => fcCall('post', `/lists/${encodeURIComponent(key)}/options`, body),
  updateOption: (id, body) => fcCall('put', `/options/${id}`, body),
  reorderOptions: (key, ids) => fcCall('put', `/lists/${encodeURIComponent(key)}/order`, { ids }),
  fields: (formKey) => fcCall('get', `/forms/${encodeURIComponent(formKey)}`),
  updateField: (id, body) => fcCall('put', `/fields/${id}`, body),
  reorderFields: (formKey, ids) => fcCall('put', `/forms/${encodeURIComponent(formKey)}/order`, { ids }),
};

// Main dashboard encounter analytics (any signed-in staff; silent on failure).
lib.dashboardEncounters = async (params = {}) => {
  try {
    const clean = {};
    if (params.from) clean.from = params.from;
    if (params.to) clean.to = params.to;
    const resp = await request.get('/v2/dashboard/encounters', { ...setHeader(), params: clean });
    if (parseInt(resp?.data?.error)) return null;
    return resp?.data?.data || null;
  } catch (e) {
    return null;
  }
};

export default lib;
